/**
 * Prepopulated document templates for the QuickDoc canvas editor.
 *
 * Every element is a real canvas element (TextEl, ImageEl, SignatureEl,
 * FieldEl, CardEl) so the user can edit text, drag elements, and sign.
 *
 * Decorative shapes (gradient headers, dividers) are SVG ImageEl elements.
 * Party cards use CardEl with shadow for a polished look.
 *
 * All layouts use cursor-based positioning so body text never overflows
 * into dividers or adjacent sections.
 */

import type {
  TextEl,
  ImageEl,
  SignatureEl,
  FieldEl,
  CardEl,
  CanvasElement,
  Page,
  Doc,
} from "./quickdoc-types";

import { uid, MARGIN_X, CONTENT_W, FONT_STACK, LOGO_PLACEHOLDER_DATA_URI } from "./template-constants";

import {
  accentStripeSvg,
  headerBannerSvg,
  sectionCircleSvg,
  dividerSvg,
  accentDividerSvg,
  sigBlockSvg,
  metaPillSvg,
  invoiceTableHeaderSvg,
  invoiceTableBodySvg,
  invoiceMetaSvg,
  totalsBoxSvg,
  noteBoxSvg,
} from "./template-svgs";

/* ====================================================================
 *  Element helpers
 * ==================================================================== */

/** Estimate text height from content, fontSize, width, lineHeight. */
const estH = (t: string, fs: number, w: number, lh = 1): number => {
  const avgCW = fs * 0.55;
  const cpl = Math.max(1, Math.floor(w / avgCW));
  let total = 0;
  for (const line of (t || "").split("\n")) {
    total += Math.max(1, Math.ceil((line.length || 1) / cpl));
  }
  return Math.ceil(total * fs * lh) + 4;
};

/** Body text height shorthand */
const bodyH = (t: string, w = CONTENT_W): number => estH(t, 10.5, w, 1.6);

const text = (
  o: Omit<TextEl, "id" | "kind" | "rotation"> & { rotation?: number }
): TextEl => ({
  id: uid("txt"),
  kind: "text",
  rotation: 0,
  height: estH(o.text ?? "", o.fontSize ?? 14, o.width ?? 200, (o as any).lineHeight ?? 1),
  ...o,
});

const img = (
  o: Omit<ImageEl, "id" | "kind" | "rotation"> & { rotation?: number }
): ImageEl => ({ id: uid("img"), kind: "image", rotation: 0, ...o });

const card = (
  o: Omit<CardEl, "id" | "kind" | "rotation"> & { rotation?: number }
): CardEl => ({ id: uid("crd"), kind: "card", rotation: 0, ...o });

const sig = (
  o: Omit<SignatureEl, "id" | "kind" | "rotation" | "signed"> & {
    rotation?: number;
    signed?: boolean;
  }
): SignatureEl => ({ id: uid("sig"), kind: "signature", rotation: 0, signed: false, ...o });

const field = (
  o: Omit<FieldEl, "id" | "kind" | "rotation"> & { rotation?: number }
): FieldEl => ({ id: uid("fld"), kind: "field", rotation: 0, ...o });

const page = (elements: CanvasElement[]): Page => ({ id: uid("pg"), elements });

/* ── Colors & fonts ──────────────────────────────────────────── */

const F = FONT_STACK;
const MX = MARGIN_X;
const CW = CONTENT_W;
const INK = "#0f172a";
const MUTED = "#64748b";
const BODY_C = "#334155";
const FAINT = "#94a3b8";
const WHITE = "#ffffff";

/* ── Text style shortcuts ────────────────────────────────────── */

const title = (x: number, y: number, t: string, w = 508): TextEl =>
  text({ x, y, text: t, fontSize: 21, fontWeight: 800, fill: WHITE, fontFamily: F, align: "left", width: w });

const subtitle = (x: number, y: number, t: string, w = CW): TextEl =>
  text({ x, y, text: t, fontSize: 11, fontWeight: 500, fill: "rgba(255,255,255,0.55)", fontFamily: F, align: "left", width: w });

const secTitle = (x: number, y: number, t: string): TextEl =>
  text({ x, y, text: t, fontSize: 13, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: CW - (x - MX) });

const body = (x: number, y: number, t: string, w = CW): TextEl =>
  text({ x, y, text: t, fontSize: 10.5, fill: BODY_C, fontFamily: F, align: "left", width: w, lineHeight: 1.6 });

const label = (x: number, y: number, t: string, w = 220): TextEl =>
  text({ x, y, text: t, fontSize: 9, fontWeight: 700, fill: MUTED, fontFamily: F, align: "left", width: w, letterSpacing: 0.8 });

const name = (x: number, y: number, t: string, w = 220): TextEl =>
  text({ x, y, text: t, fontSize: 13, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: w });

const addr = (x: number, y: number, t: string, w = 220): TextEl =>
  text({ x, y, text: t, fontSize: 10, fill: MUTED, fontFamily: F, align: "left", width: w, lineHeight: 1.4 });

const meta = (x: number, y: number, t: string, w = 180): TextEl =>
  text({ x, y, text: t, fontSize: 10, fontWeight: 600, fill: BODY_C, fontFamily: F, align: "left", width: w });

const letterHead = (y: number, t: string): TextEl =>
  text({ x: MX, y, text: t, fontSize: 13, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: CW });

const contHdr = (t: string): TextEl =>
  text({ x: MX, y: 24, text: `${t} — continued`, fontSize: 9, fontWeight: 600, fill: FAINT, fontFamily: F, align: "left", width: CW });

const footer = (y: number, t: string): TextEl =>
  text({ x: MX, y, text: t, fontSize: 8, fontWeight: 500, fill: "#cbd5e1", fontFamily: F, align: "left", width: 200 });

const footerR = (y: number, t: string): TextEl =>
  text({ x: MX + CW - 120, y, text: t, fontSize: 8, fontWeight: 500, fill: "#cbd5e1", fontFamily: F, align: "right", width: 120 });

/* ── Decorative SVG helpers ──────────────────────────────────── */

const stripe = (): ImageEl => img({ x: 0, y: 0, src: accentStripeSvg(), width: 612, height: 5 });
const logoPh = (): ImageEl => img({ x: MX, y: 14, src: LOGO_PLACEHOLDER_DATA_URI, width: 140, height: 36 });
const banner = (y: number, h = 66): ImageEl => img({ x: MX, y, src: headerBannerSvg(CW, h), width: CW, height: h });
const secCircle = (x: number, y: number, n: number): ImageEl => img({ x, y, src: sectionCircleSvg(n), width: 22, height: 22 });
const divLine = (y: number): ImageEl => img({ x: MX, y, src: dividerSvg(), width: CW, height: 1 });
const accentDiv = (y: number): ImageEl => img({ x: MX, y, src: accentDividerSvg(), width: CW, height: 2 });
const sigFrame = (x: number, y: number): ImageEl => img({ x, y, src: sigBlockSvg(), width: 256, height: 200 });
const pill = (x: number, y: number, w = 180): ImageEl => img({ x, y, src: metaPillSvg(w), width: w, height: 26 });
const noteBox = (x: number, y: number, w = CW, h = 60): ImageEl => img({ x, y, src: noteBoxSvg(w, h), width: w, height: h });

/* ── Party card (CardEl with shadow + accent bar) ────────────── */

const pCard = (x: number, y: number, w: number, h: number, accent = INK): CanvasElement[] => [
  card({
    x, y, width: w, height: h, fill: "#ffffff",
    stroke: "#e2e8f0", strokeWidth: 1, cornerRadius: 12,
    shadowColor: "#000000", shadowBlur: 8, shadowOffsetX: 0, shadowOffsetY: 2, shadowOpacity: 0.06,
  }),
  card({ x, y: y + 6, width: 4, height: h - 12, fill: accent, cornerRadius: 2 }),
];

const fCard = (x: number, y: number, w = CW, h = 80): CanvasElement[] => [
  card({
    x, y, width: w, height: h, fill: "#ffffff",
    stroke: "#e2e8f0", strokeWidth: 1, cornerRadius: 12,
    shadowColor: "#000000", shadowBlur: 8, shadowOffsetX: 0, shadowOffsetY: 2, shadowOpacity: 0.06,
  }),
  card({ x, y: y + 6, width: 4, height: h - 12, fill: INK, cornerRadius: 2 }),
];

/* ── Section (circle + title + body), returns [elements, endY] ─ */

const sec = (y: number, n: number, sTitle: string, bodyText: string): [CanvasElement[], number] => {
  const bh = bodyH(bodyText);
  return [
    [
      secCircle(MX, y, n),
      secTitle(MX + 30, y + 3, sTitle),
      body(MX, y + 24, bodyText),
    ],
    y + 24 + bh,
  ];
};

/* ── Letter-style section (heading + body), returns [els, endY] ─ */

const lSec = (y: number, heading: string, bodyText: string): [CanvasElement[], number] => {
  const bh = bodyH(bodyText);
  return [
    [letterHead(y, heading), body(MX, y + 18, bodyText)],
    y + 18 + bh,
  ];
};

/* ── Sig block (frame + sig + fields + labels) ───────────────── */

const sigBlock = (x: number, y: number, lbl: string): CanvasElement[] => [
  sigFrame(x, y),
  text({ x: x + 14, y: y + 10, text: lbl, fontSize: 11, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: 228 }),
  sig({ x: x + 14, y: y + 40, label: "Signature", width: 228, height: 56 }),
  field({ x: x + 14, y: y + 108, type: "text", label: "Printed Name", width: 228, height: 26, fill: "transparent", stroke: "transparent", placeholder: "Full name" }),
  field({ x: x + 14, y: y + 146, type: "date", label: "Date", width: 228, height: 26, fill: "transparent", stroke: "transparent", placeholder: "Select date" }),
];

/**
 * Signature block variant with deterministic element IDs for role-based quick-send.
 * The roleKey is used to prefix element IDs so they can be looked up from templateMetadata.
 */
const sigBlockWithRole = (x: number, y: number, lbl: string, roleKey: string): CanvasElement[] => {
  const sigId = `${roleKey}_sig`;
  const nameId = `${roleKey}_name`;
  const dateId = `${roleKey}_date`;

  return [
    sigFrame(x, y),
    text({ x: x + 14, y: y + 10, text: lbl, fontSize: 11, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: 228 }),
    { ...sig({ x: x + 14, y: y + 40, label: "Signature", width: 228, height: 56 }), id: sigId },
    { ...field({ x: x + 14, y: y + 108, type: "text", label: "Printed Name", width: 228, height: 26, fill: "transparent", stroke: "transparent", placeholder: "Full name" }), id: nameId },
    { ...field({ x: x + 14, y: y + 146, type: "date", label: "Date", width: 228, height: 26, fill: "transparent", stroke: "transparent", placeholder: "Select date" }), id: dateId },
  ];
};

/* ── Layout gap constants ────────────────────────────────────── */

const GAP = 8;       // standard vertical gap between elements
const DIV_PRE = 6;   // gap before a divider
const DIV_POST = 6;  // gap after a divider
const PILL_TEXT_Y = 8; // vertical offset for text inside pills (centers 10px text in 26px pill)

/* ====================================================================
 *  Template type
 * ==================================================================== */

export interface TemplateDescriptor {
  key: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  pageCount: number;
  doc: Doc;
}

/* ====================================================================
 *  1. NDA
 * ==================================================================== */

function buildNda(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Non-Disclosure Agreement"));
  p1.push(subtitle(MX + 18, 96, "Mutual Confidentiality & Non-Disclosure"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: January 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const ndaIntro = "This Non-Disclosure Agreement (\"Agreement\") is entered into as of January 15, 2025, by and between the parties identified below, for the purpose of preventing the unauthorized disclosure of Confidential Information.";
  p1.push(body(MX, y, ndaIntro));
  y += bodyH(ndaIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "DISCLOSING PARTY"));
  p1.push(name(MX + 16, y + 28, "Acme Corporation"));
  p1.push(addr(MX + 16, y + 46, "123 Innovation Drive\nSan Francisco, CA 94102"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "RECEIVING PARTY"));
  p1.push(name(MX + 286, y + 28, "Jane Smith Consulting"));
  p1.push(addr(MX + 286, y + 46, "456 Market Street\nNew York, NY 10001"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "\"Confidential Information\" means any non-public information disclosed by the Disclosing Party, whether in written, oral, electronic, or other form, including technical, business, financial, and operational data that is designated as confidential or should reasonably be understood to be confidential given the nature of the information and circumstances of disclosure.";
  const [s1, s1e] = sec(y, 1, "Definition of Confidential Information", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Receiving Party shall: (a) hold all Confidential Information in strict confidence; (b) not disclose it to third parties without prior written consent; (c) use it solely for evaluating or pursuing a business relationship between the parties; and (d) protect it using the same degree of care used for its own confidential information, but in no event less than reasonable care.";
  const [s2, s2e] = sec(y, 2, "Obligations of the Receiving Party", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Information is not Confidential if it: (a) is or becomes publicly available without fault of the Receiving Party; (b) was previously known to the Receiving Party; (c) is independently developed without use of Confidential Information; or (d) is rightfully obtained from a third party without restriction on disclosure.";
  const [s3, s3e] = sec(y, 3, "Exclusions from Confidential Information", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "This Agreement remains in effect for two (2) years from the Effective Date. Confidentiality obligations survive termination for three (3) additional years.";
  const [s4, s4e] = sec(y, 4, "Term and Duration", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Non-Disclosure Agreement"), divLine(40));
  y = 48;

  const [s5, s5e] = sec(y, 5, "Return of Materials",
    "Upon written request or termination, the Receiving Party shall promptly return or destroy all materials containing Confidential Information and provide written certification of such return or destruction.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "No License or Rights Granted",
    "Nothing in this Agreement grants the Receiving Party any rights, title, or interest in the Confidential Information or any intellectual property of the Disclosing Party.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Remedies",
    "The Receiving Party acknowledges that breach may cause irreparable harm. The Disclosing Party shall be entitled to seek injunctive relief in addition to any other remedies available at law or in equity.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Governing Law and Jurisdiction",
    "This Agreement is governed by the laws of the State of Delaware. Any disputes arising under this Agreement shall be resolved in the courts of that jurisdiction.");
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Entire Agreement",
    "This Agreement constitutes the entire agreement between the parties regarding its subject matter and supersedes all prior negotiations, representations, or agreements. Amendments must be in writing and signed by both parties.");
  p2.push(...s9); y = s9e;

  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Disclosing Party", "nda_partyA"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Receiving Party", "nda_partyB"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  2. INDEPENDENT CONTRACTOR AGREEMENT
 * ==================================================================== */

function buildContractor(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "Independent Contractor"));
  p1.push(title(MX + 18, 92, "Agreement"));
  p1.push(subtitle(MX + 18, 116, "Professional Services Engagement"));

  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: February 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Contact: alex@rivera.dev", 170));

  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const cIntro = "This Independent Contractor Agreement (\"Agreement\") is entered into as of February 1, 2025, by and between the following parties.";
  p1.push(body(MX, y, cIntro));
  y += bodyH(cIntro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "Meridian Labs Inc."));
  p1.push(addr(MX + 16, y + 46, "800 Tech Park Boulevard\nAustin, TX 78701"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CONTRACTOR"));
  p1.push(name(MX + 286, y + 28, "Alex Rivera"));
  p1.push(addr(MX + 286, y + 46, "1250 Freelancer Lane\nPortland, OR 97201"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [c1, c1e] = sec(y, 1, "Engagement of Services",
    "The Company engages the Contractor, and the Contractor accepts such engagement, to perform the services described herein as an independent contractor — not as an employee, agent, or partner of the Company.");
  p1.push(...c1); y = c1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [c2, c2e] = sec(y, 2, "Scope of Work",
    "The Contractor shall perform services as mutually agreed upon in any attached Statement of Work or as directed by the Company. The Contractor shall devote sufficient time to ensure timely and professional completion of all assigned tasks.");
  p1.push(...c2); y = c2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [c3, c3e] = sec(y, 3, "Compensation and Payment",
    "The Company shall compensate the Contractor at the agreed rate. Payment is due within thirty (30) days of receipt of a proper invoice. The Contractor is responsible for all taxes, insurance, and other obligations arising from this engagement.");
  p1.push(...c3); y = c3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [c4, c4e] = sec(y, 4, "Term and Termination",
    "This Agreement commences on the Effective Date and continues until completion of services, unless terminated earlier. Either party may terminate upon fourteen (14) days' written notice. Upon termination, the Contractor shall be compensated for all work satisfactorily completed.");
  p1.push(...c4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Independent Contractor Agreement"), divLine(40));
  y = 48;

  const [c5, c5e] = sec(y, 5, "Independent Contractor Status",
    "The Contractor is an independent contractor, not an employee. The Contractor is not entitled to employee benefits including health insurance, retirement plans, paid leave, or workers' compensation. The Contractor determines the manner and means of performing services.");
  p2.push(...c5); y = c5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c6, c6e] = sec(y, 6, "Confidentiality",
    "The Contractor agrees to maintain the confidentiality of all proprietary information, trade secrets, and confidential business information disclosed by the Company during the term of this Agreement. This obligation survives termination for two (2) years.");
  p2.push(...c6); y = c6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c7, c7e] = sec(y, 7, "Intellectual Property",
    "All work product, inventions, discoveries, and materials created by the Contractor in performing services under this Agreement shall be the exclusive property of the Company. The Contractor hereby assigns all right, title, and interest in such work product to the Company.");
  p2.push(...c7); y = c7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c8, c8e] = sec(y, 8, "Non-Solicitation",
    "During the term and for twelve (12) months following termination, the Contractor shall not directly or indirectly solicit, recruit, or hire any employee or contractor of the Company, or encourage any such person to terminate their relationship with the Company.");
  p2.push(...c8); y = c8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c9, c9e] = sec(y, 9, "Indemnification",
    "The Contractor shall indemnify and hold harmless the Company from any claims, damages, losses, or expenses arising from the Contractor's breach of this Agreement or negligent performance of services.");
  p2.push(...c9); y = c9e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c10, c10e] = sec(y, 10, "Governing Law",
    "This Agreement is governed by the laws of the State of Texas. Disputes shall be resolved through binding arbitration in accordance with the rules of the applicable arbitration body.");
  p2.push(...c10); y = c10e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [c11, c11e] = sec(y, 11, "Entire Agreement",
    "This Agreement constitutes the entire agreement between the parties and supersedes all prior agreements. Amendments must be in writing and signed by both parties.");
  p2.push(...c11);

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Independent Contractor Agreement"), divLine(40));
  y = 58;
  p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Company Representative", "contractor_company"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Contractor", "contractor_contractor"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  3. OFFER LETTER
 * ==================================================================== */

function buildOfferLetter(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  p1.push(stripe(), logoPh(), banner(58, 60));
  p1.push(title(MX + 18, 72, "Offer of Employment"));
  p1.push(subtitle(MX + 18, 96, "Confidential Employment Offer"));

  y = 126;
  p1.push(pill(MX, y, 140), meta(MX + 20, y + PILL_TEXT_Y, "March 10, 2025", 120));

  y = 160;
  p1.push(accentDiv(y)); y += 10;

  const CH = 72;
  p1.push(...fCard(MX, y, CW, CH));
  p1.push(label(MX + 16, y + 12, "CANDIDATE"));
  p1.push(name(MX + 16, y + 28, "Jordan Chen"));
  p1.push(addr(MX + 16, y + 44, "789 Oakwood Avenue, Chicago, IL 60601  ·  jordan.chen@email.com", CW - 32));
  y += CH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  p1.push(text({ x: MX, y, text: "Dear Jordan,", fontSize: 12, fontWeight: 600, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 20;

  const oIntro = "We are pleased to extend this formal offer of employment at TechForward Inc. After careful review of your qualifications and experience, we are confident you will be a valuable addition to our team.";
  p1.push(body(MX, y, oIntro));
  y += bodyH(oIntro) + GAP;

  const [ls1, ls1e] = lSec(y, "Position and Start Date",
    "You are offered the position of Senior Product Designer, reporting to the VP of Design. Your anticipated start date is April 14, 2025. This is a full-time position based at our Chicago office.");
  p1.push(...ls1); y = ls1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [ls2, ls2e] = lSec(y, "Compensation",
    "Your annual base salary will be $145,000.00, payable bi-weekly in accordance with the Company's standard payroll schedule. You will also be eligible for an annual performance bonus of up to 15% of your base salary, subject to Company and individual performance targets.");
  p1.push(...ls2); y = ls2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [ls3, ls3e] = lSec(y, "Benefits",
    "As a full-time employee, you will be eligible for our comprehensive benefits package including health, dental, and vision insurance; 401(k) with Company match; 20 days paid time off; parental leave; and professional development stipend. Benefits eligibility begins on your start date.");
  p1.push(...ls3); y = ls3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [ls4, ls4e] = lSec(y, "At-Will Employment",
    "Your employment is at-will, meaning either you or the Company may terminate the relationship at any time, with or without cause or notice.");
  p1.push(...ls4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Offer of Employment"), divLine(40));
  y = 48;

  const [ls5, ls5e] = lSec(y, "Confidentiality and Non-Compete",
    "As a condition of employment, you will be required to sign the Company's standard confidentiality and non-disclosure agreement. You may also be required to sign a non-compete or non-solicitation agreement as applicable under local law.");
  p2.push(...ls5); y = ls5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [ls6, ls6e] = lSec(y, "Contingencies",
    "This offer is contingent upon: (a) satisfactory completion of a background check; (b) your legal authorization to work in the United States; and (c) execution of all required onboarding documents.");
  p2.push(...ls6); y = ls6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [ls7, ls7e] = lSec(y, "Acceptance",
    "To accept this offer, please sign and date below and return by March 24, 2025. We look forward to welcoming you to TechForward Inc.");
  p2.push(...ls7); y = ls7e;

  y += 10;
  p2.push(body(MX, y, "Sincerely,"));
  y += 20;
  p2.push(text({ x: MX, y, text: "The TechForward Inc. Team", fontSize: 12, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 24;

  p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Company Representative", "offer_company"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Candidate", "offer_candidate"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. INVOICE
 * ==================================================================== */

function buildInvoice(): Doc {
  let y: number;
  const p1: CanvasElement[] = [];

  p1.push(stripe(), logoPh());
  p1.push(text({ x: 420, y: 18, text: "INVOICE", fontSize: 26, fontWeight: 900, fill: INK, fontFamily: F, align: "right", width: 156 }));
  p1.push(text({ x: 420, y: 46, text: "INV-2025-0042", fontSize: 11, fontWeight: 700, fill: MUTED, fontFamily: F, align: "right", width: 156 }));
  p1.push(accentDiv(68));

  // Bill From / To
  y = 82;
  const IPH = 120;
  p1.push(...pCard(MX, y, 258, IPH, INK));
  p1.push(label(MX + 16, y + 10, "BILL FROM"));
  p1.push(name(MX + 16, y + 26, "Summit Design Co."));
  p1.push(addr(MX + 16, y + 44, "2100 Creative Drive, Suite 400\nSeattle, WA 98101\nbilling@summitdesign.co"));
  p1.push(...pCard(MX + 270, y, 270, IPH, "#3b82f6"));
  p1.push(label(MX + 286, y + 10, "BILL TO"));
  p1.push(name(MX + 286, y + 26, "Cascade Industries Ltd."));
  p1.push(addr(MX + 286, y + 44, "550 Commerce Way\nDenver, CO 80202\nap@cascade-industries.com"));
  y += IPH + GAP;

  // Invoice meta bar
  y += 4;
  p1.push(img({ x: MX, y, src: invoiceMetaSvg(), width: CW, height: 44 }));
  p1.push(text({ x: MX + 16, y: y + 8, text: "INVOICE DATE", fontSize: 8, fontWeight: 700, fill: FAINT, fontFamily: F, align: "left", width: 140, letterSpacing: 0.5 }));
  p1.push(meta(MX + 16, y + 20, "January 28, 2025", 140));
  p1.push(text({ x: 216, y: y + 8, text: "DUE DATE", fontSize: 8, fontWeight: 700, fill: FAINT, fontFamily: F, align: "left", width: 140, letterSpacing: 0.5 }));
  p1.push(meta(216, y + 20, "February 27, 2025", 140));
  p1.push(text({ x: 400, y: y + 8, text: "TERMS", fontSize: 8, fontWeight: 700, fill: FAINT, fontFamily: F, align: "left", width: 140, letterSpacing: 0.5 }));
  p1.push(meta(400, y + 20, "Net 30", 100));
  y += 44 + GAP;

  // Table header
  const TH_Y = y;
  p1.push(img({ x: MX, y: TH_Y, src: invoiceTableHeaderSvg(), width: CW, height: 28 }));
  p1.push(text({ x: 50, y: TH_Y + 8, text: "Description", fontSize: 10, fontWeight: 700, fill: WHITE, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 320, y: TH_Y + 8, text: "Qty", fontSize: 10, fontWeight: 700, fill: WHITE, fontFamily: F, align: "center", width: 60 }));
  p1.push(text({ x: 390, y: TH_Y + 8, text: "Rate", fontSize: 10, fontWeight: 700, fill: WHITE, fontFamily: F, align: "center", width: 80 }));
  p1.push(text({ x: 480, y: TH_Y + 8, text: "Amount", fontSize: 10, fontWeight: 700, fill: WHITE, fontFamily: F, align: "right", width: 80 }));

  // Table body
  const TB_Y = TH_Y + 28;
  p1.push(img({ x: MX, y: TB_Y, src: invoiceTableBodySvg(), width: CW, height: 140 }));
  // Row 1
  p1.push(text({ x: 50, y: TB_Y + 10, text: "Website Redesign & Development", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 50, y: TB_Y + 24, text: "Complete responsive redesign with CMS integration", fontSize: 9, fill: MUTED, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 320, y: TB_Y + 14, text: "40 hrs", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 60 }));
  p1.push(text({ x: 390, y: TB_Y + 14, text: "$125.00", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 80 }));
  p1.push(text({ x: 480, y: TB_Y + 14, text: "$5,000.00", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "right", width: 80 }));
  // Row 2
  p1.push(text({ x: 50, y: TB_Y + 56, text: "Brand Identity Package", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 50, y: TB_Y + 70, text: "Logo, color palette, typography, brand guidelines", fontSize: 9, fill: MUTED, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 320, y: TB_Y + 60, text: "1", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 60 }));
  p1.push(text({ x: 390, y: TB_Y + 60, text: "$2,500.00", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 80 }));
  p1.push(text({ x: 480, y: TB_Y + 60, text: "$2,500.00", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "right", width: 80 }));
  // Row 3
  p1.push(text({ x: 50, y: TB_Y + 102, text: "Content Strategy & Copywriting", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 50, y: TB_Y + 116, text: "Homepage, About, Services, Contact page copy", fontSize: 9, fill: MUTED, fontFamily: F, align: "left", width: 260 }));
  p1.push(text({ x: 320, y: TB_Y + 106, text: "8 hrs", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 60 }));
  p1.push(text({ x: 390, y: TB_Y + 106, text: "$95.00", fontSize: 10, fill: BODY_C, fontFamily: F, align: "center", width: 80 }));
  p1.push(text({ x: 480, y: TB_Y + 106, text: "$760.00", fontSize: 10, fontWeight: 600, fill: INK, fontFamily: F, align: "right", width: 80 }));

  // Totals
  y = TB_Y + 140 + GAP;
  p1.push(img({ x: 356, y, src: totalsBoxSvg(), width: 220, height: 100 }));
  p1.push(text({ x: 370, y: y + 16, text: "Subtotal", fontSize: 11, fill: "#475569", fontFamily: F, align: "left", width: 90 }));
  p1.push(text({ x: 476, y: y + 16, text: "$8,260.00", fontSize: 11, fontWeight: 600, fill: BODY_C, fontFamily: F, align: "right", width: 86 }));
  p1.push(text({ x: 370, y: y + 36, text: "Tax (8.5%)", fontSize: 11, fill: "#475569", fontFamily: F, align: "left", width: 90 }));
  p1.push(text({ x: 476, y: y + 36, text: "$702.10", fontSize: 11, fontWeight: 600, fill: BODY_C, fontFamily: F, align: "right", width: 86 }));
  p1.push(text({ x: 370, y: y + 70, text: "Total Due", fontSize: 13, fontWeight: 900, fill: INK, fontFamily: F, align: "left", width: 90 }));
  p1.push(text({ x: 476, y: y + 70, text: "$8,962.10", fontSize: 13, fontWeight: 900, fill: INK, fontFamily: F, align: "right", width: 86 }));
  y += 100 + GAP;

  // Payment terms
  p1.push(noteBox(MX, y, CW, 58));
  p1.push(text({ x: MX + 14, y: y + 10, text: "Payment Terms", fontSize: 11, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: 500 }));
  p1.push(text({ x: MX + 14, y: y + 26, text: "Payment is due within 30 days of the invoice date. Late payments incur a 1.5% monthly interest charge. Please remit via bank transfer or check payable to Summit Design Co.", fontSize: 10, fill: MUTED, fontFamily: F, align: "left", width: 510, lineHeight: 1.4 }));
  y += 58 + GAP;

  // Signature
  p1.push(accentDiv(y)); y += 10;
  p1.push(text({ x: MX, y, text: "Authorized By", fontSize: 10, fontWeight: 700, fill: INK, fontFamily: F, align: "left", width: 120 }));
  y += 18;
  p1.push({ ...sig({ x: MX, y, label: "Signature", width: 200, height: 50 }), id: "invoice_sender_sig" });

  p1.push(footer(772, "Summit Design Co."), footerR(772, "INV-2025-0042"));

  return [page(p1)];
}

/* ====================================================================
 *  5. SERVICE AGREEMENT
 * ==================================================================== */

function buildServiceAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  p1.push(stripe(), logoPh(), banner(58, 60));
  p1.push(title(MX + 18, 72, "Service Agreement"));
  p1.push(subtitle(MX + 18, 96, "Professional Services Engagement Agreement"));

  y = 126;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 180));

  y = 160;
  p1.push(accentDiv(y)); y += 10;
  const saIntro = "This Service Agreement (\"Agreement\") is entered into as of March 1, 2025, by and between the following parties.";
  p1.push(body(MX, y, saIntro));
  y += bodyH(saIntro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "SERVICE PROVIDER"));
  p1.push(name(MX + 16, y + 28, "Pinnacle Solutions LLC"));
  p1.push(addr(MX + 16, y + 46, "3400 Enterprise Way\nBoston, MA 02101"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT"));
  p1.push(name(MX + 286, y + 28, "Harbor Analytics Inc."));
  p1.push(addr(MX + 286, y + 46, "1700 Bayfront Plaza\nMiami, FL 33101"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [sa1, sa1e] = sec(y, 1, "Services",
    "The Service Provider agrees to provide the services described in any attached Statement of Work or as otherwise agreed in writing. Services shall be performed in a professional manner consistent with industry standards.");
  p1.push(...sa1); y = sa1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [sa2, sa2e] = sec(y, 2, "Compensation",
    "The Client shall pay the fees set forth in the applicable Statement of Work. Invoices shall be submitted monthly, with payment due within thirty (30) days. All fees are exclusive of applicable taxes, which are the Client's responsibility.");
  p1.push(...sa2); y = sa2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [sa3, sa3e] = sec(y, 3, "Term",
    "This Agreement commences on the Effective Date for an initial term of twelve (12) months. It auto-renews for successive twelve-month periods unless either party provides thirty (30) days' written notice of non-renewal.");
  p1.push(...sa3); y = sa3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [sa4, sa4e] = sec(y, 4, "Termination",
    "Either party may terminate: (a) on thirty (30) days' written notice; (b) immediately for material breach uncured within fifteen (15) days; or (c) immediately if the other party becomes insolvent or files for bankruptcy.");
  p1.push(...sa4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Service Agreement"), divLine(40));
  y = 48;

  const [sa5, sa5e] = sec(y, 5, "Confidentiality",
    "Each party shall not disclose any confidential or proprietary information of the other party learned during this Agreement to third parties. This obligation survives termination for two (2) years.");
  p2.push(...sa5); y = sa5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa6, sa6e] = sec(y, 6, "Intellectual Property",
    "All IP created specifically for the Client is owned by the Client upon full payment. The Service Provider retains ownership of all pre-existing IP, tools, methodologies, and know-how.");
  p2.push(...sa6); y = sa6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa7, sa7e] = sec(y, 7, "Limitation of Liability",
    "Neither party shall be liable for indirect, incidental, special, consequential, or punitive damages. The Service Provider's total liability shall not exceed fees paid during the twelve (12) months preceding the claim.");
  p2.push(...sa7); y = sa7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa8, sa8e] = sec(y, 8, "Indemnification",
    "Each party indemnifies the other against third-party claims arising from: (a) breach of this Agreement; or (b) negligence or willful misconduct, including reasonable attorneys' fees.");
  p2.push(...sa8); y = sa8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa9, sa9e] = sec(y, 9, "Warranties",
    "The Service Provider warrants that: (a) it has authority to enter this Agreement; (b) services will be performed professionally; and (c) deliverables will conform to agreed specifications. No other warranties are made.");
  p2.push(...sa9); y = sa9e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa10, sa10e] = sec(y, 10, "Dispute Resolution",
    "Disputes shall first be resolved through good-faith negotiation. If unresolved within thirty (30) days, they shall be submitted to binding arbitration under the rules of the applicable arbitration body in New York.");
  p2.push(...sa10); y = sa10e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [sa11, sa11e] = sec(y, 11, "General Provisions",
    "This Agreement is the entire agreement between the parties and supersedes all prior agreements. Amendments require written consent of both parties. Neither party may assign without prior written consent.");
  p2.push(...sa11);

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Service Agreement"), divLine(40));
  y = 58;
  p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Service Provider", "service_provider"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Client", "service_client"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  6. RESIDENTIAL LEASE AGREEMENT
 * ==================================================================== */

function buildResidentialLease(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "Residential Lease"));
  p1.push(title(MX + 18, 92, "Agreement"));
  p1.push(subtitle(MX + 18, 116, "Standard Residential Property Lease"));

  // Pills
  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: April 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 180));

  // Accent divider + intro
  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const leaseIntro = "This Residential Lease Agreement (\"Lease\") is entered into as of April 1, 2025, by and between the Landlord and Tenant identified below, for the rental of the residential property described herein, subject to the terms and conditions set forth in this Lease.";
  p1.push(body(MX, y, leaseIntro));
  y += bodyH(leaseIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LANDLORD"));
  p1.push(name(MX + 16, y + 28, "Greenfield Properties LLC"));
  p1.push(addr(MX + 16, y + 46, "4200 Sunset Boulevard\nLos Angeles, CA 90028"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "TENANT"));
  p1.push(name(MX + 286, y + 28, "Michael & Sarah Torres"));
  p1.push(addr(MX + 286, y + 46, "1825 Willow Creek Drive\nLos Angeles, CA 90012"));
  y += PH + GAP;

  // Section 1 – Property Description
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Landlord hereby leases to the Tenant the residential property located at 742 Evergreen Terrace, Unit 4B, Los Angeles, CA 90015 (the \"Premises\"), including all fixtures, appliances, and furnishings listed in the attached inventory. The Premises shall be used solely as a private residential dwelling and for no other purpose without prior written consent of the Landlord.";
  const [s1, s1e] = sec(y, 1, "Property Description", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 – Lease Term
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The initial term of this Lease shall commence on April 1, 2025 and terminate on March 31, 2026 (the \"Lease Term\"). Upon expiration, this Lease shall automatically convert to a month-to-month tenancy under the same terms and conditions, unless either party provides at least thirty (30) days' written notice of intent to terminate prior to the end of any monthly period.";
  const [s2, s2e] = sec(y, 2, "Lease Term", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 – Rent
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Tenant shall pay monthly rent of Two Thousand Eight Hundred Dollars ($2,800.00), due on the first day of each calendar month. Rent shall be paid via electronic transfer, check, or money order to the Landlord at the address above. A late fee of five percent (5%) of the monthly rent shall be assessed for any payment received more than five (5) days after the due date. Partial payments shall not be accepted without prior written agreement.";
  const [s3, s3e] = sec(y, 3, "Rent", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 – Security Deposit
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Upon execution of this Lease, the Tenant shall pay a security deposit of Five Thousand Six Hundred Dollars ($5,600.00). The deposit shall be held in accordance with applicable state law and returned within twenty-one (21) days of lease termination, less any lawful deductions for unpaid rent, cleaning, or repair of damages beyond normal wear and tear. An itemized statement of deductions shall accompany any partial return.";
  const [s4, s4e] = sec(y, 4, "Security Deposit", s4t);
  p1.push(...s4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Residential Lease Agreement"), divLine(40));
  y = 48;

  // Section 5 – Maintenance and Repairs
  const s5t = "The Landlord shall maintain the structural components, plumbing, electrical systems, heating, and air conditioning in good working order. The Tenant shall maintain the Premises in a clean and habitable condition, promptly report any maintenance issues, and shall be responsible for repairs necessitated by the Tenant's negligence or misuse. The Tenant shall not make any alterations or improvements without the Landlord's prior written consent.";
  const [s5, s5e] = sec(y, 5, "Maintenance and Repairs", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 – Utilities
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Tenant shall be responsible for the payment of all utilities and services associated with the Premises, including but not limited to electricity, gas, water, sewer, trash collection, internet, and cable television. The Landlord shall be responsible only for those utilities expressly identified in writing as Landlord-paid. Failure to maintain utility services shall constitute a material breach of this Lease.";
  const [s6, s6e] = sec(y, 6, "Utilities", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 – Rules and Regulations
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "The Tenant agrees to comply with all applicable laws, ordinances, and homeowner association rules. No pets shall be permitted without prior written approval and payment of a pet deposit. The Tenant shall not engage in any activity that creates a nuisance or disturbs the quiet enjoyment of neighboring residents. Smoking is prohibited inside the Premises and within all common areas of the property.";
  const [s7, s7e] = sec(y, 7, "Rules and Regulations", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 – Entry and Inspection
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "The Landlord or authorized agents may enter the Premises for inspection, maintenance, or showing to prospective tenants or buyers upon twenty-four (24) hours' written notice, except in cases of emergency. Entry shall be during reasonable hours unless the Tenant consents otherwise. The Landlord shall not abuse the right of entry or use it to harass the Tenant.";
  const [s8, s8e] = sec(y, 8, "Entry and Inspection", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 – Termination
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "Either party may terminate this Lease at the end of the initial term or any renewal period by providing at least thirty (30) days' written notice. The Landlord may terminate immediately for material breach, including nonpayment of rent exceeding ten (10) days, illegal activity on the Premises, or substantial damage to the property. Upon termination, the Tenant shall vacate the Premises, return all keys, and leave the property in broom-clean condition.";
  const [s9, s9e] = sec(y, 9, "Termination", s9t);
  p2.push(...s9); y = s9e;

  // Section 10 – Default and Remedies
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s10t = "If the Tenant fails to pay rent or breaches any term of this Lease, the Landlord shall provide written notice specifying the default. If the default is not cured within the applicable statutory period, the Landlord may pursue all remedies available under law, including eviction proceedings, recovery of unpaid rent, and damages. The prevailing party in any legal action shall be entitled to recover reasonable attorneys' fees and court costs.";
  const [s10, s10e] = sec(y, 10, "Default and Remedies", s10t);
  p2.push(...s10);

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Residential Lease Agreement"), divLine(40));
  y = 48;

  // Section 11 – Governing Law
  const s11t = "This Lease shall be governed by and construed in accordance with the laws of the State of California. Any disputes arising under or in connection with this Lease shall be resolved in the courts of Los Angeles County, California. If any provision of this Lease is found to be unenforceable, the remaining provisions shall continue in full force and effect. This Lease constitutes the entire agreement between the parties and supersedes all prior negotiations and agreements.";
  const [s11, s11e] = sec(y, 11, "Governing Law", s11t);
  p3.push(...s11); y = s11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Landlord", "lease_landlord"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Tenant", "lease_tenant"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  7. COMMERCIAL PROPERTY PURCHASE AGREEMENT
 * ==================================================================== */

function buildPropertyPurchase(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "Commercial Property"));
  p1.push(title(MX + 18, 92, "Purchase Agreement"));
  p1.push(subtitle(MX + 18, 116, "Real Estate Sale & Transfer Contract"));

  // Pills
  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: May 15, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 180));

  // Accent divider + intro
  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const purchaseIntro = "This Commercial Property Purchase Agreement (\"Agreement\") is entered into as of May 15, 2025, by and between the Seller and Buyer identified below, for the sale and purchase of the commercial real property described herein, subject to all terms, conditions, and contingencies set forth in this Agreement.";
  p1.push(body(MX, y, purchaseIntro));
  y += bodyH(purchaseIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "SELLER"));
  p1.push(name(MX + 16, y + 28, "Metropolitan Realty Holdings"));
  p1.push(addr(MX + 16, y + 46, "1500 Park Avenue, Suite 2200\nNew York, NY 10022"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "BUYER"));
  p1.push(name(MX + 286, y + 28, "Horizon Capital Partners LLC"));
  p1.push(addr(MX + 286, y + 46, "880 Third Avenue, 12th Floor\nNew York, NY 10022"));
  y += PH + GAP;

  // Section 1 – Property Description
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const ps1t = "The Seller agrees to sell and convey to the Buyer the commercial real property located at 350 Commerce Boulevard, Brooklyn, NY 11201 (the \"Property\"), including all buildings, improvements, fixtures, and appurtenances thereto, together with all rights, privileges, and easements pertaining to the Property. The legal description is set forth in Exhibit A attached hereto and incorporated by reference.";
  const [ps1, ps1e] = sec(y, 1, "Property Description", ps1t);
  p1.push(...ps1); y = ps1e;

  // Section 2 – Purchase Price
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const ps2t = "The total purchase price for the Property shall be Four Million Two Hundred Fifty Thousand Dollars ($4,250,000.00) (the \"Purchase Price\"), payable as follows: (a) Earnest Money deposit as set forth in Section 3; (b) the balance due at Closing by certified funds or wire transfer. The Purchase Price shall not be adjusted except as expressly provided in this Agreement or by mutual written consent of the parties.";
  const [ps2, ps2e] = sec(y, 2, "Purchase Price", ps2t);
  p1.push(...ps2); y = ps2e;

  // Section 3 – Earnest Money
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const ps3t = "Within five (5) business days of the Effective Date, the Buyer shall deposit earnest money in the amount of Two Hundred Twelve Thousand Five Hundred Dollars ($212,500.00) with National Title & Escrow Company (the \"Escrow Agent\"). The earnest money shall be held in an interest-bearing escrow account and applied toward the Purchase Price at Closing. If the Buyer terminates during the Due Diligence Period, the earnest money shall be refunded in full. After expiration of the Due Diligence Period, the earnest money shall become non-refundable except as otherwise provided herein.";
  const [ps3, ps3e] = sec(y, 3, "Earnest Money", ps3t);
  p1.push(...ps3); y = ps3e;

  // Section 4 – Due Diligence
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const ps4t = "The Buyer shall have a period of forty-five (45) days from the Effective Date (the \"Due Diligence Period\") to conduct inspections, environmental assessments, financial audits, and any other investigations of the Property. The Seller shall provide reasonable access to the Property and all relevant records including leases, operating statements, tax returns, and environmental reports. If the Buyer is not satisfied with the results of due diligence, the Buyer may terminate this Agreement by written notice before expiration of the Due Diligence Period.";
  const [ps4, ps4e] = sec(y, 4, "Due Diligence", ps4t);
  p1.push(...ps4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Commercial Property Purchase Agreement"), divLine(40));
  y = 48;

  // Section 5 – Title and Survey
  const ps5t = "The Seller shall deliver marketable and insurable fee simple title to the Property at Closing, free and clear of all liens, encumbrances, and defects except Permitted Exceptions. The Buyer may obtain, at Buyer's expense, a current ALTA survey and title commitment. The Buyer shall have fifteen (15) days after receipt of the title commitment and survey to object to any title defects. The Seller shall have fifteen (15) days to cure objections; failure to cure shall entitle the Buyer to terminate and receive a full refund of earnest money.";
  const [ps5, ps5e] = sec(y, 5, "Title and Survey", ps5t);
  p2.push(...ps5); y = ps5e;

  // Section 6 – Closing
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ps6t = "Closing shall occur within thirty (30) days after the expiration of the Due Diligence Period at the offices of the Escrow Agent, or at such other location as the parties may agree. At Closing, the Seller shall deliver a general warranty deed, bill of sale, assignment of leases, tenant estoppel certificates, and all other documents customarily required. Closing costs shall be allocated as follows: the Seller shall pay for the deed preparation, transfer taxes, and Seller's attorney fees; the Buyer shall pay for title insurance, survey, recording fees, and Buyer's attorney fees.";
  const [ps6, ps6e] = sec(y, 6, "Closing", ps6t);
  p2.push(...ps6); y = ps6e;

  // Section 7 – Representations and Warranties
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ps7t = "The Seller represents and warrants that: (a) the Seller has full authority to execute this Agreement and convey the Property; (b) there are no pending or threatened lawsuits, condemnation proceedings, or governmental actions affecting the Property; (c) all building systems are in good working order; (d) the Property complies with all applicable zoning, building, and environmental laws; and (e) all information provided to the Buyer is true and complete. These representations shall survive Closing for a period of twelve (12) months.";
  const [ps7, ps7e] = sec(y, 7, "Representations and Warranties", ps7t);
  p2.push(...ps7); y = ps7e;

  // Section 8 – Risk of Loss
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ps8t = "Risk of loss or damage to the Property shall remain with the Seller until Closing. If the Property is materially damaged or destroyed prior to Closing, the Buyer may elect to: (a) terminate this Agreement and receive a full refund of earnest money; or (b) proceed with Closing and receive an assignment of all insurance proceeds. Material damage shall be defined as damage exceeding five percent (5%) of the Purchase Price.";
  const [ps8, ps8e] = sec(y, 8, "Risk of Loss", ps8t);
  p2.push(...ps8); y = ps8e;

  // Section 9 – Default
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ps9t = "If the Buyer defaults, the Seller's sole remedy shall be retention of the earnest money as liquidated damages. If the Seller defaults, the Buyer may elect to: (a) terminate and receive a full refund of earnest money plus reimbursement of documented out-of-pocket expenses up to Fifty Thousand Dollars ($50,000.00); or (b) pursue specific performance to compel the Seller to complete the sale. The prevailing party in any legal action shall be entitled to recover reasonable attorneys' fees.";
  const [ps9, ps9e] = sec(y, 9, "Default", ps9t);
  p2.push(...ps9); y = ps9e;

  // Section 10 – Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ps10t = "This Agreement shall be governed by the laws of the State of New York. Any disputes shall be resolved in the state or federal courts located in New York County. This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations. Amendments must be in writing and signed by both parties. Time is of the essence with respect to all dates and deadlines in this Agreement.";
  const [ps10, ps10e] = sec(y, 10, "Governing Law", ps10t);
  p2.push(...ps10);

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Commercial Property Purchase Agreement"), divLine(40));
  y = 58;
  p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Seller", "purchase_seller"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Buyer", "purchase_buyer"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  8. PROPERTY MANAGEMENT AGREEMENT
 * ==================================================================== */

function buildPropertyManagement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "Property Management"));
  p1.push(title(MX + 18, 92, "Agreement"));
  p1.push(subtitle(MX + 18, 116, "Professional Property Management Services"));

  // Pills
  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Florida", 180));

  // Accent divider + intro
  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const mgmtIntro = "This Property Management Agreement (\"Agreement\") is entered into as of June 1, 2025, by and between the Property Owner and Management Company identified below. The Management Company shall manage, operate, and maintain the property on behalf of the Owner in accordance with the terms set forth herein.";
  p1.push(body(MX, y, mgmtIntro));
  y += bodyH(mgmtIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "PROPERTY OWNER"));
  p1.push(name(MX + 16, y + 28, "Bayshore Investment Group"));
  p1.push(addr(MX + 16, y + 46, "2600 Bayshore Drive, Suite 300\nMiami, FL 33133"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "MANAGEMENT COMPANY"));
  p1.push(name(MX + 286, y + 28, "Coastal Property Services Inc."));
  p1.push(addr(MX + 286, y + 46, "1400 Brickell Avenue, 8th Floor\nMiami, FL 33131"));
  y += PH + GAP;

  // Section 1 – Authority
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const m1t = "The Owner hereby appoints the Management Company as its exclusive agent to manage the property located at 9500 Collins Avenue, Units 1-48, Miami Beach, FL 33154 (the \"Property\"). The Management Company shall have authority to execute leases, collect rents, hire and supervise maintenance personnel, and take all actions reasonably necessary for the proper management of the Property, subject to the limitations and budget approved by the Owner.";
  const [m1, m1e] = sec(y, 1, "Authority", m1t);
  p1.push(...m1); y = m1e;

  // Section 2 – Services
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m2t = "The Management Company shall provide the following services: (a) marketing and advertising vacant units; (b) tenant screening, including credit checks and background verification; (c) preparation and execution of lease agreements; (d) collection of rent and security deposits; (e) coordination of maintenance and emergency repairs; (f) regular property inspections; (g) handling of tenant complaints and disputes; and (h) compliance with all applicable local, state, and federal laws governing rental properties.";
  const [m2, m2e] = sec(y, 2, "Services", m2t);
  p1.push(...m2); y = m2e;

  // Section 3 – Compensation
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m3t = "The Owner shall compensate the Management Company as follows: (a) a monthly management fee equal to eight percent (8%) of gross collected rents; (b) a leasing fee equal to one month's rent for each new tenant placed; and (c) a renewal fee equal to twenty-five percent (25%) of one month's rent for each lease renewal. The Management Company may also charge a maintenance coordination fee of ten percent (10%) on all repair and maintenance expenditures exceeding Five Hundred Dollars ($500.00). All fees shall be deducted from collected rents before disbursement to the Owner.";
  const [m3, m3e] = sec(y, 3, "Compensation", m3t);
  p1.push(...m3); y = m3e;

  // Section 4 – Term
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m4t = "The initial term of this Agreement shall be twelve (12) months commencing on the Effective Date. This Agreement shall automatically renew for successive twelve-month periods unless either party provides at least sixty (60) days' written notice of intent to terminate prior to the end of the then-current term. During any renewal period, the Management Company may adjust its fee schedule upon thirty (30) days' written notice to the Owner.";
  const [m4, m4e] = sec(y, 4, "Term", m4t);
  p1.push(...m4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Property Management Agreement"), divLine(40));
  y = 48;

  // Section 5 – Insurance
  const m5t = "The Management Company shall maintain, at its own expense, commercial general liability insurance with limits of not less than One Million Dollars ($1,000,000.00) per occurrence, professional liability (errors and omissions) insurance, and workers' compensation insurance as required by law. The Owner shall maintain property insurance, including fire, hazard, and flood coverage, and shall name the Management Company as an additional insured. Both parties shall provide certificates of insurance upon request.";
  const [m5, m5e] = sec(y, 5, "Insurance", m5t);
  p2.push(...m5); y = m5e;

  // Section 6 – Reporting
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const m6t = "The Management Company shall provide the Owner with monthly financial reports including: (a) a detailed income and expense statement; (b) rent roll showing all current tenants, lease terms, and payment status; (c) maintenance and repair log; (d) vacancy report; and (e) bank account reconciliation. Annual reports shall include a year-end financial summary and budget proposal for the following year. All reports shall be delivered within fifteen (15) days of the end of each reporting period.";
  const [m6, m6e] = sec(y, 6, "Reporting", m6t);
  p2.push(...m6); y = m6e;

  // Section 7 – Termination
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const m7t = "Either party may terminate this Agreement: (a) upon sixty (60) days' written notice for any reason; (b) immediately for cause, including material breach uncured within thirty (30) days of written notice; (c) immediately if the other party becomes insolvent or files for bankruptcy. Upon termination, the Management Company shall deliver all records, keys, security deposits, and collected funds to the Owner within thirty (30) days, and shall cooperate in the orderly transition of management responsibilities.";
  const [m7, m7e] = sec(y, 7, "Termination", m7t);
  p2.push(...m7); y = m7e;

  // Section 8 – Liability
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const m8t = "The Management Company shall not be liable for any loss, damage, or expense arising from the management of the Property except to the extent caused by the Management Company's gross negligence or willful misconduct. The Owner shall indemnify and hold harmless the Management Company from all claims, liabilities, and expenses arising from the ownership, operation, or maintenance of the Property, provided the Management Company has acted in good faith and within the scope of its authority under this Agreement.";
  const [m8, m8e] = sec(y, 8, "Liability", m8t);
  p2.push(...m8); y = m8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Property Owner", "mgmt_owner"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Management Company", "mgmt_manager"));
  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  9. MATERIALS SUPPLY CONTRACT
 * ==================================================================== */

function buildMaterialSupply(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Materials Supply Contract"));
  p1.push(subtitle(MX + 18, 96, "Industrial Materials Procurement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: April 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Pennsylvania", 180));

  // Accent div + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const msIntro = "This Materials Supply Contract (\"Agreement\") is entered into as of April 1, 2025, by and between the Supplier and Buyer identified below, for the procurement and delivery of structural steel, reinforced concrete materials, and related industrial supplies in accordance with the specifications set forth herein.";
  p1.push(body(MX, y, msIntro)); y += bodyH(msIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "SUPPLIER"));
  p1.push(name(MX + 16, y + 28, "Ironclad Steel & Materials Co."));
  p1.push(addr(MX + 16, y + 46, "4200 Industrial Boulevard\nPittsburgh, PA 15201"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "BUYER"));
  p1.push(name(MX + 286, y + 28, "Keystone Construction Group"));
  p1.push(addr(MX + 286, y + 46, "1100 Commerce Center Drive\nPhiladelphia, PA 19103"));
  y += PH + GAP;

  // Section 1 — Materials Specification
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const ms1t = "Supplier shall furnish structural steel conforming to ASTM A992/A992M for wide-flange shapes, ASTM A500 Grade C for hollow structural sections, and ASTM A615 Grade 60 for deformed reinforcing bar. All materials shall be domestic mill production with certified Mill Test Reports (MTRs) traceable to the heat of steel. Material substitutions require prior written approval from Buyer's structural engineer of record.";
  const [ms1, ms1e] = sec(y, 1, "Materials Specification", ms1t);
  p1.push(...ms1); y = ms1e;

  // Section 2 — Quantity & Delivery
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const ms2t = "Supplier shall deliver approximately 2,400 tons of structural steel and 800 tons of reinforcing bar according to the delivery schedule attached as Exhibit A. Deliveries shall be made to the project site at intervals not to exceed fourteen (14) calendar days between shipments. Supplier shall provide forty-eight (48) hours advance notice prior to each delivery and coordinate with Buyer's site superintendent for staging and unloading logistics.";
  const [ms2, ms2e] = sec(y, 2, "Quantity & Delivery Schedule", ms2t);
  p1.push(...ms2); y = ms2e;

  // Section 3 — Pricing & Payment
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const ms3t = "Unit prices for all materials are set forth in Schedule B. Prices are firm for the first one hundred eighty (180) days from the Effective Date. Thereafter, Supplier may request a price adjustment based on the Producer Price Index for Iron and Steel (PPI Series WPU101) not to exceed five percent (5%) per adjustment period. Payment terms are Net 45 from receipt of a conforming invoice accompanied by delivery tickets and MTRs. Buyer shall remit payment via wire transfer or ACH.";
  const [ms3, ms3e] = sec(y, 3, "Pricing & Payment Terms", ms3t);
  p1.push(...ms3); y = ms3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Materials Supply Contract"), divLine(40));
  y = 48;

  // Section 4 — Quality Standards
  const ms4t = "All materials shall meet or exceed the specifications of the applicable ASTM standards and the requirements of the American Institute of Steel Construction (AISC) Code of Standard Practice. Supplier shall maintain an ISO 9001-certified quality management system and provide Buyer with current certification upon request. Each shipment shall include complete documentation including MTRs, certificates of compliance, and dimensional inspection reports.";
  const [ms4, ms4e] = sec(y, 4, "Quality Standards & Certifications", ms4t);
  p2.push(...ms4); y = ms4e;

  // Section 5 — Inspection & Testing
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ms5t = "Buyer reserves the right to inspect all materials upon delivery and to reject any materials that do not conform to the specifications. Buyer shall notify Supplier of any nonconformity within five (5) business days of delivery. Supplier shall replace rejected materials at its own expense within ten (10) business days. Buyer may, at its option, engage an independent testing laboratory to verify material properties at Supplier's expense if two or more shipments are rejected within any sixty-day period.";
  const [ms5, ms5e] = sec(y, 5, "Inspection & Rejection Procedures", ms5t);
  p2.push(...ms5); y = ms5e;

  // Section 6 — Warranties
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ms6t = "Supplier warrants that all materials delivered under this Agreement shall: (a) conform to the specifications and ASTM standards identified in Section 1; (b) be free from defects in material and workmanship; (c) be new, unused, and of first quality; and (d) be merchantable and fit for their intended structural purpose. This warranty shall survive for a period of two (2) years from the date of final delivery. Supplier's sole obligation under this warranty is to replace defective materials at no cost to Buyer.";
  const [ms6, ms6e] = sec(y, 6, "Warranties", ms6t);
  p2.push(...ms6); y = ms6e;

  // Section 7 — Force Majeure
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ms7t = "Neither party shall be liable for delays or failures in performance resulting from acts of God, natural disasters, war, terrorism, government sanctions, mill shutdowns, raw material shortages beyond the party's control, or other events beyond reasonable control. The affected party shall provide written notice within seventy-two (72) hours and use commercially reasonable efforts to mitigate the impact. If a force majeure event continues for more than ninety (90) days, either party may terminate this Agreement without liability.";
  const [ms7, ms7e] = sec(y, 7, "Force Majeure", ms7t);
  p2.push(...ms7); y = ms7e;

  // Section 8 — Indemnification
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const ms8t = "Supplier shall indemnify, defend, and hold harmless Buyer and its officers, directors, employees, and agents from and against all claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of or resulting from: (a) defective materials supplied under this Agreement; (b) Supplier's breach of any warranty or representation; or (c) Supplier's negligence or willful misconduct in the performance of its obligations.";
  const [ms8, ms8e] = sec(y, 8, "Indemnification", ms8t);
  p2.push(...ms8); y = ms8e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Materials Supply Contract"), divLine(40));
  y = 48;

  // Section 9 — Default & Remedies
  const ms9t = "A party shall be in default if it fails to perform any material obligation under this Agreement and does not cure such failure within fifteen (15) business days after receiving written notice. In the event of Supplier's default, Buyer may procure substitute materials from an alternative source and charge Supplier for any excess costs incurred. In the event of Buyer's default in payment, Supplier may suspend deliveries until all overdue amounts are paid in full, plus interest at one and one-half percent (1.5%) per month.";
  const [ms9, ms9e] = sec(y, 9, "Default & Remedies", ms9t);
  p3.push(...ms9); y = ms9e;

  // Section 10 — Governing Law
  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const ms10t = "This Agreement shall be governed by and construed in accordance with the laws of the Commonwealth of Pennsylvania, without regard to its conflict of laws principles. Any disputes arising under or related to this Agreement shall be resolved by binding arbitration administered by the American Arbitration Association in Pittsburgh, Pennsylvania. The prevailing party shall be entitled to recover its reasonable attorneys' fees and costs from the non-prevailing party.";
  const [ms10, ms10e] = sec(y, 10, "Governing Law & Dispute Resolution", ms10t);
  p3.push(...ms10); y = ms10e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Supplier", "material_supplier"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Buyer", "material_buyer"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  10. CONSTRUCTION SERVICES AGREEMENT
 * ==================================================================== */

function buildConstructionServices(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Construction Services Agreement"));
  p1.push(subtitle(MX + 18, 96, "General Contractor Engagement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: May 15, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 180));

  // Accent div + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const csIntro = "This Construction Services Agreement (\"Agreement\") is entered into as of May 15, 2025, by and between the Owner and General Contractor identified below, for the construction of improvements at the project site described herein, in accordance with the plans, specifications, and contract documents incorporated by reference.";
  p1.push(body(MX, y, csIntro)); y += bodyH(csIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "OWNER"));
  p1.push(name(MX + 16, y + 28, "Pacific Harbor Development LLC"));
  p1.push(addr(MX + 16, y + 46, "2800 Oceanfront Boulevard\nLos Angeles, CA 90045"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "GENERAL CONTRACTOR"));
  p1.push(name(MX + 286, y + 28, "Cornerstone Builders Inc."));
  p1.push(addr(MX + 286, y + 46, "5500 Construction Way, Suite 200\nSan Diego, CA 92101"));
  y += PH + GAP;

  // Section 1 — Scope of Work
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const cs1t = "Contractor shall furnish all labor, materials, equipment, tools, supervision, and services necessary to complete the construction of a mixed-use commercial building consisting of approximately 85,000 square feet of Class A office space and 12,000 square feet of ground-floor retail, including all site work, foundations, structural framing, building envelope, mechanical, electrical, plumbing, fire protection, and interior finishes as described in the Contract Documents.";
  const [cs1, cs1e] = sec(y, 1, "Scope of Work", cs1t);
  p1.push(...cs1); y = cs1e;

  // Section 2 — Contract Price
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const cs2t = "The Contract Price for all work described herein is Twenty-Four Million Seven Hundred Fifty Thousand Dollars ($24,750,000.00), subject to adjustments for approved Change Orders. The Contract Price includes all costs of labor, materials, equipment, subcontractor work, overhead, and profit. Contractor shall provide a detailed Schedule of Values allocating the Contract Price among the major divisions of work within fifteen (15) days of execution.";
  const [cs2, cs2e] = sec(y, 2, "Contract Price", cs2t);
  p1.push(...cs2); y = cs2e;

  // Section 3 — Payment Schedule
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const cs3t = "Owner shall make monthly progress payments based on Contractor's Applications for Payment, which shall reflect the percentage of each line item completed as verified by the Architect. Owner shall retain ten percent (10%) of each progress payment as retainage until Substantial Completion, at which time retainage shall be reduced to five percent (5%). Final payment, including remaining retainage, shall be made within thirty (30) days after Final Completion and receipt of all closeout documents, lien waivers, and warranties.";
  const [cs3, cs3e] = sec(y, 3, "Payment Schedule & Retainage", cs3t);
  p1.push(...cs3); y = cs3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Construction Services Agreement"), divLine(40));
  y = 48;

  // Section 4 — Project Timeline
  const cs4t = "Contractor shall commence work within ten (10) calendar days of receiving the Notice to Proceed and shall achieve Substantial Completion within eighteen (18) months thereafter. Contractor shall maintain a detailed Critical Path Method (CPM) schedule updated monthly. Liquidated damages for late Substantial Completion shall accrue at the rate of Two Thousand Five Hundred Dollars ($2,500.00) per calendar day, not as a penalty but as a reasonable estimate of Owner's damages from delay.";
  const [cs4, cs4e] = sec(y, 4, "Project Timeline & Liquidated Damages", cs4t);
  p2.push(...cs4); y = cs4e;

  // Section 5 — Change Orders
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const cs5t = "No change to the scope, price, or timeline shall be effective unless documented in a written Change Order signed by both Owner and Contractor. Contractor shall submit a detailed cost and schedule impact proposal within seven (7) business days of any Owner-requested change. The cost of Change Order work shall be determined by: (a) agreed lump sum; (b) unit prices established in the Contract Documents; or (c) cost of the work plus a markup of fifteen percent (15%) for overhead and profit.";
  const [cs5, cs5e] = sec(y, 5, "Change Orders", cs5t);
  p2.push(...cs5); y = cs5e;

  // Section 6 — Insurance Requirements
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const cs6t = "Contractor shall maintain at its own expense: (a) Commercial General Liability insurance with limits of not less than $2,000,000 per occurrence and $5,000,000 aggregate; (b) Workers' Compensation as required by law; (c) Automobile Liability of $1,000,000 combined single limit; and (d) Umbrella/Excess Liability of $5,000,000. Owner shall be named as additional insured on all policies. Certificates of insurance shall be provided prior to commencement of work.";
  const [cs6, cs6e] = sec(y, 6, "Insurance Requirements", cs6t);
  p2.push(...cs6); y = cs6e;

  // Section 7 — Safety & Compliance
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const cs7t = "Contractor shall be solely responsible for safety at the project site and shall comply with all applicable OSHA regulations, state and local safety codes, and Owner's site safety requirements. Contractor shall designate a full-time on-site safety officer, conduct weekly safety meetings, and maintain daily safety logs. Contractor shall immediately report any accidents, injuries, or OSHA citations to Owner within twenty-four (24) hours of occurrence.";
  const [cs7, cs7e] = sec(y, 7, "Safety & Regulatory Compliance", cs7t);
  p2.push(...cs7); y = cs7e;

  // Section 8 — Warranties
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const cs8t = "Contractor warrants all work shall be performed in a good and workmanlike manner, free from defects in materials and workmanship, and in conformance with the Contract Documents. Contractor shall correct any defective work at its own expense for a period of one (1) year from the date of Substantial Completion. Manufacturer warranties for equipment, systems, and materials shall be assigned to Owner and shall not be less than the manufacturer's standard warranty period.";
  const [cs8, cs8e] = sec(y, 8, "Warranties", cs8t);
  p2.push(...cs8); y = cs8e;

  // Section 9 — Liens & Lien Waivers
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const cs9t = "Contractor shall keep the project site and Owner's property free and clear of all mechanics' liens and materialmen's liens. With each Application for Payment, Contractor shall provide conditional lien waivers from all subcontractors and material suppliers for the current payment period and unconditional lien waivers for the prior period. Contractor shall indemnify Owner against all lien claims arising from Contractor's or any subcontractor's failure to pay for labor or materials.";
  const [cs9, cs9e] = sec(y, 9, "Liens & Lien Waivers", cs9t);
  p2.push(...cs9); y = cs9e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Construction Services Agreement"), divLine(40));
  y = 48;

  // Section 10 — Dispute Resolution
  const cs10t = "Any dispute arising under this Agreement shall first be submitted to the Architect for an initial decision. If the dispute remains unresolved after fourteen (14) days, the parties shall engage in mediation administered by a mutually agreed mediator. If mediation fails within thirty (30) days, the dispute shall be resolved by binding arbitration in Los Angeles County, California, in accordance with the Construction Industry Arbitration Rules of the American Arbitration Association.";
  const [cs10, cs10e] = sec(y, 10, "Dispute Resolution", cs10t);
  p3.push(...cs10); y = cs10e;

  // Section 11 — Termination
  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const cs11t = "Owner may terminate this Agreement for convenience upon fourteen (14) days' written notice, in which case Contractor shall be compensated for all work completed, materials ordered, and reasonable demobilization costs. Either party may terminate for cause if the other party materially breaches and fails to cure within ten (10) business days of written notice. Upon termination for cause by Owner, Owner may complete the work using other contractors and charge any excess costs to Contractor.";
  const [cs11, cs11e] = sec(y, 11, "Termination", cs11t);
  p3.push(...cs11); y = cs11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Owner", "construction_owner"));
  p3.push(...sigBlockWithRole(MX + 276, y, "General Contractor", "construction_contractor"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  11. EQUIPMENT LEASE AGREEMENT
 * ==================================================================== */

function buildEquipmentLease(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Equipment Lease Agreement"));
  p1.push(subtitle(MX + 18, 96, "Heavy Equipment & Machinery Rental"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Texas", 170));

  // Accent div + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const elIntro = "This Equipment Lease Agreement (\"Agreement\") is entered into as of June 1, 2025, by and between the Lessor and Lessee identified below, governing the lease of heavy construction equipment described herein for use at the Lessee's designated project site.";
  p1.push(body(MX, y, elIntro)); y += bodyH(elIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LESSOR"));
  p1.push(name(MX + 16, y + 28, "Titan Equipment Rentals LLC"));
  p1.push(addr(MX + 16, y + 46, "7800 Heavy Machinery Road\nHouston, TX 77001"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "LESSEE"));
  p1.push(name(MX + 286, y + 28, "Gulf Coast Infrastructure Corp."));
  p1.push(addr(MX + 286, y + 46, "3200 Pipeline Avenue\nDallas, TX 75201"));
  y += PH + GAP;

  // Section 1 — Equipment Description
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const el1t = "Lessor hereby leases to Lessee the following equipment: (1) Caterpillar 336F Hydraulic Excavator, S/N CAT0336FXYZ1, with standard bucket and hydraulic thumb; (2) Komatsu PC490LC-11 Excavator, S/N KMTPC490ABC2; and (3) Volvo A40G Articulated Hauler, S/N VCE0A40G1234. All equipment shall be delivered in good working condition with current maintenance records and all applicable safety devices installed and operational.";
  const [el1, el1e] = sec(y, 1, "Equipment Description", el1t);
  p1.push(...el1); y = el1e;

  // Section 2 — Lease Term
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const el2t = "The initial lease term shall commence on the delivery date and continue for a period of six (6) months, unless extended by mutual written agreement. Lessee may extend the lease on a month-to-month basis with thirty (30) days' prior written notice at the then-current monthly rate. Lessee may terminate this Agreement early upon fifteen (15) days' written notice, subject to an early termination fee equal to one (1) month's rent.";
  const [el2, el2e] = sec(y, 2, "Lease Term & Renewal", el2t);
  p1.push(...el2); y = el2e;

  // Section 3 — Rent & Payment
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const el3t = "Lessee shall pay monthly rent as follows: Caterpillar 336F at $14,500/month; Komatsu PC490LC-11 at $18,200/month; Volvo A40G at $12,800/month. Rent is due on the first business day of each month. Late payments shall incur a charge of one and one-half percent (1.5%) per month on the outstanding balance. Lessee is responsible for all fuel, lubricants, and consumable operating supplies during the lease term.";
  const [el3, el3e] = sec(y, 3, "Rent & Payment Terms", el3t);
  p1.push(...el3); y = el3e;

  // Section 4 — Maintenance & Repairs
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const el4t = "Lessee shall perform all routine maintenance in accordance with the manufacturer's recommended maintenance schedules, including daily pre-operation inspections, fluid level checks, filter replacements, and lubrication. Lessee shall maintain a written maintenance log for each unit. Major repairs and component failures due to normal wear shall be Lessor's responsibility; however, repairs necessitated by Lessee's misuse, negligence, or failure to perform routine maintenance shall be at Lessee's sole expense.";
  const [el4, el4e] = sec(y, 4, "Maintenance & Repairs", el4t);
  p1.push(...el4); y = el4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Equipment Lease Agreement"), divLine(40));
  y = 48;

  // Section 5 — Insurance
  const el5t = "Lessee shall maintain comprehensive equipment insurance covering all leased equipment for the full replacement value against risks of physical loss or damage, including theft, fire, flood, vandalism, and accidental damage. Lessor shall be named as loss payee on all policies. Lessee shall additionally maintain commercial general liability insurance with limits of not less than $2,000,000 per occurrence covering bodily injury and property damage arising from the operation of the equipment.";
  const [el5, el5e] = sec(y, 5, "Insurance Requirements", el5t);
  p2.push(...el5); y = el5e;

  // Section 6 — Operator Requirements & Use
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const el6t = "All equipment shall be operated exclusively by qualified personnel who hold applicable operator certifications and licenses as required by OSHA and state regulatory authorities. Lessee shall not permit any untrained or uncertified person to operate the equipment. Equipment shall be used solely for its intended construction purpose and shall not be subleased, loaned, or transferred to any third party without Lessor's prior written consent.";
  const [el6, el6e] = sec(y, 6, "Operator Requirements & Permitted Use", el6t);
  p2.push(...el6); y = el6e;

  // Section 7 — Return Condition
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const el7t = "Upon expiration or termination of this Agreement, Lessee shall return all equipment to Lessor's facility in the same condition as received, ordinary wear and tear excepted. Equipment shall be cleaned, fueled, and free of debris. Lessor shall conduct a return inspection within forty-eight (48) hours of receipt. Lessee shall be liable for the cost of repairing any damage beyond normal wear and tear, as determined by Lessor's inspection report and supported by repair estimates.";
  const [el7, el7e] = sec(y, 7, "Return of Equipment", el7t);
  p2.push(...el7); y = el7e;

  // Section 8 — Default & Liability
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const el8t = "Lessee shall be in default if: (a) rent remains unpaid for more than ten (10) days past the due date; (b) Lessee breaches any material term and fails to cure within seven (7) days of written notice; or (c) Lessee becomes insolvent or files for bankruptcy. Upon default, Lessor may repossess the equipment without further notice and Lessee shall be liable for all unpaid rent, repossession costs, repair costs, and any shortfall between remaining lease payments and Lessor's mitigation proceeds.";
  const [el8, el8e] = sec(y, 8, "Default & Liability", el8t);
  p2.push(...el8); y = el8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Lessor", "equip_lessor"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Lessee", "equip_lessee"));
  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  HEALTHCARE TEMPLATES
 * ==================================================================== */

function buildPatientConsent(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Patient Consent Form"));
  p1.push(subtitle(MX + 18, 96, "Informed Consent for Medical Treatment"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: January 10, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const pcIntro = "This Informed Consent Form is provided in accordance with applicable state and federal regulations governing patient rights. By signing below, the Patient acknowledges that they have been informed of the nature, purpose, and risks of the proposed treatment and voluntarily consent to proceed.";
  p1.push(body(MX, y, pcIntro));
  y += bodyH(pcIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "HEALTHCARE PROVIDER"));
  p1.push(name(MX + 16, y + 28, "Bayshore Medical Group"));
  p1.push(addr(MX + 16, y + 46, "4200 Coastal Health Boulevard\nSan Diego, CA 92101"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "PATIENT"));
  p1.push(name(MX + 286, y + 28, "Maria L. Santos"));
  p1.push(addr(MX + 286, y + 46, "1575 Elm Street, Apt 4B\nSan Diego, CA 92103"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The healthcare provider has recommended the treatment or procedure described herein to diagnose, manage, or treat the patient's medical condition. The purpose of the proposed treatment is to improve or stabilize the patient's health status based on the clinical evaluation performed. The provider has explained the medical necessity and expected timeline for the treatment plan.";
  const [s1, s1e] = sec(y, 1, "Purpose of Treatment", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The proposed treatment may include diagnostic tests, physical examinations, laboratory work, imaging studies, medication administration, and any necessary interventional procedures. The provider has described the specific steps involved, the expected duration of each procedure, and the anticipated recovery period. Additional procedures may be recommended based on findings during the course of treatment.";
  const [s2, s2e] = sec(y, 2, "Procedures", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "All medical treatments carry inherent risks, which may include but are not limited to infection, bleeding, allergic reactions, adverse drug interactions, and unforeseen complications. The anticipated benefits include symptom relief, improved function, disease management, and enhanced quality of life. The provider has discussed the likelihood and severity of potential risks specific to the patient's condition and overall health profile.";
  const [s3, s3e] = sec(y, 3, "Risks & Benefits", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The provider has informed the patient of medically reasonable alternatives to the proposed treatment, including conservative management, watchful waiting, pharmacological therapies, physical therapy, or referral to a specialist. The patient understands the potential risks and benefits of each alternative, as well as the consequences of declining treatment altogether.";
  const [s4, s4e] = sec(y, 4, "Alternative Treatments", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Protected Health Information"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Patient Consent Form"), divLine(40));
  y = 48;

  const [s5, s5e] = sec(y, 5, "Right to Refuse",
    "The patient has the right to refuse any or all proposed treatments at any time without prejudice to future care. Refusal of treatment will be documented in the medical record. The provider has explained the potential consequences of refusing the recommended treatment, including the possibility of worsening symptoms, disease progression, or other adverse outcomes.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Confidentiality",
    "All patient information, including medical records, treatment plans, and personal health data, will be maintained in strict confidence in accordance with the Health Insurance Portability and Accountability Act (HIPAA) and applicable state privacy laws. Information will only be disclosed to authorized individuals directly involved in the patient's care or as required by law.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Financial Responsibility",
    "The patient acknowledges financial responsibility for all charges incurred in connection with the proposed treatment, including co-payments, deductibles, and any services not covered by the patient's insurance plan. The provider's office will submit claims to the patient's insurance carrier; however, the patient remains ultimately responsible for any unpaid balances. A good-faith estimate of costs will be provided upon request.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Emergency Contact",
    "The patient authorizes the healthcare provider to contact the designated emergency contact person in the event of a medical emergency or if the patient becomes incapacitated during treatment. The emergency contact will be notified only to the extent necessary to ensure the patient's safety and continuity of care.");
  p2.push(...s8); y = s8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Healthcare Provider", "patientConsent_provider"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Patient", "patientConsent_patient"));

  p2.push(footer(772, "Confidential — Protected Health Information"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildHipaaBAA(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "HIPAA Business Associate"));
  p1.push(subtitle(MX + 18, 96, "Business Associate Agreement Under 45 CFR \u00A7 164.504(e)"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 1, 2025", 180));
  p1.push(pill(MX + 212, y, 210), meta(MX + 232, y + PILL_TEXT_Y, "Regulation: 45 CFR Parts 160 & 164", 190));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const baaIntro = "This Business Associate Agreement (\"BAA\") is entered into as of March 1, 2025, by and between the parties identified below, pursuant to the Health Insurance Portability and Accountability Act of 1996 (\"HIPAA\"), the HITECH Act, and their implementing regulations at 45 CFR Parts 160 and 164, to ensure the proper safeguarding of Protected Health Information (\"PHI\").";
  p1.push(body(MX, y, baaIntro));
  y += bodyH(baaIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COVERED ENTITY"));
  p1.push(name(MX + 16, y + 28, "Northwind Health Systems"));
  p1.push(addr(MX + 16, y + 46, "900 Medical Center Parkway\nNashville, TN 37203"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "BUSINESS ASSOCIATE"));
  p1.push(name(MX + 286, y + 28, "CloudMed Analytics Inc."));
  p1.push(addr(MX + 286, y + 46, "2250 Innovation Drive, Suite 500\nAtlanta, GA 30309"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const b1t = "For purposes of this Agreement, \"Protected Health Information\" (PHI) has the meaning set forth in 45 CFR \u00A7 160.103 and includes all individually identifiable health information transmitted or maintained in any form. \"Breach\" has the meaning set forth in 45 CFR \u00A7 164.402. \"Security Incident\" means the attempted or successful unauthorized access, use, disclosure, modification, or destruction of information as defined in 45 CFR \u00A7 164.304.";
  const [b1, b1e] = sec(y, 1, "Definitions", b1t);
  p1.push(...b1); y = b1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const b2t = "The Business Associate agrees to: (a) use or disclose PHI only as permitted by this Agreement or as required by law; (b) implement administrative, physical, and technical safeguards as required by 45 CFR \u00A7 164.308, \u00A7 164.310, and \u00A7 164.312; (c) report to the Covered Entity any use or disclosure of PHI not provided for by this Agreement; and (d) ensure that any subcontractors who create, receive, maintain, or transmit PHI agree to the same restrictions and conditions.";
  const [b2, b2e] = sec(y, 2, "Obligations of Business Associate", b2t);
  p1.push(...b2); y = b2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const b3t = "The Business Associate may use or disclose PHI solely for the purpose of performing services on behalf of the Covered Entity as specified in the underlying service agreement. The Business Associate may also use PHI for its proper management and administration, or to carry out its legal responsibilities, provided that each disclosure is required by law or the Business Associate obtains reasonable assurances from the recipient that the information will be held confidentially.";
  const [b3, b3e] = sec(y, 3, "Permitted Uses and Disclosures", b3t);
  p1.push(...b3); y = b3e;

  p1.push(footer(772, "HIPAA Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("HIPAA Business Associate Agreement"), divLine(40));
  y = 48;

  const [b4, b4e] = sec(y, 4, "Safeguards",
    "The Business Associate shall implement and maintain appropriate safeguards to prevent the unauthorized use or disclosure of PHI, including encryption of electronic PHI (ePHI) at rest and in transit as specified in 45 CFR \u00A7 164.312(a)(2)(iv) and \u00A7 164.312(e)(2)(ii). The Business Associate shall conduct periodic risk assessments in accordance with 45 CFR \u00A7 164.308(a)(1)(ii)(A) and promptly remediate any identified vulnerabilities.");
  p2.push(...b4); y = b4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [b5, b5e] = sec(y, 5, "Breach Notification",
    "The Business Associate shall report any Breach of Unsecured PHI to the Covered Entity without unreasonable delay and no later than thirty (30) calendar days after discovery of the Breach, as required by 45 CFR \u00A7 164.410. The notification shall include the identification of each individual whose PHI has been or is reasonably believed to have been affected, a description of the nature of the Breach, and the corrective steps taken to mitigate harm.");
  p2.push(...b5); y = b5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [b6, b6e] = sec(y, 6, "Term and Termination",
    "This Agreement shall remain in effect for the duration of the underlying service agreement between the parties. The Covered Entity may terminate this Agreement if it determines that the Business Associate has violated a material term. Upon termination, the obligations of the Business Associate with respect to PHI already received shall survive until all PHI has been returned or destroyed in accordance with Section 7.");
  p2.push(...b6); y = b6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [b7, b7e] = sec(y, 7, "Return or Destruction of PHI",
    "Upon termination of this Agreement, the Business Associate shall return or destroy all PHI received from or created on behalf of the Covered Entity, in accordance with 45 CFR \u00A7 164.504(e)(2)(ii)(J). If return or destruction is not feasible, the Business Associate shall extend the protections of this Agreement to the retained PHI and limit further uses and disclosures to the purposes that make return or destruction infeasible.");
  p2.push(...b7); y = b7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [b8, b8e] = sec(y, 8, "Covered Entity Obligations",
    "The Covered Entity shall: (a) notify the Business Associate of any limitations in its Notice of Privacy Practices that may affect the Business Associate's use or disclosure of PHI; (b) notify the Business Associate of any changes in or revocation of authorization by an individual; and (c) notify the Business Associate of any restrictions on the use or disclosure of PHI agreed to by the Covered Entity pursuant to 45 CFR \u00A7 164.522.");
  p2.push(...b8); y = b8e;

  p2.push(footer(772, "HIPAA Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("HIPAA Business Associate Agreement"), divLine(40));
  y = 48;

  const [b9, b9e] = sec(y, 9, "Amendment",
    "The parties agree to negotiate in good faith any amendment to this Agreement that may be required to ensure compliance with changes to HIPAA, the HITECH Act, or their implementing regulations. No modification of this Agreement shall be effective unless it is in writing and signed by both parties. If any provision of this Agreement is held invalid, the remaining provisions shall remain in full force and effect.");
  p3.push(...b9); y = b9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [b10, b10e] = sec(y, 10, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with applicable federal law, including HIPAA and the HITECH Act, and the laws of the State of Tennessee to the extent not preempted by federal law. Any disputes arising under this Agreement shall be resolved in the state or federal courts located in Davidson County, Tennessee. The prevailing party in any such action shall be entitled to recover reasonable attorneys' fees and costs.");
  p3.push(...b10); y = b10e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Covered Entity", "hipaaBAA_coveredEntity"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Business Associate", "hipaaBAA_businessAssociate"));

  p3.push(footer(772, "HIPAA Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

function buildMedicalRelease(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Medical Records Release"));
  p1.push(subtitle(MX + 18, 96, "Authorization for Disclosure of Protected Health Information"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Date: February 5, 2025", 180));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const mrIntro = "I hereby authorize the release of my protected health information as described below. This authorization is made voluntarily and I understand that I may revoke it at any time by submitting a written request to the healthcare provider, except to the extent that action has already been taken in reliance on this authorization.";
  p1.push(body(MX, y, mrIntro));
  y += bodyH(mrIntro) + GAP;

  // Single patient card
  const PH = 80;
  p1.push(...pCard(MX, y, CW, PH, INK));
  p1.push(label(MX + 16, y + 12, "PATIENT"));
  p1.push(name(MX + 16, y + 28, "David R. Nguyen"));
  p1.push(addr(MX + 16, y + 46, "832 Maple Avenue, Unit 12, Portland, OR 97205  \u00B7  DOB: 04/17/1985", CW - 32));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const m1t = "I authorize the release of the following records: complete medical history, laboratory and diagnostic test results, imaging studies, treatment summaries, medication lists, surgical reports, and discharge summaries. This authorization covers records from the past five (5) years unless otherwise specified in writing.";
  const [m1, m1e] = sec(y, 1, "Records to Be Released", m1t);
  p1.push(...m1); y = m1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m2t = "The purpose of this disclosure is for continuity of care, transfer of treatment to a new provider, insurance claim processing, or personal records as indicated by the patient. Information released shall be used solely for the stated purpose and shall not be further disclosed without additional written authorization.";
  const [m2, m2e] = sec(y, 2, "Purpose of Disclosure", m2t);
  p1.push(...m2); y = m2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m3t = "Records shall be released to the following authorized recipient: the receiving healthcare provider, insurer, or individual designated by the patient. The recipient is responsible for maintaining the confidentiality of all information received and may not re-disclose it without the patient's express written consent.";
  const [m3, m3e] = sec(y, 3, "Authorized Recipient", m3t);
  p1.push(...m3); y = m3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m4t = "This authorization shall expire one hundred eighty (180) days from the date of the patient's signature below, or upon written revocation by the patient, whichever occurs first. If no expiration date is specified, this authorization expires automatically twelve (12) months from the date signed.";
  const [m4, m4e] = sec(y, 4, "Expiration", m4t);
  p1.push(...m4); y = m4e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const m5t = "The patient has the right to receive a copy of this authorization, to revoke it at any time, and to refuse to sign without affecting the right to receive treatment. The provider may not condition treatment, payment, or eligibility on the signing of this authorization except where permitted by law.";
  const [m5, m5e] = sec(y, 5, "Patient Rights", m5t);
  p1.push(...m5); y = m5e;

  // Signature
  y += DIV_PRE; p1.push(accentDiv(y)); y += 10;
  p1.push(text({ x: MX, y, text: "Authorization", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p1.push(...sigBlockWithRole(MX, y, "Patient", "medicalRelease_patient"));

  p1.push(footer(772, "Confidential — Protected Health Information"), footerR(772, "Page 1 of 1"));

  return [page(p1)];
}

/* ====================================================================
 *  TECHNOLOGY TEMPLATES
 * ==================================================================== */

function buildSaasAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "SaaS Subscription Agreement"));
  p1.push(subtitle(MX + 18, 96, "Cloud Service Subscription & Service Level Terms"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: January 1, 2026", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This SaaS Subscription Agreement (\"Agreement\") is entered into as of January 1, 2026, by and between the parties identified below. This Agreement governs the Customer's access to and use of the Provider's cloud-based software platform, including all associated services, updates, documentation, and support. By executing this Agreement, both parties agree to be bound by its terms and conditions.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "PROVIDER"));
  p1.push(name(MX + 16, y + 28, "CloudNexus Technologies Inc."));
  p1.push(addr(MX + 16, y + 46, "2400 Innovation Parkway\nSan Jose, CA 95134"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CUSTOMER"));
  p1.push(name(MX + 286, y + 28, "Vertex Digital Solutions LLC"));
  p1.push(addr(MX + 286, y + 46, "780 Enterprise Boulevard\nChicago, IL 60606"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Provider shall make its cloud-based software platform (the \"Service\") available to the Customer via secure internet connection during the Subscription Term. The Service includes all features and modules identified in the applicable Order Form, along with standard technical support, system maintenance, and automatic updates. The Provider shall host the Service on enterprise-grade infrastructure with geographically distributed data centers to ensure high availability and disaster recovery capabilities.";
  const [s1, s1e] = sec(y, 1, "Service Description", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The initial Subscription Term shall commence on the Effective Date and continue for twelve (12) months. The subscription shall automatically renew for successive twelve-month periods unless either party provides written notice of non-renewal at least thirty (30) days prior to the end of the then-current term. The Provider shall notify the Customer of any pricing changes at least sixty (60) days before the renewal date, and such changes shall take effect upon the next renewal period.";
  const [s2, s2e] = sec(y, 2, "Subscription Term & Renewal", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Customer shall pay the subscription fees as set forth in the applicable Order Form. All fees are due within thirty (30) days of invoice date and are non-refundable except as expressly provided herein. Late payments shall accrue interest at the rate of 1.5% per month or the maximum rate permitted by law, whichever is less. The Provider reserves the right to suspend access to the Service if payment is more than fifteen (15) days overdue after written notice.";
  const [s3, s3e] = sec(y, 3, "Fees & Payment", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential — SaaS Subscription Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("SaaS Subscription Agreement"), divLine(40));
  y = 48;

  const s4t = "The Provider guarantees a monthly uptime of 99.9% for the Service, measured as the total minutes in a calendar month minus downtime, divided by total minutes in that month. Scheduled maintenance windows (not to exceed four hours per month, with 48-hour advance notice) are excluded from downtime calculations. In the event the Provider fails to meet the uptime commitment, the Customer shall receive a service credit equal to 5% of the monthly fee for each full 0.1% below the guaranteed level, up to a maximum credit of 30% of the monthly fee.";
  const [s4, s4e] = sec(y, 4, "Service Level Agreement", s4t);
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s5t = "The Customer retains all right, title, and interest in and to its data submitted to the Service (\"Customer Data\"). The Provider acquires no ownership rights in Customer Data and shall not access, use, or disclose it except as necessary to provide the Service or as required by law. Upon termination or expiration, the Provider shall make Customer Data available for export in a standard, machine-readable format for a period of thirty (30) days, after which the Provider shall securely delete all Customer Data from its systems within sixty (60) days.";
  const [s5, s5e] = sec(y, 5, "Data Ownership & Portability", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Provider shall implement and maintain administrative, technical, and physical safeguards designed to protect Customer Data in accordance with industry best practices. These measures include AES-256 encryption at rest and TLS 1.2+ encryption in transit, multi-factor authentication, role-based access controls, regular vulnerability scanning and penetration testing, and SOC 2 Type II compliance. The Provider shall promptly notify the Customer of any security breach affecting Customer Data within seventy-two (72) hours of discovery.";
  const [s6, s6e] = sec(y, 6, "Security Measures", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "The Provider retains all right, title, and interest in the Service, including all software, algorithms, interfaces, documentation, and related intellectual property. The Customer is granted a limited, non-exclusive, non-transferable license to access and use the Service during the Subscription Term solely for its internal business purposes. Neither party shall use the other's trademarks or trade names without prior written consent.";
  const [s7, s7e] = sec(y, 7, "Intellectual Property", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Each party agrees to maintain the confidentiality of all proprietary and confidential information received from the other party during the term of this Agreement. Confidential information shall not be disclosed to any third party without prior written consent, except to employees, contractors, or advisors who have a need to know and are bound by obligations of confidentiality no less restrictive than those herein. This obligation shall survive termination of this Agreement for a period of three (3) years.";
  const [s8, s8e] = sec(y, 8, "Confidentiality", s8t);
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential — SaaS Subscription Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("SaaS Subscription Agreement"), divLine(40));
  y = 48;

  const s9t = "Except for breaches of confidentiality, data security obligations, or indemnification duties, neither party shall be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, revenue, data, or business opportunity. Each party's total aggregate liability under this Agreement shall not exceed the total fees paid or payable by the Customer during the twelve (12) months immediately preceding the event giving rise to the claim.";
  const [s9, s9e] = sec(y, 9, "Limitation of Liability", s9t);
  p3.push(...s9); y = s9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s10t = "Either party may terminate this Agreement: (a) upon thirty (30) days' written notice if the other party materially breaches and fails to cure within that period; (b) immediately if the other party becomes insolvent, files for bankruptcy, or ceases business operations; or (c) by providing written notice of non-renewal as specified in Section 2. Upon termination, the Customer's access to the Service shall cease, and the data export provisions of Section 5 shall apply.";
  const [s10, s10e] = sec(y, 10, "Termination", s10t);
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s11t = "This Agreement shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of laws principles. Any disputes arising under or in connection with this Agreement shall be resolved exclusively in the state or federal courts located in Santa Clara County, California. This Agreement constitutes the entire agreement between the parties and supersedes all prior negotiations and agreements, whether written or oral.";
  const [s11, s11e] = sec(y, 11, "Governing Law & Entire Agreement", s11t);
  p3.push(...s11); y = s11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Provider", "saasAgreement_provider"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Customer", "saasAgreement_customer"));
  p3.push(footer(772, "Confidential — SaaS Subscription Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

function buildSoftwareLicense(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Software License Agreement"));
  p1.push(subtitle(MX + 18, 96, "Enterprise Software License & Maintenance Terms"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 15, 2026", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Software License Agreement (\"Agreement\") is entered into as of March 15, 2026, by and between the Licensor and the Licensee identified below. This Agreement sets forth the terms and conditions under which the Licensor grants the Licensee a license to use its proprietary software, including all related documentation, updates, and support services as described herein.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LICENSOR"));
  p1.push(name(MX + 16, y + 28, "Arcline Software Corp."));
  p1.push(addr(MX + 16, y + 46, "1500 Broadway, 22nd Floor\nNew York, NY 10036"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "LICENSEE"));
  p1.push(name(MX + 286, y + 28, "Trident Financial Group Inc."));
  p1.push(addr(MX + 286, y + 46, "900 North Michigan Avenue\nChicago, IL 60611"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "Subject to the terms of this Agreement and payment of all applicable fees, the Licensor hereby grants the Licensee a non-exclusive, non-transferable, non-sublicensable license to install and use the Software on the Licensee's systems for up to the number of authorized seats specified in the Order Form. Each seat entitles one named user to access and use the Software. The Licensee may reassign a seat to a new user no more than once per calendar quarter, provided the prior user's access is permanently revoked.";
  const [s1, s1e] = sec(y, 1, "License Grant", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Licensee shall not: (a) copy, modify, adapt, translate, or create derivative works of the Software; (b) reverse-engineer, decompile, disassemble, or otherwise attempt to derive the source code; (c) sublicense, rent, lease, loan, or distribute the Software to any third party; (d) remove or alter any proprietary notices, labels, or marks on the Software; or (e) use the Software to provide services to third parties, including as a service bureau, managed service, or outsourced arrangement, without the Licensor's prior written consent.";
  const [s2, s2e] = sec(y, 2, "Restrictions", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Licensee shall pay the license fees and any applicable maintenance and support fees as specified in the Order Form. License fees are due upon execution of this Agreement. Annual maintenance and support fees are due at the beginning of each renewal year and are non-refundable. The Licensor may increase maintenance fees by up to 5% annually upon sixty (60) days' written notice prior to the applicable renewal date.";
  const [s3, s3e] = sec(y, 3, "Fees & Payment", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential — Software License Agreement"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Software License Agreement"), divLine(40));
  y = 48;

  const s4t = "During the maintenance term, the Licensor shall provide: (a) commercially reasonable efforts to correct documented defects and errors; (b) all major version updates, minor releases, and security patches at no additional charge; (c) technical support via email and phone during business hours (9 AM to 6 PM ET, Monday through Friday); and (d) access to online documentation and knowledge base resources. The Licensor targets a first-response time of four (4) business hours for critical issues and twenty-four (24) hours for non-critical requests.";
  const [s4, s4e] = sec(y, 4, "Support & Maintenance", s4t);
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s5t = "The Software and all copies, modifications, and derivative works thereof are and shall remain the exclusive property of the Licensor, including all patents, copyrights, trade secrets, and other intellectual property rights. The Licensee acknowledges that the license granted herein does not convey any ownership interest in the Software. Any feedback, suggestions, or improvements provided by the Licensee may be freely used by the Licensor without obligation or compensation.";
  const [s5, s5e] = sec(y, 5, "Intellectual Property", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Licensor warrants that for a period of ninety (90) days from delivery, the Software shall perform substantially in accordance with its published documentation. If the Software fails to conform to this warranty, the Licensor shall, at its option, repair or replace the non-conforming Software or refund the license fees paid. THIS WARRANTY IS EXCLUSIVE AND IN LIEU OF ALL OTHER WARRANTIES, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE.";
  const [s6, s6e] = sec(y, 6, "Warranties & Disclaimer", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "In no event shall either party be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunity, regardless of the cause of action or theory of liability. The Licensor's total cumulative liability under this Agreement shall not exceed the total license fees actually paid by the Licensee in the twelve (12) months preceding the claim.";
  const [s7, s7e] = sec(y, 7, "Limitation of Liability", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "This Agreement may be terminated: (a) by the Licensee for convenience upon thirty (30) days' written notice; (b) by either party if the other party materially breaches and fails to cure within thirty (30) days of written notice; or (c) immediately by the Licensor if the Licensee breaches any license restrictions in Section 2. Upon termination, the Licensee shall immediately cease all use of the Software and destroy all copies in its possession, certifying such destruction in writing within ten (10) days.";
  const [s8, s8e] = sec(y, 8, "Termination", s8t);
  p2.push(...s8); y = s8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Licensor", "softwareLicense_licensor"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Licensee", "softwareLicense_licensee"));
  p2.push(footer(772, "Confidential — Software License Agreement"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildDataProcessing(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Data Processing Agreement"));
  p1.push(subtitle(MX + 18, 96, "GDPR-Compliant Data Processing Terms"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: February 1, 2026", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Regulation: EU GDPR", 180));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Data Processing Agreement (\"DPA\") is entered into as of February 1, 2026, by and between the Data Controller and the Data Processor identified below. This DPA supplements and forms part of the underlying service agreement between the parties and establishes the obligations of each party with respect to the processing of personal data in compliance with Regulation (EU) 2016/679 (the \"General Data Protection Regulation\" or \"GDPR\") and any applicable national implementing legislation.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "DATA CONTROLLER"));
  p1.push(name(MX + 16, y + 28, "NordicTech Enterprises A/S"));
  p1.push(addr(MX + 16, y + 46, "Vesterbrogade 74\nDK-1620 Copenhagen, Denmark"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "DATA PROCESSOR"));
  p1.push(name(MX + 286, y + 28, "CloudNexus Technologies Inc."));
  p1.push(addr(MX + 286, y + 46, "2400 Innovation Parkway\nSan Jose, CA 95134"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "For the purposes of this DPA, \"Personal Data,\" \"Data Subject,\" \"Processing,\" \"Controller,\" \"Processor,\" \"Supervisory Authority,\" and \"Personal Data Breach\" shall have the meanings ascribed to them in Article 4 of the GDPR. \"Sub-processor\" means any third party engaged by the Processor to process Personal Data on behalf of the Controller. \"Standard Contractual Clauses\" refers to the clauses approved by the European Commission pursuant to Article 46(2)(c) of the GDPR for transfers of Personal Data to third countries.";
  const [s1, s1e] = sec(y, 1, "Definitions", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Processor shall process Personal Data solely on behalf of the Controller and only in accordance with the Controller's documented instructions, as required under Article 28(3)(a) of the GDPR. The categories of Personal Data processed include names, email addresses, IP addresses, usage analytics, and any other data submitted by users of the Controller's platform. The processing activities include collection, storage, organization, retrieval, hosting, and deletion of Personal Data as necessary to provide the services under the underlying service agreement.";
  const [s2, s2e] = sec(y, 2, "Scope & Purpose of Processing", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Processor shall: (a) process Personal Data only on documented instructions from the Controller, including with regard to transfers to third countries, unless required by Union or Member State law (Article 28(3)(a)); (b) ensure that persons authorized to process the data have committed themselves to confidentiality (Article 28(3)(b)); (c) implement all appropriate technical and organizational measures as required by Article 32; (d) assist the Controller in fulfilling its obligations to respond to Data Subject requests under Articles 15 through 22; and (e) make available to the Controller all information necessary to demonstrate compliance with Article 28.";
  const [s3, s3e] = sec(y, 3, "Processor Obligations", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential — Data Processing Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Data Processing Agreement"), divLine(40));
  y = 48;

  const s4t = "The Processor shall not engage any Sub-processor without the prior specific or general written authorization of the Controller, in accordance with Article 28(2) of the GDPR. Where general authorization is given, the Processor shall inform the Controller of any intended changes concerning the addition or replacement of Sub-processors, giving the Controller the opportunity to object within fourteen (14) days. The Processor shall impose the same data protection obligations on Sub-processors by way of a written contract and shall remain fully liable for the performance of each Sub-processor's obligations.";
  const [s4, s4e] = sec(y, 4, "Sub-processors", s4t);
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s5t = "The Processor shall promptly assist the Controller in responding to requests from Data Subjects exercising their rights under Chapter III of the GDPR, including the right of access (Article 15), rectification (Article 16), erasure (Article 17), restriction of processing (Article 18), data portability (Article 20), and the right to object (Article 21). The Processor shall notify the Controller without undue delay upon receiving any such request directly from a Data Subject and shall not respond independently unless authorized in writing by the Controller.";
  const [s5, s5e] = sec(y, 5, "Data Subject Rights", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "Pursuant to Article 32 of the GDPR, the Processor shall implement appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including: (a) pseudonymization and encryption of Personal Data using AES-256 at rest and TLS 1.2+ in transit; (b) measures to ensure ongoing confidentiality, integrity, availability, and resilience of processing systems; (c) disaster recovery and backup procedures to restore data availability in a timely manner; and (d) regular testing, assessment, and evaluation of the effectiveness of these measures through annual penetration testing and SOC 2 Type II audits.";
  const [s6, s6e] = sec(y, 6, "Security Measures", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "In accordance with Article 33(2) of the GDPR, the Processor shall notify the Controller without undue delay and in any event within thirty-six (36) hours after becoming aware of a Personal Data Breach. The notification shall include: (a) a description of the nature of the breach, including categories and approximate number of Data Subjects and records affected; (b) the name and contact details of the Processor's data protection officer or contact point; (c) a description of likely consequences; and (d) a description of measures taken or proposed to address the breach and mitigate its effects.";
  const [s7, s7e] = sec(y, 7, "Breach Notification", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "The Processor shall not transfer Personal Data to any country outside the European Economic Area (\"EEA\") unless: (a) the European Commission has issued an adequacy decision for the destination country under Article 45 of the GDPR; (b) appropriate safeguards have been provided in accordance with Article 46, such as Standard Contractual Clauses or Binding Corporate Rules; or (c) one of the derogations in Article 49 applies. The Processor currently processes data in facilities located in the EU (Frankfurt, Germany) and the United States (under Standard Contractual Clauses).";
  const [s8, s8e] = sec(y, 8, "International Data Transfers", s8t);
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential — Data Processing Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Data Processing Agreement"), divLine(40));
  y = 48;

  const s9t = "The Processor shall make available to the Controller all information necessary to demonstrate compliance with the obligations laid down in Article 28 and shall allow for and contribute to audits, including inspections, conducted by the Controller or an independent auditor mandated by the Controller. Audits may be conducted no more than once per calendar year, upon thirty (30) days' prior written notice, during normal business hours, and subject to reasonable confidentiality obligations. The Processor shall cooperate fully and provide access to relevant facilities, systems, and documentation.";
  const [s9, s9e] = sec(y, 9, "Audit Rights", s9t);
  p3.push(...s9); y = s9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s10t = "This DPA shall remain in effect for as long as the Processor processes Personal Data on behalf of the Controller. Upon termination of the underlying service agreement, this DPA shall automatically terminate, subject to the Processor's continuing obligations regarding data return and deletion as set forth in Section 11. Either party may terminate this DPA immediately if the other party is in material breach and fails to cure within thirty (30) days of receiving written notice.";
  const [s10, s10e] = sec(y, 10, "Term & Termination", s10t);
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s11t = "Upon termination of processing or upon the Controller's written request, the Processor shall, at the Controller's election pursuant to Article 28(3)(g) of the GDPR: (a) return all Personal Data to the Controller in a structured, commonly used, and machine-readable format; or (b) securely delete all Personal Data and existing copies, unless Union or Member State law requires further storage. The Processor shall complete the return or deletion within thirty (30) days and provide written certification of deletion upon the Controller's request. Any retained data shall remain subject to the terms of this DPA.";
  const [s11, s11e] = sec(y, 11, "Data Return & Deletion", s11t);
  p3.push(...s11); y = s11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Data Controller", "dataProcessing_controller"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Data Processor", "dataProcessing_processor"));
  p3.push(footer(772, "Confidential — Data Processing Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  HR / EMPLOYMENT TEMPLATES
 * ==================================================================== */

function buildHandbookAck(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Handbook Acknowledgment"));
  p1.push(subtitle(MX + 18, 96, "Employee Receipt & Understanding Confirmation"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: January 1, 2025", 180));
  p1.push(pill(MX + 212, y, 180), meta(MX + 232, y + PILL_TEXT_Y, "Version: 2025 Rev. A", 160));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This acknowledgment confirms that the undersigned employee has received, reviewed, and understands the contents of the Company's Employee Handbook. The Handbook outlines the policies, procedures, and expectations that govern the employment relationship.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 80;
  p1.push(...fCard(MX, y, CW, PH));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "Apex Dynamics Inc."));
  p1.push(addr(MX + 16, y + 46, "500 Corporate Center Drive, Suite 200  \u00B7  Charlotte, NC 28202", CW - 32));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Employee Handbook serves as a comprehensive guide to Company policies, workplace expectations, and employee benefits. It is designed to provide clarity on the standards of conduct, available resources, and the mutual obligations shared between the Company and its employees. All employees are expected to familiarize themselves with its contents and to seek clarification from Human Resources on any provisions they do not fully understand.";
  const [s1, s1e] = sec(y, 1, "Purpose", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Employment with the Company is at-will, meaning that either the employee or the Company may terminate the employment relationship at any time, with or without cause or prior notice. Nothing in the Employee Handbook creates an express or implied contract of employment, nor does it guarantee employment for any specific duration. Only the Company's Chief Executive Officer has the authority to enter into binding employment agreements.";
  const [s2, s2e] = sec(y, 2, "At-Will Employment", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "All employees are expected to conduct themselves with professionalism, integrity, and respect. The Code of Conduct outlined in the Handbook covers ethical business practices, use of Company property, attendance standards, dress code, and the proper handling of conflicts of interest. Violations of these policies may result in disciplinary action up to and including termination.";
  const [s3, s3e] = sec(y, 3, "Code of Conduct", s3t);
  p1.push(...s3); y = s3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Company is committed to maintaining a workplace free from harassment, discrimination, and retaliation. All employees are required to review and comply with the Anti-Harassment Policy, which prohibits conduct based on race, color, religion, sex, national origin, age, disability, or any other protected characteristic. Employees who experience or witness harassment must report it immediately through the channels described in the Handbook.";
  const [s4, s4e] = sec(y, 4, "Anti-Harassment Policy", s4t);
  p1.push(...s4); y = s4e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s5t = "Employees may have access to proprietary information, trade secrets, and confidential business data during the course of their employment. All such information must be kept strictly confidential and may not be disclosed to any unauthorized party, whether during or after employment. Breach of confidentiality obligations may result in disciplinary action and legal remedies.";
  const [s5, s5e] = sec(y, 5, "Confidentiality", s5t);
  p1.push(...s5); y = s5e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s6t = "By signing below, I acknowledge that I have received a copy of the Employee Handbook, that I have read and understood its contents, and that I agree to comply with all policies and procedures described therein. I understand that the Company reserves the right to modify, revoke, or supplement any policy at any time, and that the most current version of the Handbook will always be available through Human Resources.";
  const [s6, s6e] = sec(y, 6, "Acknowledgment", s6t);
  p1.push(...s6); y = s6e;

  // Signature
  y += DIV_PRE; p1.push(accentDiv(y)); y += 10;
  p1.push(text({ x: MX, y, text: "Employee Signature", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p1.push(...sigBlockWithRole(MX, y, "Employee", "handbookAck_employee"));

  p1.push(footer(772, "Confidential — Human Resources"), footerR(772, "Page 1 of 1"));

  return [page(p1)];
}

function buildSeparationAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Separation Agreement"));
  p1.push(subtitle(MX + 18, 96, "Mutual Release & Severance Terms"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 15, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 180));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Separation Agreement and General Release (\"Agreement\") is entered into by and between the Employer and the Employee identified below. This Agreement sets forth the complete terms and conditions governing the separation of the Employee's employment, including severance benefits, release of claims, and post-employment obligations.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "EMPLOYER"));
  p1.push(name(MX + 16, y + 28, "Stratus Technologies Inc."));
  p1.push(addr(MX + 16, y + 46, "2200 Enterprise Boulevard\nSan Jose, CA 95110"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "EMPLOYEE"));
  p1.push(name(MX + 286, y + 28, "Morgan Taylor"));
  p1.push(addr(MX + 286, y + 46, "840 Cypress Lane\nSanta Clara, CA 95051"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Employee's last day of active employment with the Employer shall be March 31, 2025 (the \"Separation Date\"). The Employee shall continue to perform their regular duties through the Separation Date unless otherwise directed by the Employer. The Employee's access to Company systems, facilities, and resources shall terminate at the close of business on the Separation Date.";
  const [s1, s1e] = sec(y, 1, "Separation Date", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "In consideration of the Employee's execution of this Agreement and compliance with all terms herein, the Employer shall provide the Employee with severance compensation equal to twelve (12) weeks of the Employee's current base salary, less applicable withholdings and deductions. Severance payments shall commence within fourteen (14) days following the Effective Date of this Agreement and shall be paid in accordance with the Employer's standard payroll schedule.";
  const [s2, s2e] = sec(y, 2, "Severance Compensation", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Employer shall continue to pay its portion of the Employee's group health, dental, and vision insurance premiums under COBRA for a period of three (3) months following the Separation Date, provided the Employee timely elects COBRA continuation coverage. After this subsidized period, the Employee may continue COBRA coverage at their own expense for the remainder of the applicable COBRA period. All other employee benefits, including 401(k) contributions, paid time off accrual, and equity vesting, shall cease as of the Separation Date.";
  const [s3, s3e] = sec(y, 3, "Benefits Continuation", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential — Separation Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Separation Agreement"), divLine(40));
  y = 48;

  const s4t = "The Employee agrees to return all Company property on or before the Separation Date, including but not limited to laptop computers, mobile devices, access badges, keys, credit cards, files, documents, and any materials containing Confidential Information. The Employee shall permanently delete all Company data from personal devices and shall certify in writing that all Company property has been returned and all Company data has been destroyed.";
  const [s4, s4e] = sec(y, 4, "Return of Company Property", s4t);
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s5t = "The Employee acknowledges that during their employment they had access to Confidential Information including trade secrets, customer lists, business strategies, financial data, and proprietary technology. The Employee agrees to maintain the strict confidentiality of all such information indefinitely following the Separation Date and shall not use or disclose such information for any purpose. The Employee further agrees that the terms and existence of this Agreement are confidential and shall not be disclosed to any person other than the Employee's spouse, tax advisor, or attorney.";
  const [s5, s5e] = sec(y, 5, "Confidentiality", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "Both parties agree not to make any disparaging, defamatory, or derogatory statements about the other party, whether orally, in writing, or through electronic media, including social media platforms. The Employer shall direct its officers and directors to refrain from making disparaging statements about the Employee. This provision does not restrict either party from providing truthful testimony if compelled by legal process or from communicating with government agencies.";
  const [s6, s6e] = sec(y, 6, "Non-Disparagement", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "In exchange for the severance benefits described herein, the Employee hereby releases and forever discharges the Employer, its parent companies, subsidiaries, affiliates, officers, directors, employees, agents, successors, and assigns from any and all claims, demands, causes of action, and liabilities of any kind, whether known or unknown, arising out of or related to the Employee's employment or separation, including but not limited to claims under Title VII, the ADA, the ADEA, ERISA, California FEHA, and any other federal, state, or local employment laws. This release does not waive rights or claims that arise after the date of execution of this Agreement.";
  const [s7, s7e] = sec(y, 7, "General Release of Claims", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "The Employee acknowledges that they have been advised in writing to consult with an attorney before signing this Agreement. Pursuant to the Age Discrimination in Employment Act (ADEA), the Employee is given twenty-one (21) calendar days from receipt of this Agreement to consider whether to sign it. The Employee may sign this Agreement before the expiration of the twenty-one-day period, but is not required to do so. Any modifications to this Agreement, whether material or immaterial, do not restart the twenty-one-day consideration period.";
  const [s8, s8e] = sec(y, 8, "Consideration Period", s8t);
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential — Separation Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Separation Agreement"), divLine(40));
  y = 48;

  const s9t = "The Employee may revoke this Agreement within seven (7) calendar days after signing it by delivering a written notice of revocation to the Employer's Human Resources Department. This Agreement shall not become effective or enforceable until the seven-day revocation period has expired without revocation (the \"Effective Date\"). If the Employee revokes this Agreement, it shall be null and void in its entirety, and the Employee shall not be entitled to any severance benefits described herein.";
  const [s9, s9e] = sec(y, 9, "Revocation Period", s9t);
  p3.push(...s9); y = s9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s10t = "This Agreement shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law principles. Any disputes arising under or in connection with this Agreement shall be resolved exclusively in the state or federal courts located in Santa Clara County, California. This Agreement constitutes the entire agreement between the parties regarding the subject matter hereof and supersedes all prior oral and written agreements, understandings, and representations.";
  const [s10, s10e] = sec(y, 10, "Governing Law & Entire Agreement", s10t);
  p3.push(...s10); y = s10e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Employer Representative", "separationAgreement_employer"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Employee", "separationAgreement_employee"));

  p3.push(footer(772, "Confidential — Separation Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

function buildNonCompete(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Non-Compete Agreement"));
  p1.push(subtitle(MX + 18, 96, "Restrictive Covenant & Non-Solicitation"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: April 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 180));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Non-Compete and Non-Solicitation Agreement (\"Agreement\") is entered into as of April 1, 2025, by and between the Employer and the Employee identified below. The Employee acknowledges that the restrictions contained herein are reasonable and necessary to protect the Employer's legitimate business interests, including its trade secrets, confidential information, and customer relationships.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "EMPLOYER"));
  p1.push(name(MX + 16, y + 28, "Vantage Partners LLC"));
  p1.push(addr(MX + 16, y + 46, "1400 Broadway, 30th Floor\nNew York, NY 10018"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "EMPLOYEE"));
  p1.push(name(MX + 286, y + 28, "Casey Williams"));
  p1.push(addr(MX + 286, y + 46, "225 Park Avenue South, Apt 14B\nNew York, NY 10003"));
  y += PH + GAP;

  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "For purposes of this Agreement: (a) \"Competing Business\" means any entity that engages in activities substantially similar to the Employer's current or planned business operations; (b) \"Restricted Territory\" means the geographic area defined in Section 3; (c) \"Restricted Period\" means the duration specified in Section 4; and (d) \"Confidential Information\" includes all non-public information relating to the Employer's business, technology, clients, finances, and strategic plans.";
  const [s1, s1e] = sec(y, 1, "Definitions", s1t);
  p1.push(...s1); y = s1e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "During the Restricted Period, the Employee shall not, directly or indirectly, own, manage, operate, join, control, finance, or participate in the ownership, management, operation, or control of any Competing Business within the Restricted Territory. This restriction applies whether the Employee acts as a principal, agent, partner, officer, director, employee, consultant, or in any other capacity. The Employee further agrees not to assist any person or entity in engaging in any activity that would violate this provision.";
  const [s2, s2e] = sec(y, 2, "Non-Compete Restrictions", s2t);
  p1.push(...s2); y = s2e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The restrictions set forth in this Agreement shall apply within a fifty (50) mile radius of any office or facility operated by the Employer at the time of the Employee's separation, as well as in any market or territory in which the Employer actively conducts business or has demonstrable plans for expansion at the time of separation. The Employee acknowledges that this geographic scope is reasonable given the nature of the Employer's business and the Employee's role.";
  const [s3, s3e] = sec(y, 3, "Geographic Scope", s3t);
  p1.push(...s3); y = s3e;

  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Restricted Period shall begin on the date the Employee's employment terminates, regardless of the reason for termination, and shall continue for a period of twelve (12) months thereafter. The Employee agrees that the duration of these restrictions is reasonable in light of the Employer's investment in the Employee's training, professional development, and access to confidential business information.";
  const [s4, s4e] = sec(y, 4, "Duration of Restrictions", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Non-Compete Agreement"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Non-Compete Agreement"), divLine(40));
  y = 48;

  const s5t = "During the Restricted Period, the Employee shall not, directly or indirectly: (a) solicit, contact, or attempt to divert any client, customer, or prospective customer of the Employer with whom the Employee had material contact during the last two (2) years of employment; or (b) recruit, solicit, or induce any employee, contractor, or consultant of the Employer to leave the Employer's service or to accept employment or engagement with any other person or entity.";
  const [s5, s5e] = sec(y, 5, "Non-Solicitation", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Employee agrees to hold all Confidential Information in strict confidence both during and after employment. The Employee shall not use, disclose, copy, or permit access to any Confidential Information except as required in the performance of their duties for the Employer. Upon termination of employment, the Employee shall immediately return or destroy all materials containing Confidential Information and provide written certification of compliance.";
  const [s6, s6e] = sec(y, 6, "Confidentiality Obligations", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "The Employee acknowledges that a breach of any provision of this Agreement would cause the Employer irreparable harm that cannot be adequately compensated by monetary damages alone. Accordingly, the Employer shall be entitled to seek injunctive and equitable relief, in addition to all other remedies available at law or in equity, without the necessity of posting a bond or proving actual damages.";
  const [s7, s7e] = sec(y, 7, "Remedies", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "If any provision of this Agreement is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect. The court may modify any overbroad provision to the minimum extent necessary to make it enforceable, and the parties agree to be bound by any such modification. The parties intend each restriction to be read independently of every other restriction.";
  const [s8, s8e] = sec(y, 8, "Severability", s8t);
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "This Agreement shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions. Any dispute arising under this Agreement shall be resolved exclusively in the state or federal courts located in New York County. This Agreement constitutes the entire understanding between the parties with respect to the subject matter hereof and supersedes all prior agreements, whether oral or written.";
  const [s9, s9e] = sec(y, 9, "Governing Law & Jurisdiction", s9t);
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Employer Representative", "nonCompete_employer"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Employee", "nonCompete_employee"));

  p2.push(footer(772, "Confidential — Non-Compete Agreement"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildShareholderAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Shareholder Agreement"));
  p1.push(subtitle(MX + 18, 96, "Corporate Governance & Equity Arrangement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: January 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const shaIntro = "This Shareholder Agreement (\"Agreement\") is entered into as of January 1, 2025, by and between the Company and the Shareholders identified below, for the purpose of establishing their respective rights, obligations, and governance of the Company.";
  p1.push(body(MX, y, shaIntro));
  y += bodyH(shaIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "TechVenture Holdings Inc."));
  p1.push(addr(MX + 16, y + 46, "2400 Corporate Center Drive\nWilmington, DE 19801"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "LEAD SHAREHOLDER"));
  p1.push(name(MX + 286, y + 28, "Lead Shareholder"));
  p1.push(addr(MX + 286, y + 46, "Representative of\nAll Participating Shareholders"));
  y += PH + GAP;

  // Section 1 — Definitions
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "For the purposes of this Agreement, \"Shares\" refers to all classes of issued and outstanding capital stock of the Company. \"Affiliate\" means any entity that directly or indirectly controls, is controlled by, or is under common control with a party. \"Transfer\" includes any sale, assignment, pledge, encumbrance, gift, or other disposition of Shares, whether voluntary or involuntary. \"Fair Market Value\" shall be determined by an independent appraiser mutually selected by the parties.";
  const [s1, s1e] = sec(y, 1, "Definitions", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Share Capital & Ownership
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The authorized share capital of the Company and the initial allocation of Shares among the Shareholders are set forth in Exhibit A attached hereto. Each Shareholder represents and warrants that it is the lawful and beneficial owner of the Shares attributed to it and that such Shares are free and clear of all liens, encumbrances, and third-party claims. Any issuance of additional Shares shall require the prior written approval of holders representing at least two-thirds of the outstanding voting Shares.";
  const [s2, s2e] = sec(y, 2, "Share Capital and Ownership", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Transfer Restrictions
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "No Shareholder shall Transfer any Shares except in compliance with this Agreement. Any proposed Transfer shall first be offered to the existing Shareholders on a pro-rata basis at Fair Market Value (\"Right of First Refusal\"). If a majority Shareholder receives a bona fide third-party offer, minority Shareholders shall have the right to participate in such sale on the same terms (\"Tag-Along Right\"). Conversely, if holders of seventy-five percent or more of the Shares approve a sale, remaining Shareholders shall be obligated to participate on identical terms (\"Drag-Along Right\").";
  const [s3, s3e] = sec(y, 3, "Transfer Restrictions", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Shareholder Agreement"), divLine(40));
  y = 48;

  // Section 4 — Voting Rights & Board Composition
  const [s4, s4e] = sec(y, 4, "Voting Rights and Board Composition",
    "Each Share shall carry one vote on all matters submitted to Shareholders. The Board of Directors shall consist of no fewer than three and no more than seven members. Shareholders holding at least twenty percent of the outstanding Shares shall be entitled to nominate one director. Board meetings require a quorum of a majority of directors then in office, and decisions shall be made by simple majority vote unless otherwise specified herein.");
  p2.push(...s4); y = s4e;

  // Section 5 — Dividend Policy
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s5, s5e] = sec(y, 5, "Dividend Policy",
    "Dividends shall be declared and paid at the discretion of the Board of Directors, subject to the Company maintaining adequate reserves for operations and compliance with applicable law. When declared, dividends shall be distributed pro rata to all Shareholders based on their respective ownership percentages. The Board may establish a minimum annual dividend distribution of not less than thirty percent of net profits after tax, provided the Company's cash reserves exceed six months of operating expenses.");
  p2.push(...s5); y = s5e;

  // Section 6 — Non-Compete & Confidentiality
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Non-Compete and Confidentiality",
    "During the term of this Agreement and for twenty-four months following the date on which a Shareholder ceases to hold any Shares, such Shareholder shall not directly or indirectly engage in, own, manage, or participate in any business that competes with the Company within the territories in which the Company operates. Each Shareholder shall maintain the strict confidentiality of all proprietary information, trade secrets, customer data, and financial records of the Company, and shall not disclose such information to any third party without prior written consent.");
  p2.push(...s6); y = s6e;

  // Section 7 — Anti-Dilution Protections
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Anti-Dilution Protections",
    "In the event the Company issues new Shares at a price per Share that is lower than the price paid by any existing Shareholder, such Shareholder shall be entitled to weighted-average anti-dilution protection. The Company shall provide each Shareholder with written notice at least thirty days prior to any proposed issuance of new Shares, together with the material terms of such issuance. Each Shareholder shall have a pre-emptive right to subscribe for its proportionate share of any new issuance on identical terms.");
  p2.push(...s7); y = s7e;

  // Section 8 — Deadlock Resolution
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Deadlock Resolution",
    "In the event the Board or Shareholders are unable to reach agreement on a material matter after two successive meetings, the parties shall engage in good-faith mediation with a mutually acceptable mediator. If mediation fails to resolve the deadlock within sixty days, either party may invoke binding arbitration under the rules of the American Arbitration Association. As a last resort, either party may trigger a buy-sell arrangement whereby one party offers to buy the other's Shares at a stated price, and the receiving party may elect to buy or sell at that same price.");
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Shareholder Agreement"), divLine(40));
  y = 48;

  // Section 9 — Governing Law
  const [s9, s9e] = sec(y, 9, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of laws principles. Any legal action or proceeding arising under this Agreement shall be brought exclusively in the state or federal courts located in Wilmington, Delaware, and each party irrevocably consents to the personal jurisdiction and venue of such courts.");
  p3.push(...s9); y = s9e;

  // Signature block
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Company Representative", "shareholder_company"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Lead Shareholder", "shareholder_lead"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  2. LLC OPERATING AGREEMENT
 * ==================================================================== */

function buildLLCOperatingAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "LLC Operating"));
  p1.push(title(MX + 18, 92, "Agreement"));
  p1.push(subtitle(MX + 18, 116, "Limited Liability Company Governance & Operations"));

  // Pills
  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Wyoming", 170));

  // Accent divider + intro
  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const llcIntro = "This Operating Agreement (\"Agreement\") of Atlas Ventures LLC (the \"Company\") is entered into as of March 1, 2025, by and among the Members identified below, to govern the operations, management, and affairs of the Company organized under the laws of the State of Wyoming.";
  p1.push(body(MX, y, llcIntro));
  y += bodyH(llcIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "MANAGING MEMBER"));
  p1.push(name(MX + 16, y + 28, "Atlas Ventures LLC"));
  p1.push(addr(MX + 16, y + 46, "1800 Summit Ridge Parkway\nCheyenne, WY 82001"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "MEMBER"));
  p1.push(name(MX + 286, y + 28, "Pinnacle Capital Group"));
  p1.push(addr(MX + 286, y + 46, "900 Financial District Plaza\nNew York, NY 10004"));
  y += PH + GAP;

  // Section 1 — Formation & Purpose
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Company was formed by filing Articles of Organization with the Wyoming Secretary of State. The purpose of the Company is to engage in any lawful business activity permitted under the Wyoming Limited Liability Company Act, including but not limited to investment management, holding of equity interests, and consulting services. The Company shall have perpetual existence unless dissolved in accordance with this Agreement or applicable law.";
  const [s1, s1e] = sec(y, 1, "Formation and Purpose", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Members & Capital Contributions
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The initial Members and their respective capital contributions are set forth in Schedule A attached hereto. No Member shall be required to make additional capital contributions without the unanimous written consent of all Members. Capital accounts shall be maintained for each Member in accordance with Treasury Regulation Section 1.704-1(b)(2)(iv). A Member's percentage interest shall be proportional to its capital contribution relative to the total capital of the Company.";
  const [s2, s2e] = sec(y, 2, "Members and Capital Contributions", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Allocation of Profits/Losses
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Net profits and net losses of the Company for each fiscal year shall be allocated among the Members in proportion to their respective percentage interests. Allocations shall be made in accordance with Section 704 of the Internal Revenue Code and the Treasury Regulations thereunder. The Company shall elect to be treated as a partnership for federal income tax purposes, and each Member shall report its distributive share of income, gains, losses, deductions, and credits on its own tax return.";
  const [s3, s3e] = sec(y, 3, "Allocation of Profits and Losses", s3t);
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("LLC Operating Agreement"), divLine(40));
  y = 48;

  // Section 4 — Management & Voting
  const [s4, s4e] = sec(y, 4, "Management and Voting",
    "The Company shall be managed by the Managing Member, who shall have the authority to conduct the day-to-day business and affairs of the Company. Major decisions, including the admission of new Members, the incurrence of indebtedness exceeding $100,000, the sale of all or substantially all Company assets, and any amendment to this Agreement, shall require the affirmative vote of Members holding at least sixty-six and two-thirds percent of the total membership interests. The Managing Member shall provide quarterly financial reports to all Members.");
  p2.push(...s4); y = s4e;

  // Section 5 — Distributions
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s5, s5e] = sec(y, 5, "Distributions",
    "Distributions shall be made at such times and in such amounts as the Managing Member determines, provided that distributions shall be made at least quarterly to the extent cash is available in excess of reasonably anticipated operating expenses, reserves, and capital expenditures. All distributions shall be made to the Members in proportion to their respective percentage interests. No distribution shall be made if it would render the Company unable to pay its debts as they become due in the ordinary course of business.");
  p2.push(...s5); y = s5e;

  // Section 6 — Transfer of Membership Interests
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Transfer of Membership Interests",
    "No Member may Transfer all or any portion of its membership interest without the prior written consent of all other Members, which consent may be withheld in each Member's sole discretion. Notwithstanding the foregoing, a Member may Transfer its interest to an Affiliate or a family trust without the consent of the other Members, provided the transferee agrees in writing to be bound by all terms of this Agreement. Any Transfer in violation of this Section shall be void and of no effect.");
  p2.push(...s6); y = s6e;

  // Section 7 — Dissolution & Winding Up
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Dissolution and Winding Up",
    "The Company shall dissolve upon: (a) the unanimous written consent of all Members; (b) the entry of a decree of judicial dissolution; or (c) the occurrence of any event that makes it unlawful for the business of the Company to continue. Upon dissolution, the Managing Member shall wind up the Company's affairs, liquidate its assets, pay all creditors, and distribute any remaining proceeds to the Members in accordance with their positive capital account balances.");
  p2.push(...s7); y = s7e;

  // Section 8 — Books & Records
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Books and Records",
    "The Managing Member shall maintain complete and accurate books and records of the Company's business and affairs at the Company's principal office. Each Member and its duly authorized representative shall have the right to examine and copy the Company's books, records, and tax returns during normal business hours upon reasonable notice. The Company's books shall be kept on the accrual basis of accounting in accordance with generally accepted accounting principles consistently applied.");
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("LLC Operating Agreement"), divLine(40));
  y = 48;

  // Section 9 — Amendments
  const [s9, s9e] = sec(y, 9, "Amendments",
    "This Agreement may be amended or modified only by a written instrument executed by Members holding at least sixty-six and two-thirds percent of the total membership interests. Any amendment that would adversely affect the rights or obligations of a particular Member in a manner disproportionate to other Members shall require the written consent of that affected Member. No oral modification shall be binding on any party.");
  p3.push(...s9); y = s9e;

  // Section 10 — Governing Law
  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [s10, s10e] = sec(y, 10, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the Wyoming Limited Liability Company Act and the laws of the State of Wyoming, without regard to conflict of laws principles. Any dispute arising under this Agreement shall be submitted to binding arbitration in Cheyenne, Wyoming, in accordance with the rules of the American Arbitration Association. The prevailing party shall be entitled to recover its reasonable attorneys' fees and costs.");
  p3.push(...s10); y = s10e;

  // Signature block
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Managing Member", "llcOperating_managing"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Member", "llcOperating_member"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  3. POWER OF ATTORNEY
 * ==================================================================== */

function buildPowerOfAttorney(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Power of Attorney"));
  p1.push(subtitle(MX + 18, 96, "Durable General Power of Attorney"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: February 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Virginia", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const poaIntro = "This Power of Attorney (\"POA\") is executed by the Principal identified below, who hereby appoints the Attorney-in-Fact to act on the Principal's behalf with respect to the matters described herein, effective as of February 1, 2025.";
  p1.push(body(MX, y, poaIntro));
  y += bodyH(poaIntro) + GAP;

  // Party cards — wider single card for Principal, standard for Attorney-in-Fact
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "PRINCIPAL"));
  p1.push(name(MX + 16, y + 28, "Eleanor M. Hartwell"));
  p1.push(addr(MX + 16, y + 46, "1540 Magnolia Lane\nRichmond, VA 23219"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "ATTORNEY-IN-FACT"));
  p1.push(name(MX + 286, y + 28, "Robert J. Hartwell"));
  p1.push(addr(MX + 286, y + 46, "1540 Magnolia Lane\nRichmond, VA 23219"));
  y += PH + GAP;

  // Section 1 — Grant of Authority
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Principal hereby grants to the Attorney-in-Fact full power and authority to act on the Principal's behalf in all matters described in this instrument. This Power of Attorney is durable and shall not be affected by the subsequent disability or incapacity of the Principal, in accordance with the Virginia Uniform Power of Attorney Act. The Attorney-in-Fact shall exercise the powers granted herein in the Principal's best interest, in good faith, and within the scope of authority conferred by this document.";
  const [s1, s1e] = sec(y, 1, "Grant of Authority", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Scope of Powers
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Attorney-in-Fact is authorized to act on behalf of the Principal with respect to the following: (a) Financial matters, including managing bank accounts, investments, tax filings, and retirement assets; (b) Real estate transactions, including the purchase, sale, lease, and management of real property; (c) Legal proceedings, including the authority to initiate, defend, or settle claims and litigation on the Principal's behalf; and (d) Healthcare decisions, to the extent not otherwise governed by a separate healthcare directive, including consulting with medical providers and accessing medical records.";
  const [s2, s2e] = sec(y, 2, "Scope of Powers", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Limitations
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Notwithstanding the broad authority granted herein, the Attorney-in-Fact shall not: (a) make gifts of the Principal's property exceeding $15,000 per recipient per calendar year; (b) change beneficiary designations on the Principal's life insurance policies, retirement accounts, or estate planning instruments; (c) create, amend, or revoke the Principal's will or trust; or (d) delegate the authority granted hereunder to any third party without the Principal's prior written consent.";
  const [s3, s3e] = sec(y, 3, "Limitations", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Effective Date & Duration
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "This Power of Attorney shall become effective immediately upon execution and shall remain in full force and effect until revoked in writing by the Principal, or upon the death of the Principal. If the Principal becomes incapacitated, this instrument shall continue in effect as a durable power of attorney. The Attorney-in-Fact's authority shall terminate automatically upon the appointment of a court-appointed guardian or conservator for the Principal, unless the court orders otherwise.";
  const [s4, s4e] = sec(y, 4, "Effective Date and Duration", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Power of Attorney"), divLine(40));
  y = 48;

  // Section 5 — Revocation
  const [s5, s5e] = sec(y, 5, "Revocation",
    "The Principal may revoke this Power of Attorney at any time by providing written notice to the Attorney-in-Fact. Revocation shall be effective upon actual receipt of written notice by the Attorney-in-Fact. The Principal should also notify any third parties who have been provided copies of this instrument. Until a third party receives actual notice of revocation, any action taken by the Attorney-in-Fact in good faith under this instrument shall be binding on the Principal.");
  p2.push(...s5); y = s5e;

  // Section 6 — Third-Party Reliance
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Third-Party Reliance",
    "Any third party who receives a copy of this Power of Attorney may rely upon the authority granted herein and upon the representations of the Attorney-in-Fact. No third party shall be required to investigate the facts underlying or the propriety of any action taken by the Attorney-in-Fact. The Principal hereby indemnifies and holds harmless any third party who acts in good faith reliance on this instrument from any claims arising from such reliance.");
  p2.push(...s6); y = s6e;

  // Section 7 — Compensation
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Compensation",
    "The Attorney-in-Fact shall serve without compensation unless otherwise agreed in writing by the Principal. The Attorney-in-Fact shall be entitled to reimbursement for all reasonable expenses incurred in the performance of duties under this instrument, including travel, legal fees, and accounting costs. The Attorney-in-Fact shall maintain accurate records of all expenses and provide an accounting to the Principal or the Principal's estate upon request.");
  p2.push(...s7); y = s7e;

  // Section 8 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Governing Law",
    "This Power of Attorney shall be governed by and construed in accordance with the laws of the Commonwealth of Virginia, including the Virginia Uniform Power of Attorney Act (Va. Code Ann. Section 64.2-1600 et seq.). Any dispute arising under this instrument shall be resolved in the Circuit Court for the City of Richmond, Virginia.");
  p2.push(...s8); y = s8e;

  // Signature block
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Principal", "poa_principal"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Attorney-in-Fact", "poa_attorney"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. PROMISSORY NOTE
 * ==================================================================== */

function buildPromissoryNote(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Promissory Note"));
  p1.push(subtitle(MX + 18, 96, "Secured Loan Agreement & Payment Obligation"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: April 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const pnIntro = "This Promissory Note (\"Note\") is executed as of April 15, 2025, by the Borrower in favor of the Lender identified below, evidencing an unconditional promise to pay the principal sum and accrued interest in accordance with the terms set forth herein.";
  p1.push(body(MX, y, pnIntro));
  y += bodyH(pnIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LENDER"));
  p1.push(name(MX + 16, y + 28, "Meridian Capital Partners LLC"));
  p1.push(addr(MX + 16, y + 46, "600 Park Avenue, Suite 3200\nNew York, NY 10065"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "BORROWER"));
  p1.push(name(MX + 286, y + 28, "Cascade Industries Inc."));
  p1.push(addr(MX + 286, y + 46, "4100 Industrial Parkway\nBuffalo, NY 14201"));
  y += PH + GAP;

  // Section 1 — Promise to Pay
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "For value received, the Borrower unconditionally promises to pay to the order of the Lender the principal sum of Two Hundred Fifty Thousand Dollars ($250,000.00), together with all accrued and unpaid interest thereon, in accordance with the terms and conditions of this Note. This obligation is absolute, unconditional, and irrevocable, and shall not be subject to any defense, setoff, counterclaim, or recoupment by the Borrower.";
  const [s1, s1e] = sec(y, 1, "Promise to Pay", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Interest Rate
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The outstanding principal balance of this Note shall bear interest at a fixed rate of five and one-quarter percent (5.25%) per annum, computed on the basis of a 360-day year of twelve 30-day months. Interest shall accrue daily from the date of disbursement and shall be payable in arrears as part of each monthly installment. In no event shall the interest rate charged hereunder exceed the maximum rate permitted by applicable law.";
  const [s2, s2e] = sec(y, 2, "Interest Rate", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Payment Schedule
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Borrower shall repay this Note in sixty (60) equal monthly installments of principal and interest, with each payment due on the fifteenth day of each calendar month, commencing on May 15, 2025 and continuing through April 15, 2030. Payments shall be applied first to accrued interest, then to outstanding principal. All payments shall be made in lawful currency of the United States by wire transfer or certified check to the account designated by the Lender.";
  const [s3, s3e] = sec(y, 3, "Payment Schedule", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Prepayment
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Borrower may prepay this Note in whole or in part at any time without penalty or premium. Any partial prepayment shall be applied first to accrued interest and then to principal, and shall not excuse the Borrower from making subsequent scheduled installments unless the entire outstanding balance has been paid in full. The Borrower shall provide the Lender with at least ten business days' prior written notice of any prepayment exceeding $25,000.";
  const [s4, s4e] = sec(y, 4, "Prepayment", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Promissory Note"), divLine(40));
  y = 48;

  // Section 5 — Late Payment & Default
  const [s5, s5e] = sec(y, 5, "Late Payment and Default",
    "Any installment not received within ten calendar days of its due date shall be subject to a late charge of five percent of the overdue amount. The occurrence of any of the following shall constitute an Event of Default: (a) failure to make any payment within thirty days of its due date; (b) breach of any material representation, warranty, or covenant; (c) the Borrower becoming insolvent or filing for bankruptcy; or (d) any material adverse change in the financial condition of the Borrower. Upon an Event of Default, the interest rate shall increase to the lesser of ten percent per annum or the maximum rate permitted by law.");
  p2.push(...s5); y = s5e;

  // Section 6 — Security/Collateral
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Security and Collateral",
    "As security for the repayment of this Note, the Borrower hereby grants to the Lender a first-priority security interest in all of the Borrower's accounts receivable, inventory, equipment, and general intangibles, whether now owned or hereafter acquired. The Borrower shall execute and deliver any financing statements, security agreements, and other documents reasonably requested by the Lender to perfect such security interest. The Borrower shall not create or permit any lien or encumbrance on the collateral without the Lender's prior written consent.");
  p2.push(...s6); y = s6e;

  // Section 7 — Acceleration Clause
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Acceleration Clause",
    "Upon the occurrence of any Event of Default, the Lender may, at its sole option and without prior notice, declare the entire unpaid principal balance, together with all accrued interest, late charges, and other amounts payable under this Note, immediately due and payable. The Lender's failure to exercise this right upon any default shall not constitute a waiver of its right to do so upon any subsequent default.");
  p2.push(...s7); y = s7e;

  // Section 8 — Waiver of Presentment
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Waiver of Presentment",
    "The Borrower and any endorsers, sureties, and guarantors hereby waive presentment for payment, demand, notice of dishonor, protest, and notice of protest with respect to this Note. No extension of time for payment, modification of terms, or acceptance of partial payment shall release the Borrower or any guarantor from liability under this Note.");
  p2.push(...s8); y = s8e;

  // Section 9 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Governing Law",
    "This Note shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of laws provisions. The Borrower consents to the exclusive jurisdiction of the state and federal courts located in the County of New York for any action arising under or relating to this Note.");
  p2.push(...s9); y = s9e;

  // Signature block
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Lender", "promissory_lender"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Borrower", "promissory_borrower"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildTutorAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Tutor Services Agreement"));
  p1.push(subtitle(MX + 18, 96, "Private Tutoring & Academic Support Engagement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Colorado", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const tIntro = "This Tutor Services Agreement (\"Agreement\") is entered into as of June 1, 2025, by and between the Tutoring Provider and the Client identified below, for the purpose of establishing terms governing private tutoring and academic support services.";
  p1.push(body(MX, y, tIntro));
  y += bodyH(tIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "TUTORING PROVIDER"));
  p1.push(name(MX + 16, y + 28, "Brightpath Learning Services"));
  p1.push(addr(MX + 16, y + 46, "510 Academy Lane\nBoulder, CO 80301"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT / FAMILY"));
  p1.push(name(MX + 286, y + 28, "The Johnson Family"));
  p1.push(addr(MX + 286, y + 46, "2240 Maple Ridge Drive\nDenver, CO 80220"));
  y += PH + GAP;

  // Section 1 – Scope of Tutoring Services
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Tutor shall provide individualized academic tutoring in the subjects of Algebra II, Biology, and SAT Preparation for the student currently enrolled at the 10th-grade level. Sessions will focus on homework support, concept reinforcement, test preparation strategies, and building long-term study skills aligned with the student's academic goals.";
  const [s1, s1e] = sec(y, 1, "Scope of Tutoring Services", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 – Schedule & Session Duration
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Tutoring sessions shall be held twice per week, each lasting sixty (60) minutes, at mutually agreed times. Sessions may be conducted in person at the Client's residence or via a secure video conferencing platform. The schedule may be adjusted by mutual written agreement, provided changes are communicated at least forty-eight (48) hours in advance.";
  const [s2, s2e] = sec(y, 2, "Schedule & Session Duration", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 – Compensation & Payment Terms
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Client agrees to compensate the Tutor at a rate of $75.00 per hour. Invoices shall be issued on a bi-weekly basis and are due within seven (7) days of receipt. Payment may be made by check, bank transfer, or electronic payment. A late fee of 1.5% per month shall apply to any overdue balance.";
  const [s3, s3e] = sec(y, 3, "Compensation & Payment Terms", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 – Cancellation & Rescheduling Policy
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Either party may cancel or reschedule a session with at least twenty-four (24) hours' notice without penalty. Cancellations made with less than twenty-four hours' notice shall be billed at the full session rate. The Tutor reserves the right to reschedule in the event of illness or emergency with prompt notice to the Client.";
  const [s4, s4e] = sec(y, 4, "Cancellation & Rescheduling Policy", s4t);
  p1.push(...s4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Tutor Services Agreement"), divLine(40));
  y = 48;

  // Section 5 – Student Progress & Communication
  const [s5, s5e] = sec(y, 5, "Student Progress & Communication",
    "The Tutor shall provide the Client with written progress reports on a monthly basis, summarizing the student's performance, areas of improvement, and recommended next steps. The Tutor shall maintain open communication with the Client and, where appropriate, coordinate with the student's school teachers to ensure alignment with classroom objectives.");
  p2.push(...s5); y = s5e;

  // Section 6 – Confidentiality & Student Records
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Confidentiality & Student Records",
    "The Tutor agrees to maintain the confidentiality of all student records, academic performance data, and personal information disclosed during the course of this engagement. The Tutor shall comply with all applicable provisions of the Family Educational Rights and Privacy Act (FERPA) and shall not disclose student information to any third party without the Client's prior written consent.");
  p2.push(...s6); y = s6e;

  // Section 7 – Liability & Indemnification
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Liability & Indemnification",
    "The Tutor shall not be liable for any failure of the student to achieve specific academic results. Each party agrees to indemnify and hold the other harmless from any claims, damages, or expenses arising from that party's negligence or breach of this Agreement. The Client acknowledges that tutoring is a supplemental service and does not guarantee academic outcomes.");
  p2.push(...s7); y = s7e;

  // Section 8 – Term & Termination
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Term & Termination",
    "This Agreement shall remain in effect for the duration of the current academic year unless terminated earlier by either party upon fourteen (14) days' written notice. Upon termination, the Client shall pay for all sessions completed through the date of termination. Either party may terminate immediately for cause if the other party materially breaches this Agreement.");
  p2.push(...s8); y = s8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Tutor / Service Provider", "tutor_provider"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Client / Parent", "tutor_client"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  2. SECURITY GUARD SERVICES AGREEMENT
 * ==================================================================== */

function buildSecurityServicesAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 76));
  p1.push(title(MX + 18, 68, "Security Guard Services"));
  p1.push(title(MX + 18, 92, "Agreement"));
  p1.push(subtitle(MX + 18, 116, "Commercial Security & Protective Services Engagement"));

  // Pills
  y = 142;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: July 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Texas", 180));

  // Accent divider + intro
  y = 176;
  p1.push(accentDiv(y)); y += 10;
  const secIntro = "This Security Guard Services Agreement (\"Agreement\") is entered into as of July 1, 2025, by and between the Security Provider and the Client identified below, for the provision of on-site security personnel, patrol, surveillance, and access control services at the Client's designated properties.";
  p1.push(body(MX, y, secIntro));
  y += bodyH(secIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "SECURITY PROVIDER"));
  p1.push(name(MX + 16, y + 28, "Sentinel Shield Security Inc."));
  p1.push(addr(MX + 16, y + 46, "7800 Guardian Boulevard\nHouston, TX 77002"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT"));
  p1.push(name(MX + 286, y + 28, "Westgate Commercial Properties LLC"));
  p1.push(addr(MX + 286, y + 46, "1500 Westgate Center Drive\nDallas, TX 75201"));
  y += PH + GAP;

  // Section 1 – Scope of Security Services
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Security Provider shall furnish both armed and unarmed security personnel to perform on-site patrol, access control, visitor management, and video surveillance monitoring at the Client's designated commercial properties. Services shall include perimeter checks, incident response, emergency coordination with local law enforcement, and preparation of detailed daily activity reports.";
  const [s1, s1e] = sec(y, 1, "Scope of Security Services", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 – Personnel Requirements
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "All security personnel assigned to the Client's premises shall have successfully completed comprehensive background checks, possess valid state security licenses, and maintain current certifications in first aid and CPR. Personnel shall wear clearly identifiable uniforms bearing the Security Provider's insignia at all times while on duty and shall conduct themselves in a professional manner consistent with industry standards.";
  const [s2, s2e] = sec(y, 2, "Personnel Requirements", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 – Service Hours & Scheduling
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Security Provider shall maintain twenty-four (24) hour, seven (7) day-per-week coverage at the Client's properties through a rotating shift schedule. Shift assignments shall include day shift (0600–1400), swing shift (1400–2200), and overnight shift (2200–0600). The Security Provider shall ensure adequate staffing levels and maintain qualified replacement personnel to cover absences or emergencies.";
  const [s3, s3e] = sec(y, 3, "Service Hours & Scheduling", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 – Compensation & Payment
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Client shall pay a monthly retainer of $18,500.00 for base coverage, plus hourly rates of $28.00 per hour for unarmed officers and $42.00 per hour for armed officers for any additional staffing requests. Invoices shall be submitted on the first business day of each month and are due within fifteen (15) days of receipt.";
  const [s4, s4e] = sec(y, 4, "Compensation & Payment", s4t);
  p1.push(...s4);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Security Guard Services Agreement"), divLine(40));
  y = 48;

  // Section 5 – Insurance & Bonding Requirements
  const [s5, s5e] = sec(y, 5, "Insurance & Bonding Requirements",
    "The Security Provider shall maintain, at its own expense, comprehensive general liability insurance with limits of not less than $2,000,000 per occurrence and $5,000,000 in the aggregate, workers' compensation insurance as required by law, and a fidelity bond covering all personnel assigned to the Client's premises. Certificates of insurance shall be provided to the Client prior to commencement of services and upon each renewal.");
  p2.push(...s5); y = s5e;

  // Section 6 – Use of Force Policy & Incident Reporting
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Use of Force Policy & Incident Reporting",
    "Security personnel shall employ the minimum force reasonably necessary to protect persons and property, consistent with applicable law and the Security Provider's written use-of-force policy. All incidents involving use of force, property damage, injuries, or law enforcement contact shall be documented in a written incident report submitted to the Client within twelve (12) hours of the occurrence.");
  p2.push(...s6); y = s6e;

  // Section 7 – Equipment & Technology
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Equipment & Technology",
    "The Security Provider shall furnish all equipment necessary for the performance of services, including two-way radios, flashlights, body cameras, and mobile patrol vehicles. The Client shall provide access to on-site surveillance systems and shall grant the Security Provider reasonable use of designated break and equipment storage areas.");
  p2.push(...s7); y = s7e;

  // Section 8 – Confidentiality
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Confidentiality",
    "The Security Provider and all assigned personnel shall maintain strict confidentiality regarding the Client's business operations, security systems, access codes, tenant information, and any proprietary data encountered during the performance of services. This obligation shall survive termination of this Agreement for a period of three (3) years.");
  p2.push(...s8); y = s8e;

  // Section 9 – Liability & Indemnification
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Liability & Indemnification",
    "Each party shall indemnify and hold the other harmless from and against all claims, losses, damages, and expenses, including reasonable attorneys' fees, arising from the indemnifying party's negligence, willful misconduct, or breach of this Agreement. The Security Provider's total aggregate liability shall not exceed the total fees paid during the twelve (12) months immediately preceding the claim.");
  p2.push(...s9); y = s9e;

  // Section 10 – Term & Termination
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s10, s10e] = sec(y, 10, "Term & Termination",
    "This Agreement shall commence on the Effective Date for an initial term of twenty-four (24) months and shall automatically renew for successive twelve-month periods unless either party provides sixty (60) days' written notice of non-renewal. Either party may terminate for cause upon thirty (30) days' written notice if the other party materially breaches and fails to cure within that period.");
  p2.push(...s10);

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Security Guard Services Agreement"), divLine(40));
  y = 58;
  p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Security Provider", "security_provider"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Client", "security_client"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  3. CONSULTING SERVICES AGREEMENT
 * ==================================================================== */

function buildConsultingAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Consulting Services Agreement"));
  p1.push(subtitle(MX + 18, 96, "Strategic Advisory & Operational Consulting Engagement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: August 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Illinois", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const cIntro = "This Consulting Services Agreement (\"Agreement\") is entered into as of August 1, 2025, by and between the Consultant and the Client identified below, for the provision of strategic advisory, operational assessment, and management consulting services.";
  p1.push(body(MX, y, cIntro));
  y += bodyH(cIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "CONSULTANT"));
  p1.push(name(MX + 16, y + 28, "Elevate Strategy Group LLC"));
  p1.push(addr(MX + 16, y + 46, "200 North Wacker Drive, Suite 1800\nChicago, IL 60606"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT"));
  p1.push(name(MX + 286, y + 28, "Nexgen Manufacturing Corp."));
  p1.push(addr(MX + 286, y + 46, "900 Industrial Parkway\nDetroit, MI 48201"));
  y += PH + GAP;

  // Section 1 – Scope of Consulting Services
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Consultant shall provide strategic advisory services including organizational assessment, process optimization, market analysis, and executive coaching. The specific scope of each engagement phase shall be detailed in a mutually agreed Statement of Work that references this Agreement. The Consultant shall allocate a dedicated engagement team with relevant industry expertise.";
  const [s1, s1e] = sec(y, 1, "Scope of Consulting Services", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 – Deliverables & Milestones
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Consultant shall deliver written reports, presentations, and recommendations at agreed milestones as set forth in the applicable Statement of Work. Each deliverable shall be subject to the Client's review and reasonable feedback within ten (10) business days of submission. Final deliverables shall incorporate the Client's feedback to the extent commercially reasonable.";
  const [s2, s2e] = sec(y, 2, "Deliverables & Milestones", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 – Fees & Expenses
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Client shall pay the Consultant a monthly retainer of $15,000.00 for ongoing advisory services, plus reimbursement for reasonable pre-approved expenses including travel, lodging, and materials. Expense reports shall be submitted monthly with supporting documentation. All fees are exclusive of applicable taxes, which are the Client's responsibility.";
  const [s3, s3e] = sec(y, 3, "Fees & Expenses", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 – Payment Terms
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Invoices shall be submitted on the first business day of each month for the prior month's services. Payment is due Net 30 from the date of invoice. Late payments shall accrue interest at the rate of 1.5% per month. The Client shall notify the Consultant in writing of any disputed charges within fifteen (15) days of receipt.";
  const [s4, s4e] = sec(y, 4, "Payment Terms", s4t);
  p1.push(...s4); y = s4e;

  // Section 5 – Intellectual Property & Work Product
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s5t = "All deliverables and work product created specifically for the Client shall become the Client's property upon full payment. The Consultant retains ownership of all pre-existing methodologies, frameworks, tools, and general knowledge. The Consultant is granted a limited license to reference anonymized engagement results for marketing and case study purposes.";
  const [s5, s5e] = sec(y, 5, "Intellectual Property & Work Product", s5t);
  p1.push(...s5);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Consulting Services Agreement"), divLine(40));
  y = 48;

  // Section 6 – Confidentiality
  const [s6, s6e] = sec(y, 6, "Confidentiality",
    "Each party shall maintain the confidentiality of all proprietary and confidential information disclosed by the other party during the term of this Agreement. Confidential information shall not be disclosed to third parties without prior written consent. This obligation shall survive termination for a period of three (3) years.");
  p2.push(...s6); y = s6e;

  // Section 7 – Non-Solicitation
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Non-Solicitation",
    "During the term and for twelve (12) months following termination, neither party shall directly or indirectly solicit, recruit, or hire any employee or contractor of the other party who was involved in the performance of services under this Agreement, without the prior written consent of the other party.");
  p2.push(...s7); y = s7e;

  // Section 8 – Representations & Warranties
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Representations & Warranties",
    "The Consultant represents and warrants that: (a) it has the authority and expertise to perform the services described herein; (b) services shall be performed in a professional manner consistent with industry standards; and (c) deliverables shall not infringe upon the intellectual property rights of any third party. No other warranties, express or implied, are made.");
  p2.push(...s8); y = s8e;

  // Section 9 – Limitation of Liability
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Limitation of Liability",
    "Neither party shall be liable for indirect, incidental, special, consequential, or punitive damages arising from this Agreement. The Consultant's total aggregate liability for all claims shall not exceed the total fees paid by the Client during the six (6) months immediately preceding the event giving rise to the claim.");
  p2.push(...s9); y = s9e;

  // Section 10 – Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s10, s10e] = sec(y, 10, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the laws of the State of Illinois. Any disputes arising under or relating to this Agreement shall be resolved through binding arbitration conducted in Cook County, Illinois, in accordance with the commercial arbitration rules of the American Arbitration Association.");
  p2.push(...s10); y = s10e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Consultant", "consulting_consultant"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Client", "consulting_client"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. FREELANCER / GIG WORKER AGREEMENT
 * ==================================================================== */

function buildFreelancerAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Freelancer Agreement"));
  p1.push(subtitle(MX + 18, 96, "Independent Design Services & Project Engagement"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: September 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y));
  y += 10;
  const fIntro = "This Freelancer Agreement (\"Agreement\") is entered into as of September 1, 2025, by and between the Freelancer and the Client identified below, governing the terms under which the Freelancer shall provide independent creative and design services for the specified project.";
  p1.push(body(MX, y, fIntro));
  y += bodyH(fIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "FREELANCER"));
  p1.push(name(MX + 16, y + 28, "Jordan Rivera"));
  p1.push(addr(MX + 16, y + 46, "Independent Designer\n84 Prospect Park West, Brooklyn, NY 11215"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT"));
  p1.push(name(MX + 286, y + 28, "Bloom Digital Agency"));
  p1.push(addr(MX + 286, y + 46, "350 Fifth Avenue, Suite 2400\nNew York, NY 10118"));
  y += PH + GAP;

  // Section 1 – Project Description & Scope
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Freelancer shall design and deliver a complete brand identity system for the Client's new product line, including a primary logo, secondary mark, color palette, typography guidelines, and brand usage documentation. The project scope is limited to the deliverables expressly listed in the attached creative brief; any additional work shall require a written change order signed by both parties.";
  const [s1, s1e] = sec(y, 1, "Project Description & Scope", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 – Timeline & Milestones
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The project shall be completed within six (6) weeks from the Effective Date. Key milestones include: concept presentation (Week 2), refined design direction (Week 4), and final deliverables package (Week 6). The Freelancer shall notify the Client promptly if any milestone is at risk of delay, and the parties shall negotiate a revised timeline in good faith.";
  const [s2, s2e] = sec(y, 2, "Timeline & Milestones", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 – Compensation
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The total project fee is $5,500.00, payable in two installments: fifty percent (50%) upon execution of this Agreement and the remaining fifty percent (50%) upon delivery and Client acceptance of the final deliverables. Payment shall be made via bank transfer or electronic payment within ten (10) days of each invoice date.";
  const [s3, s3e] = sec(y, 3, "Compensation", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 – Revision Policy
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The project fee includes two (2) rounds of revisions at each milestone stage. Additional revision rounds beyond those included shall be billed at the Freelancer's standard hourly rate of $125.00 per hour. Revision requests must be submitted in writing within five (5) business days of each deliverable presentation.";
  const [s4, s4e] = sec(y, 4, "Revision Policy", s4t);
  p1.push(...s4); y = s4e;

  // Section 5 – Intellectual Property Transfer
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s5t = "Upon receipt of full payment, the Freelancer hereby assigns to the Client all right, title, and interest in and to the final deliverables, including all copyrights and other intellectual property rights. The Freelancer retains the right to display the work in personal portfolios and marketing materials. Pre-existing materials and tools remain the Freelancer's property.";
  const [s5, s5e] = sec(y, 5, "Intellectual Property Transfer", s5t);
  p1.push(...s5);

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Freelancer Agreement"), divLine(40));
  y = 48;

  // Section 6 – Confidentiality
  const [s6, s6e] = sec(y, 6, "Confidentiality",
    "The Freelancer agrees to maintain the confidentiality of all proprietary information, business strategies, and unpublished creative concepts shared by the Client during the course of this engagement. The Freelancer shall not disclose such information to any third party without the Client's prior written consent. This obligation survives termination for two (2) years.");
  p2.push(...s6); y = s6e;

  // Section 7 – Independent Contractor Status
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Independent Contractor Status",
    "The Freelancer is an independent contractor and not an employee, agent, or partner of the Client. The Freelancer shall be solely responsible for all federal, state, and local taxes, self-employment contributions, and insurance. The Client shall not provide employee benefits, withhold taxes, or make contributions on the Freelancer's behalf.");
  p2.push(...s7); y = s7e;

  // Section 8 – Termination & Kill Fee
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Termination & Kill Fee",
    "Either party may terminate this Agreement upon seven (7) days' written notice. If the Client terminates without cause after work has commenced, the Freelancer shall be entitled to payment for all work completed plus a kill fee equal to twenty-five percent (25%) of the remaining unpaid project balance. If the Freelancer terminates, the Freelancer shall deliver all work completed to date.");
  p2.push(...s8); y = s8e;

  // Section 9 – Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the laws of the State of New York. Any disputes arising under this Agreement shall be resolved through mediation in New York County, and if mediation fails, through binding arbitration in accordance with the rules of the American Arbitration Association.");
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Freelancer", "freelancer_freelancer"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Client", "freelancer_client"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildShiftWorkerAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Shift Worker Employment Agreement"));
  p1.push(subtitle(MX + 18, 96, "Hourly Employment — Rotating Shift Schedule"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Illinois", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Shift Worker Employment Agreement (\"Agreement\") is entered into as of March 1, 2025, by and between the Employer and the Employee identified below. This Agreement sets forth the terms and conditions of the Employee's at-will, shift-based employment including work schedule, compensation, attendance policies, and workplace safety requirements.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "EMPLOYER"));
  p1.push(name(MX + 16, y + 28, "Metro Logistics & Warehousing Inc."));
  p1.push(addr(MX + 16, y + 46, "4500 Industrial Parkway\nChicago, IL 60632"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "EMPLOYEE"));
  p1.push(name(MX + 286, y + 28, "Alex Chen"));
  p1.push(addr(MX + 286, y + 46, "1122 West Archer Avenue\nChicago, IL 60609"));
  y += PH + GAP;

  // Section 1 — Position & Duties
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Employee is hired for the position of Warehouse Associate and shall perform duties including receiving, sorting, picking, packing, and shipping inventory in accordance with standard operating procedures. The Employee shall carry out all lawful directions given by supervisors and comply with all workplace policies. Additional duties may be assigned as reasonably required by operational needs.";
  const [s1, s1e] = sec(y, 1, "Position & Duties", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Work Schedule & Shifts
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Employee shall work on a rotating day/night shift schedule as determined by the Employer. Standard shifts are eight (8) hours in duration, exclusive of a thirty (30) minute unpaid meal break. Shift assignments will be posted at least one (1) week in advance. The Employer reserves the right to modify schedules based on operational demands, and the Employee may be required to work weekends and holidays as assigned.";
  const [s2, s2e] = sec(y, 2, "Work Schedule & Shifts", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Compensation & Overtime
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Employee shall be compensated at an hourly rate of $22.00 per hour, payable bi-weekly. All hours worked in excess of forty (40) hours in a single workweek shall be compensated at one and one-half times (1.5x) the regular hourly rate, in compliance with the Fair Labor Standards Act and applicable state law. Night shift differentials, if applicable, will be paid in accordance with the Employer's shift premium policy.";
  const [s3, s3e] = sec(y, 3, "Compensation & Overtime", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Benefits & PTO
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "After completion of a ninety (90) day probationary period, the Employee shall be eligible for the Employer's standard benefits package including medical and dental insurance, a 401(k) retirement plan with employer match, and paid time off accrued at a rate of one (1) hour per thirty (30) hours worked. Sick leave, holiday pay, and additional benefits are governed by the Employee Handbook.";
  const [s4, s4e] = sec(y, 4, "Benefits & PTO", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Shift Worker Employment Agreement"), divLine(40));
  y = 48;

  // Section 5 — Attendance & Punctuality Policy
  const s5t = "The Employee is expected to report for each assigned shift on time and ready to work. Absences must be reported to the direct supervisor at least two (2) hours before the start of the shift. Excessive absenteeism or tardiness, defined as three (3) or more unexcused occurrences in a rolling thirty (30) day period, may result in disciplinary action up to and including termination. No-call/no-show incidents are grounds for immediate termination.";
  const [s5, s5e] = sec(y, 5, "Attendance & Punctuality Policy", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — Safety & Workplace Rules
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Employee shall comply with all Occupational Safety and Health Administration (OSHA) regulations, facility safety rules, and standard operating procedures at all times. The Employee must attend all mandatory safety training sessions and promptly report any workplace injuries, hazardous conditions, or near-miss incidents to a supervisor. Failure to observe safety protocols may result in disciplinary action up to and including termination.";
  const [s6, s6e] = sec(y, 6, "Safety & Workplace Rules", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Uniform & Equipment
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "The Employer shall provide required uniforms, personal protective equipment (PPE), and tools necessary to perform assigned duties at no cost to the Employee. The Employee is responsible for maintaining all issued items in good condition and returning them upon separation of employment. Loss or willful damage to Employer-provided equipment may result in payroll deductions to the extent permitted by applicable law.";
  const [s7, s7e] = sec(y, 7, "Uniform & Equipment", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — Termination & At-Will Employment
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Employment under this Agreement is at-will. Either the Employer or the Employee may terminate the employment relationship at any time, with or without cause or notice. Upon termination, the Employee shall return all Employer property and receive final wages in accordance with applicable state law, including payment for all hours worked and any accrued, unused PTO as required by the Employer's policy.";
  const [s8, s8e] = sec(y, 8, "Termination & At-Will Employment", s8t);
  p2.push(...s8); y = s8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Employer Representative", "shiftWorker_employer"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Employee", "shiftWorker_employee"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  2. INTERNSHIP AGREEMENT
 * ==================================================================== */

function buildInternshipAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Internship Agreement"));
  p1.push(subtitle(MX + 18, 96, "Paid Internship — Software Engineering"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 2, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Michigan", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Internship Agreement (\"Agreement\") is entered into as of June 2, 2025, by and between the Company and the Intern identified below. This Agreement establishes the terms and conditions of a paid internship engagement, including scope of responsibilities, stipend, mentorship, and confidentiality obligations applicable during and after the internship period.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "Innovate Labs Inc."));
  p1.push(addr(MX + 16, y + 46, "200 North State Street, Suite 1200\nAnn Arbor, MI 48104"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "INTERN"));
  p1.push(name(MX + 286, y + 28, "Priya Sharma"));
  p1.push(addr(MX + 286, y + 46, "University of Michigan\nAnn Arbor, MI 48109"));
  y += PH + GAP;

  // Section 1 — Internship Position & Department
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Intern is engaged for the position of Software Engineering Intern within the Engineering Department. The Intern shall work on assigned projects under the guidance of a designated mentor, contributing to software development, code review, testing, and documentation activities as directed by the supervising team lead.";
  const [s1, s1e] = sec(y, 1, "Internship Position & Department", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Duration
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The internship shall commence on June 2, 2025, and conclude on August 22, 2025, for a total duration of twelve (12) weeks. The internship term may be extended by mutual written agreement of both parties. Early termination by either party requires at least two (2) weeks' written notice unless circumstances warrant immediate separation.";
  const [s2, s2e] = sec(y, 2, "Duration", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Stipend & Compensation
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Intern shall receive compensation at a rate of $25.00 per hour for up to forty (40) hours per week, payable bi-weekly in accordance with the Company's standard payroll schedule. The Intern is classified as a non-exempt, temporary worker and is entitled to overtime compensation as required by applicable federal and state wage laws. No benefits are provided unless otherwise specified in writing.";
  const [s3, s3e] = sec(y, 3, "Stipend & Compensation", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Supervisor & Mentorship
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Intern shall report to a designated supervisor who will provide project assignments, performance feedback, and day-to-day direction. A senior engineer shall serve as the Intern's mentor, meeting weekly to discuss professional development goals, technical challenges, and career guidance. The Company is committed to providing a meaningful learning experience throughout the internship.";
  const [s4, s4e] = sec(y, 4, "Supervisor & Mentorship", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Internship Agreement"), divLine(40));
  y = 48;

  // Section 5 — Learning Objectives & Evaluation
  const s5t = "The Company and the Intern shall establish mutually agreed-upon learning objectives at the start of the internship. A mid-term evaluation will be conducted at week six (6) and a final performance review at the conclusion of the internship. The Intern's performance will be assessed on technical competency, collaboration, initiative, and adherence to professional standards.";
  const [s5, s5e] = sec(y, 5, "Learning Objectives & Evaluation", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — Confidentiality & IP Assignment
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Intern agrees to maintain the confidentiality of all proprietary information, trade secrets, source code, and business strategies disclosed during the internship. All inventions, works of authorship, software, and intellectual property created by the Intern within the scope of the internship shall be the exclusive property of the Company. The Intern hereby assigns all right, title, and interest in such work product to the Company.";
  const [s6, s6e] = sec(y, 6, "Confidentiality & IP Assignment", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Academic Credit
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "If the Intern elects to receive academic credit for this internship, the Company will cooperate with the Intern's educational institution by providing required documentation, evaluations, or supervisor certifications. The Intern is solely responsible for meeting all university requirements and deadlines related to credit registration. Academic credit arrangements do not alter the compensation terms of this Agreement.";
  const [s7, s7e] = sec(y, 7, "Academic Credit", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — At-Will & Termination
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "This internship is at-will. Either the Company or the Intern may terminate the engagement at any time, with or without cause, upon providing reasonable written notice. Upon termination, the Intern shall return all Company property, equipment, access credentials, and confidential materials. The confidentiality and IP assignment provisions of this Agreement shall survive termination.";
  const [s8, s8e] = sec(y, 8, "At-Will & Termination", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 — Code of Conduct
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "The Intern shall comply with the Company's code of conduct, anti-harassment policies, information security protocols, and all applicable workplace regulations. The Intern is expected to maintain professional behavior, attend scheduled meetings and training sessions, and communicate proactively with their supervisor and mentor regarding any issues that may arise during the internship.";
  const [s9, s9e] = sec(y, 9, "Code of Conduct", s9t);
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Company Representative", "internship_company"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Intern", "internship_intern"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  3. COMMISSION-BASED SALES AGREEMENT
 * ==================================================================== */

function buildCommissionAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Commission-Based Sales Agreement"));
  p1.push(subtitle(MX + 18, 96, "Sales Compensation & Territory Assignment"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: April 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Texas", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Commission-Based Sales Agreement (\"Agreement\") is entered into as of April 15, 2025, by and between the Company and the Sales Representative identified below. This Agreement establishes the compensation structure, territory assignment, performance targets, and other terms governing the sales engagement between the parties.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "Apex Commercial Realty Group"));
  p1.push(addr(MX + 16, y + 46, "3200 Main Street, Suite 700\nDallas, TX 75201"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "SALES REPRESENTATIVE"));
  p1.push(name(MX + 286, y + 28, "Taylor Brooks"));
  p1.push(addr(MX + 286, y + 46, "814 Commerce Street, Apt 6C\nDallas, TX 75202"));
  y += PH + GAP;

  // Section 1 — Appointment & Territory
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Company hereby appoints the Sales Representative on an exclusive territory basis for the Dallas–Fort Worth metropolitan area. The Representative shall have primary responsibility for all named accounts assigned by the Company and any new prospects within the defined territory. The Company reserves the right to reassign territory boundaries upon sixty (60) days' written notice.";
  const [s1, s1e] = sec(y, 1, "Appointment & Territory", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Commission Structure
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Sales Representative shall receive a monthly base draw of $3,000.00 against commissions, plus a commission rate of eight percent (8%) on the gross value of all closed deals. The base draw is a non-recoverable advance that will be offset against earned commissions each quarter. If earned commissions exceed the draw in any quarter, the excess shall be paid to the Representative within thirty (30) days of quarter-end.";
  const [s2, s2e] = sec(y, 2, "Commission Structure", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Commission Calculation & Payment Schedule
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Commissions shall be calculated on the gross transaction value at the time of deal closing and shall become payable upon receipt of full payment from the client by the Company. Commission statements will be provided monthly, and disputed amounts must be raised in writing within fifteen (15) days. Payment of undisputed commissions shall be made on the fifteenth of each month following the close of the reporting period.";
  const [s3, s3e] = sec(y, 3, "Commission Calculation & Payment Schedule", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Quota & Performance Targets
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Sales Representative shall meet a minimum quarterly sales quota of $500,000.00 in closed deal volume. Failure to meet quota for two (2) consecutive quarters may result in territory reassignment, reduction of base draw, or termination of this Agreement at the Company's discretion. Accelerators of up to twelve percent (12%) commission may apply for performance exceeding 120% of quota.";
  const [s4, s4e] = sec(y, 4, "Quota & Performance Targets", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Commission-Based Sales Agreement"), divLine(40));
  y = 48;

  // Section 5 — Clawback & Chargeback Policy
  const s5t = "If a client cancels, defaults, or fails to complete payment within one hundred twenty (120) days of deal closing, the Company may claw back the commission paid on that transaction from future commission payments. Chargebacks shall be reflected on the next monthly commission statement. The Representative shall have the opportunity to cure the chargeback by securing replacement revenue from the same client within sixty (60) days.";
  const [s5, s5e] = sec(y, 5, "Clawback & Chargeback Policy", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — Expenses & Reimbursement
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Company shall reimburse the Sales Representative for reasonable and pre-approved business expenses incurred in connection with sales activities, including travel, client entertainment, and marketing materials. Expense reports must be submitted within thirty (30) days of the expenditure with supporting documentation. The Company reserves the right to set monthly expense caps and require prior approval for expenses exceeding $500.00.";
  const [s6, s6e] = sec(y, 6, "Expenses & Reimbursement", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Non-Compete During Employment
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "During the term of this Agreement, the Sales Representative shall not engage in, or represent, any competing commercial real estate brokerage or sales organization within the assigned territory without the prior written consent of the Company. The Representative shall devote their best professional efforts exclusively to promoting the Company's services and maintaining client relationships within the territory.";
  const [s7, s7e] = sec(y, 7, "Non-Compete During Employment", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — Termination & Earned Commissions
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Either party may terminate this Agreement upon thirty (30) days' written notice. Upon termination, the Sales Representative shall be entitled to commissions on deals that close within a ninety (90) day tail period, provided the Representative substantially originated and developed the opportunity prior to termination. Commissions on deals closing after the tail period shall not be payable. All Company property, client files, and CRM records must be returned within five (5) business days of termination.";
  const [s8, s8e] = sec(y, 8, "Termination & Earned Commissions", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "This Agreement shall be governed by and construed in accordance with the laws of the State of Texas, without regard to conflict of law principles. Any dispute arising under this Agreement shall be resolved through binding arbitration in Dallas County, Texas. This Agreement constitutes the entire understanding between the parties and supersedes all prior negotiations and agreements, whether written or oral.";
  const [s9, s9e] = sec(y, 9, "Governing Law", s9t);
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Company", "commission_company"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Sales Representative", "commission_rep"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. TEMPORARY STAFFING AGREEMENT
 * ==================================================================== */

function buildTempStaffingAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Temporary Staffing Agreement"));
  p1.push(subtitle(MX + 18, 96, "Staffing Services & Workforce Solutions"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: May 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Ohio", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Temporary Staffing Agreement (\"Agreement\") is entered into as of May 1, 2025, by and between the Staffing Agency and the Client Company identified below. This Agreement governs the placement of temporary workers, billing arrangements, conversion terms, and the respective responsibilities of each party with respect to staffing engagements.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "STAFFING AGENCY"));
  p1.push(name(MX + 16, y + 28, "ProStaff Solutions Inc."));
  p1.push(addr(MX + 16, y + 46, "750 Staffing Boulevard, Suite 300\nCleveland, OH 44113"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "CLIENT COMPANY"));
  p1.push(name(MX + 286, y + 28, "Greenfield Manufacturing LLC"));
  p1.push(addr(MX + 286, y + 46, "1800 Factory Drive\nAkron, OH 44308"));
  y += PH + GAP;

  // Section 1 — Staffing Services & Positions
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Staffing Agency agrees to recruit, screen, and assign qualified temporary workers to the Client Company for positions specified in individual work orders. Each work order shall identify the job title, required qualifications, anticipated duration, number of workers, and reporting location. The Agency shall use commercially reasonable efforts to fill positions promptly with appropriately skilled candidates.";
  const [s1, s1e] = sec(y, 1, "Staffing Services & Positions", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Assignment Terms & Duration
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Each temporary assignment shall be governed by a written work order signed by both parties specifying the start date, expected duration, and applicable terms. Assignments may be extended by mutual written agreement. Either party may end an individual assignment upon forty-eight (48) hours' written notice, provided that the Client Company shall pay for all hours worked through the effective date of termination.";
  const [s2, s2e] = sec(y, 2, "Assignment Terms & Duration", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Bill Rate & Payment Terms
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Client Company shall pay the Staffing Agency at a bill rate of $38.00 per hour for each temporary worker, which includes the worker's wages, payroll taxes, workers' compensation insurance, and the Agency's service fee. Invoices will be issued weekly based on approved timesheets, and payment is due Net 30 from the invoice date. Late payments shall accrue interest at a rate of 1.5% per month on the outstanding balance.";
  const [s3, s3e] = sec(y, 3, "Bill Rate & Payment Terms", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Temp-to-Hire Conversion Fee
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "If the Client Company wishes to hire a temporary worker as a direct employee, a conversion fee of twenty percent (20%) of the worker's projected first-year annual salary shall be due to the Staffing Agency. The conversion fee is waived if the worker has completed at least 720 billable hours on assignment with the Client Company. The fee is payable within thirty (30) days of the worker's direct hire start date.";
  const [s4, s4e] = sec(y, 4, "Temp-to-Hire Conversion Fee", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Temporary Staffing Agreement"), divLine(40));
  y = 48;

  // Section 5 — Worker's Compensation & Insurance
  const s5t = "The Staffing Agency shall maintain workers' compensation insurance covering all temporary workers during their assignments, as well as commercial general liability insurance with minimum coverage of $1,000,000 per occurrence and $2,000,000 aggregate. Certificates of insurance shall be provided to the Client Company upon request. The Agency is the employer of record for all temporary workers for purposes of payroll taxes and benefits.";
  const [s5, s5e] = sec(y, 5, "Worker's Compensation & Insurance", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — Supervision & Work Direction
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "While on assignment, temporary workers shall be under the day-to-day supervision and work direction of the Client Company. The Client Company is responsible for providing a safe work environment, necessary tools and equipment, and site-specific training. The Staffing Agency retains the right to reassign or remove any worker at its discretion and remains responsible for all employment-related decisions including hiring, discipline, and termination of the worker.";
  const [s6, s6e] = sec(y, 6, "Supervision & Work Direction", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Confidentiality
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "Both parties agree to maintain the confidentiality of all proprietary business information, pricing terms, worker compensation data, and trade secrets exchanged in connection with this Agreement. The Staffing Agency shall ensure that all temporary workers execute individual confidentiality agreements prior to the start of their assignments. Confidentiality obligations shall survive the termination of this Agreement for a period of two (2) years.";
  const [s7, s7e] = sec(y, 7, "Confidentiality", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — Non-Solicitation of Temporary Workers
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "During the term of this Agreement and for twelve (12) months following its termination, the Client Company shall not directly or indirectly solicit, recruit, or hire any temporary worker introduced by the Staffing Agency without paying the applicable conversion fee. This restriction applies regardless of whether the worker is actively on assignment at the time of solicitation.";
  const [s8, s8e] = sec(y, 8, "Non-Solicitation of Temporary Workers", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 — Liability & Indemnification
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "Each party shall indemnify and hold harmless the other party from any claims, damages, losses, or expenses arising from the indemnifying party's negligence, willful misconduct, or breach of this Agreement. The Client Company shall be solely responsible for workplace safety and any injuries arising from hazardous conditions at the worksite. The Staffing Agency shall be responsible for all employment law compliance related to the temporary workers it employs.";
  const [s9, s9e] = sec(y, 9, "Liability & Indemnification", s9t);
  p2.push(...s9); y = s9e;

  // Section 10 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s10t = "This Agreement shall be governed by and construed in accordance with the laws of the State of Ohio. Any disputes shall be resolved through mediation, and if mediation fails, through binding arbitration in Cuyahoga County, Ohio. This Agreement, together with any executed work orders, constitutes the entire agreement between the parties and supersedes all prior discussions, representations, and understandings.";
  const [s10, s10e] = sec(y, 10, "Governing Law", s10t);
  p2.push(...s10); y = s10e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Staffing Agency", "tempStaffing_agency"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Client Company", "tempStaffing_client"));

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildLiabilityWaiver(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Liability Waiver & Release"));
  p1.push(subtitle(MX + 18, 96, "Assumption of Risk & Release of Liability"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Colorado", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Liability Waiver and Release of Claims (\"Waiver\") is executed by the Participant identified below in favor of the Organization. By signing this Waiver, the Participant acknowledges and accepts the inherent risks associated with the activities described herein and voluntarily agrees to release the Organization from all liability.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Organization card (full-width)
  const PH = 80;
  p1.push(...fCard(MX, y, CW, PH));
  p1.push(label(MX + 16, y + 12, "ORGANIZATION"));
  p1.push(name(MX + 16, y + 28, "Summit Adventure Sports LLC"));
  p1.push(addr(MX + 16, y + 46, "4500 Mountain Pass Road, Boulder, CO 80302  \u00B7  info@summitadventuresports.com", CW - 32));
  y += PH + GAP;

  // Section 1 — Activity Description
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Participant intends to engage in one or more of the following activities offered by the Organization: rock climbing, rappelling, bouldering, zip-lining, and other guided outdoor adventure programs. These activities may take place at indoor facilities, outdoor recreation areas, and designated wilderness locations managed or utilized by the Organization. The Participant understands that the nature, duration, and difficulty of each activity may vary based on conditions and instructor discretion.";
  const [s1, s1e] = sec(y, 1, "Activity Description", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Assumption of Risk
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Participant acknowledges that the activities described above involve inherent risks including, but not limited to, physical injury, falls, equipment failure, adverse weather conditions, wildlife encounters, and other hazards that may result in serious bodily harm or death. The Participant voluntarily assumes all risks, both known and unknown, associated with participation in these activities. The Participant certifies that they are physically fit and have no medical conditions that would prevent safe participation.";
  const [s2, s2e] = sec(y, 2, "Assumption of Risk", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Release & Waiver of Liability
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "In consideration of being permitted to participate, the Participant hereby releases, waives, discharges, and covenants not to sue the Organization, its owners, officers, employees, agents, instructors, and affiliates from any and all liability, claims, demands, and causes of action arising out of or related to any injury, illness, or property damage sustained during participation. This release applies regardless of whether such injury is caused by negligence of the released parties or otherwise.";
  const [s3, s3e] = sec(y, 3, "Release & Waiver of Liability", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Indemnification
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Participant agrees to indemnify, defend, and hold harmless the Organization and its officers, employees, and agents from any and all claims, losses, damages, costs, and expenses, including reasonable attorney's fees, arising from the Participant's actions, omissions, or breach of this Waiver. This indemnification obligation shall survive the termination or expiration of this Waiver.";
  const [s4, s4e] = sec(y, 4, "Indemnification", s4t);
  p1.push(...s4); y = s4e;

  // Section 5 — Medical Authorization
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s5t = "In the event of an emergency, the Participant authorizes the Organization and its personnel to secure emergency medical treatment, transportation, and hospitalization on the Participant's behalf if the Participant is unable to provide consent. The Participant agrees to bear all costs associated with such emergency medical care and releases the Organization from any liability arising from the provision of or failure to provide such treatment.";
  const [s5, s5e] = sec(y, 5, "Medical Authorization", s5t);
  p1.push(...s5); y = s5e;

  // Section 6 — Severability
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s6t = "If any provision of this Waiver is found to be unenforceable or invalid by a court of competent jurisdiction, the remaining provisions shall remain in full force and effect. The unenforceable provision shall be modified to the minimum extent necessary to make it enforceable while preserving the original intent of the parties.";
  const [s6, s6e] = sec(y, 6, "Severability", s6t);
  p1.push(...s6); y = s6e;

  // Signature
  y += DIV_PRE; p1.push(accentDiv(y)); y += 10;
  p1.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p1.push(...sigBlockWithRole(MX, y, "Participant", "liabilityWaiver_participant"));

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 1"));

  return [page(p1)];
}

/* ====================================================================
 *  2. CEASE AND DESIST LETTER
 * ==================================================================== */

function buildCeaseAndDesist(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Cease and Desist Letter"));
  p1.push(subtitle(MX + 18, 96, "Formal Demand to Stop Infringing Activity"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Date: May 12, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Via: Certified Mail", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This letter constitutes a formal cease and desist demand issued on behalf of the Rights Holder identified below. The purpose of this correspondence is to notify the Recipient of specific infringing activities and to demand that all such activities cease immediately. Failure to comply may result in the initiation of legal proceedings without further notice.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "RIGHTS HOLDER"));
  p1.push(name(MX + 16, y + 28, "Meridian Brands International Inc."));
  p1.push(addr(MX + 16, y + 46, "900 Commerce Plaza, 14th Floor\nNew York, NY 10036"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "RECIPIENT"));
  p1.push(field({ x: MX + 286, y: y + 30, type: "text", label: "Recipient Name", width: 238, height: 26, fill: "#f8fafc", stroke: "#e2e8f0", placeholder: "Enter recipient name" }));
  p1.push(field({ x: MX + 286, y: y + 62, type: "text", label: "Recipient Address", width: 238, height: 26, fill: "#f8fafc", stroke: "#e2e8f0", placeholder: "Enter recipient address" }));
  y += PH + GAP;

  // Section 1 — Notice of Infringement
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "Our client, Meridian Brands International Inc., is the exclusive owner of certain trademarks, trade names, and intellectual property rights registered with the United States Patent and Trademark Office. These marks have been in continuous commercial use for over fifteen years and have acquired significant goodwill and consumer recognition in the marketplace. It has come to our attention that your organization is engaging in unauthorized use of our client's protected intellectual property.";
  const [s1, s1e] = sec(y, 1, "Notice of Infringement", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Description of Infringing Activity
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Our investigation reveals that you are using marks, logos, and trade dress that are confusingly similar to our client's registered trademarks in connection with the marketing, promotion, and sale of competing goods and services. Specifically, your use of substantially identical branding elements on your website, product packaging, and advertising materials creates a likelihood of consumer confusion and constitutes trademark infringement under the Lanham Act, 15 U.S.C. \u00A7 1114.";
  const [s2, s2e] = sec(y, 2, "Description of Infringing Activity", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Demand to Cease
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "We hereby demand that you immediately and permanently cease and desist from all use of our client's trademarks, trade names, logos, and any confusingly similar variations thereof. You must discontinue all manufacturing, distribution, marketing, advertising, and sale of any products or services bearing the infringing marks. This demand extends to all physical and digital channels, including but not limited to websites, social media accounts, printed materials, and product packaging.";
  const [s3, s3e] = sec(y, 3, "Demand to Cease", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Required Actions & Deadline
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Within fifteen (15) business days of receipt of this letter, you must: (a) remove all infringing materials from commerce and all digital platforms; (b) destroy or surrender all inventory bearing the infringing marks; (c) provide a written accounting of all revenues generated from the sale of infringing goods or services; and (d) deliver a signed written assurance that you will not resume the infringing activities. Your timely compliance with these demands is essential.";
  const [s4, s4e] = sec(y, 4, "Required Actions & Deadline", s4t);
  p1.push(...s4); y = s4e;

  // Section 5 — Legal Consequences
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s5t = "Should you fail to comply with the demands set forth herein within the specified timeframe, our client is prepared to pursue all available legal remedies, including the filing of a federal lawsuit seeking injunctive relief, actual and statutory damages, disgorgement of profits, treble damages for willful infringement, and recovery of attorney's fees and litigation costs. Our client reserves the right to seek temporary and permanent injunctive relief without further notice.";
  const [s5, s5e] = sec(y, 5, "Legal Consequences", s5t);
  p1.push(...s5); y = s5e;

  // Section 6 — Reservation of Rights
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s6t = "Nothing contained in this letter shall be construed as a waiver of any rights or remedies available to our client, all of which are expressly reserved. This letter does not constitute an exhaustive statement of our client's rights or of the facts and legal claims relating to this matter. Our client reserves the right to pursue additional claims and seek additional remedies as the facts and circumstances may warrant.";
  const [s6, s6e] = sec(y, 6, "Reservation of Rights", s6t);
  p1.push(...s6); y = s6e;

  // Signature
  y += DIV_PRE; p1.push(accentDiv(y)); y += 10;
  p1.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p1.push(...sigBlockWithRole(MX, y, "Authorized Representative", "ceaseDesist_sender"));

  p1.push(footer(772, "Confidential — Privileged & Confidential"), footerR(772, "Page 1 of 1"));

  return [page(p1)];
}

/* ====================================================================
 *  3. SETTLEMENT AGREEMENT & MUTUAL RELEASE
 * ==================================================================== */

function buildSettlementAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Settlement Agreement"));
  p1.push(subtitle(MX + 18, 96, "Mutual Release & Confidential Resolution of Dispute"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: August 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Settlement Agreement and Mutual Release (\"Agreement\") is entered into as of August 15, 2025, by and between the parties identified below for the purpose of resolving all disputes, claims, and causes of action arising from or related to the matter referenced herein. The parties desire to settle this matter amicably and without any admission of liability.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "FIRST PARTY"));
  p1.push(name(MX + 16, y + 28, "Redwood Enterprises Inc."));
  p1.push(addr(MX + 16, y + 46, "3200 Financial District Drive\nSan Francisco, CA 94111"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "SECOND PARTY"));
  p1.push(name(MX + 286, y + 28, "Pacific Coast Trading LLC"));
  p1.push(addr(MX + 286, y + 46, "1750 Harbor Boulevard, Suite 600\nLong Beach, CA 90802"));
  y += PH + GAP;

  // Section 1 — Recitals & Background
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The parties are involved in a pending dispute arising from commercial dealings, currently docketed as Case No. 2025-CV-04821 in the Superior Court of California, County of Los Angeles. First Party filed claims alleging breach of contract and related damages, and Second Party has denied liability and asserted counterclaims. The parties now wish to resolve all claims and counterclaims without further litigation and the associated expense, delay, and uncertainty of continued proceedings.";
  const [s1, s1e] = sec(y, 1, "Recitals & Background", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Settlement Amount
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "In consideration of the mutual covenants and releases contained herein, Second Party agrees to pay First Party a total settlement amount of One Hundred Eighty-Five Thousand Dollars ($185,000.00). Payment shall be made in two installments: (a) $100,000.00 within thirty (30) days of the Effective Date; and (b) $85,000.00 within ninety (90) days of the Effective Date. All payments shall be made by wire transfer to the account designated by First Party in writing.";
  const [s2, s2e] = sec(y, 2, "Settlement Amount", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Mutual Release of Claims
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Upon execution of this Agreement and receipt of the full settlement amount, each party hereby releases and forever discharges the other party, together with its officers, directors, employees, agents, affiliates, successors, and assigns, from any and all claims, demands, damages, actions, and causes of action, whether known or unknown, that arise from or relate to the subject matter of the pending dispute. This release is intended to be comprehensive and shall be broadly construed.";
  const [s3, s3e] = sec(y, 3, "Mutual Release of Claims", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Confidentiality
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The terms and conditions of this Agreement, including the settlement amount, are strictly confidential. Neither party shall disclose any terms of this Agreement to any third party, except as required by law, regulation, or court order, or to each party's legal counsel, accountants, and tax advisors who are bound by professional obligations of confidentiality. Any breach of this confidentiality provision shall constitute a material breach of this Agreement.";
  const [s4, s4e] = sec(y, 4, "Confidentiality", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Settlement Agreement"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Settlement Agreement"), divLine(40));
  y = 48;

  // Section 5 — Non-Disparagement
  const s5t = "Each party agrees that it shall not make, publish, or cause to be made any disparaging, defamatory, or derogatory statements, whether oral or written, about the other party, its officers, directors, employees, products, or services. This non-disparagement obligation extends to communications made through social media, press releases, interviews, public forums, and all other channels of communication. The parties agree that this provision is a material inducement to enter into this Agreement.";
  const [s5, s5e] = sec(y, 5, "Non-Disparagement", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — No Admission of Liability
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "This Agreement shall not be construed as an admission of liability, fault, or wrongdoing by either party. The parties expressly acknowledge that they are entering into this Agreement solely to avoid the expense, burden, and uncertainty of continued litigation. Neither this Agreement nor any of its terms may be offered as evidence of liability in any legal proceeding, except to enforce the terms of this Agreement itself.";
  const [s6, s6e] = sec(y, 6, "No Admission of Liability", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Representations & Warranties
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "Each party represents and warrants that: (a) it has the full legal authority to enter into and perform its obligations under this Agreement; (b) the person signing on its behalf is duly authorized to do so; (c) it has not assigned, transferred, or conveyed any of the claims released herein to any third party; and (d) it has had the opportunity to consult with legal counsel of its own choosing before executing this Agreement.";
  const [s7, s7e] = sec(y, 7, "Representations & Warranties", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — Entire Agreement
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior and contemporaneous negotiations, representations, understandings, and agreements. No modification or amendment of this Agreement shall be effective unless made in writing and signed by both parties. The failure of either party to enforce any provision shall not constitute a waiver of that provision.";
  const [s8, s8e] = sec(y, 8, "Entire Agreement", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "This Agreement shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. Any dispute arising under or in connection with this Agreement shall be resolved exclusively in the state or federal courts located in Los Angeles County, California. The prevailing party in any such action shall be entitled to recover its reasonable attorney's fees and costs.";
  const [s9, s9e] = sec(y, 9, "Governing Law", s9t);
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "First Party", "settlement_partyA"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Second Party", "settlement_partyB"));

  p2.push(footer(772, "Confidential — Settlement Agreement"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. INDEMNIFICATION AGREEMENT
 * ==================================================================== */

function buildIndemnificationAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Indemnification Agreement"));
  p1.push(subtitle(MX + 18, 96, "Mutual Indemnification & Defense Obligations"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: July 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Indemnification Agreement (\"Agreement\") is entered into as of July 1, 2025, by and between the Indemnitor and the Indemnitee identified below. This Agreement sets forth the terms under which the Indemnitor agrees to indemnify, defend, and hold harmless the Indemnitee against certain claims, losses, and liabilities arising from the parties' business relationship.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "INDEMNITOR"));
  p1.push(name(MX + 16, y + 28, "Cobalt Solutions Group Inc."));
  p1.push(addr(MX + 16, y + 46, "1600 Corporate Center Drive\nWhite Plains, NY 10601"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "INDEMNITEE"));
  p1.push(name(MX + 286, y + 28, "Sterling Technologies LLC"));
  p1.push(addr(MX + 286, y + 46, "250 Park Avenue, 20th Floor\nNew York, NY 10177"));
  y += PH + GAP;

  // Section 1 — Definitions
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "For purposes of this Agreement: (a) \"Claims\" means any and all demands, actions, suits, proceedings, judgments, orders, and awards brought by any person or entity; (b) \"Losses\" means all damages, liabilities, costs, expenses, penalties, fines, and reasonable attorney's fees and disbursements; and (c) \"Third-Party Claims\" means any Claims asserted against the Indemnitee by any person or entity that is not a party to this Agreement. These definitions shall be construed broadly to effectuate the intent of the parties.";
  const [s1, s1e] = sec(y, 1, "Definitions", s1t);
  p1.push(...s1); y = s1e;

  // Section 2 — Scope of Indemnification
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Indemnitor shall indemnify, defend, and hold harmless the Indemnitee and its officers, directors, employees, agents, successors, and assigns from and against any and all Claims and Losses arising out of, relating to, or resulting from: (a) any breach of this Agreement or any related agreement between the parties; (b) any negligent or wrongful act or omission of the Indemnitor; (c) any violation of applicable law by the Indemnitor; and (d) any Third-Party Claims arising from the Indemnitor's performance or failure to perform its obligations.";
  const [s2, s2e] = sec(y, 2, "Scope of Indemnification", s2t);
  p1.push(...s2); y = s2e;

  // Section 3 — Defense & Control of Claims
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Upon receipt of notice of a Claim, the Indemnitor shall assume the defense thereof at its sole cost and expense, using counsel reasonably acceptable to the Indemnitee. The Indemnitee shall have the right to participate in the defense at its own expense and with counsel of its choosing. The Indemnitor shall not settle any Claim without the prior written consent of the Indemnitee if such settlement would impose any obligation on the Indemnitee or would not include a complete release of the Indemnitee from all liability.";
  const [s3, s3e] = sec(y, 3, "Defense & Control of Claims", s3t);
  p1.push(...s3); y = s3e;

  // Section 4 — Notice Requirements
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Indemnitee shall provide the Indemnitor with prompt written notice of any Claim for which indemnification is sought, including a description of the Claim and copies of all relevant documents and correspondence. Failure to provide timely notice shall not relieve the Indemnitor of its indemnification obligations except to the extent that the Indemnitor demonstrates actual prejudice resulting from such delay. All notices shall be delivered in accordance with the notice provisions of this Agreement.";
  const [s4, s4e] = sec(y, 4, "Notice Requirements", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Indemnification Agreement"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Indemnification Agreement"), divLine(40));
  y = 48;

  // Section 5 — Limitations & Exclusions
  const s5t = "The Indemnitor's obligations under this Agreement shall not apply to Claims or Losses to the extent they arise from: (a) the gross negligence or willful misconduct of the Indemnitee; (b) a material breach of this Agreement by the Indemnitee; or (c) any modification to the Indemnitor's products or services made by the Indemnitee without the Indemnitor's prior written approval. In no event shall the Indemnitor's aggregate liability under this Agreement exceed the total fees paid or payable by the Indemnitee to the Indemnitor under the related service agreement during the twelve-month period preceding the Claim.";
  const [s5, s5e] = sec(y, 5, "Limitations & Exclusions", s5t);
  p2.push(...s5); y = s5e;

  // Section 6 — Insurance Requirements
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Indemnitor shall maintain, at its own expense, comprehensive general liability insurance, professional errors and omissions insurance, and such other insurance coverage as is commercially reasonable for the Indemnitor's business activities. All insurance policies shall have coverage limits of no less than Two Million Dollars ($2,000,000) per occurrence and Five Million Dollars ($5,000,000) in the aggregate. The Indemnitor shall name the Indemnitee as an additional insured and provide certificates of insurance upon request.";
  const [s6, s6e] = sec(y, 6, "Insurance Requirements", s6t);
  p2.push(...s6); y = s6e;

  // Section 7 — Subrogation Rights
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "Upon payment of any Claim or Loss under this Agreement, the Indemnitor shall be subrogated to the extent of such payment to all rights, claims, and remedies that the Indemnitee may have against any third party with respect to such Claim or Loss. The Indemnitee shall cooperate with the Indemnitor in the exercise of such subrogation rights, including executing any documents and taking any actions reasonably necessary to enable the Indemnitor to pursue recovery against such third parties.";
  const [s7, s7e] = sec(y, 7, "Subrogation Rights", s7t);
  p2.push(...s7); y = s7e;

  // Section 8 — Term & Survival
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "This Agreement shall commence on the Effective Date and shall continue in full force and effect for the duration of the parties' business relationship. The indemnification obligations contained herein shall survive the termination or expiration of this Agreement for a period of three (3) years following the date of termination. Any Claims arising from events that occur during the term of this Agreement shall be subject to indemnification regardless of when such Claims are formally asserted.";
  const [s8, s8e] = sec(y, 8, "Term & Survival", s8t);
  p2.push(...s8); y = s8e;

  // Section 9 — Governing Law
  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "This Agreement shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law principles. Any dispute arising under this Agreement shall be resolved exclusively in the state or federal courts located in New York County. This Agreement constitutes the entire understanding between the parties regarding indemnification and supersedes all prior agreements on this subject matter.";
  const [s9, s9e] = sec(y, 9, "Governing Law", s9t);
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Indemnitor", "indemnification_indemnitor"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Indemnitee", "indemnification_indemnitee"));

  p2.push(footer(772, "Confidential — Indemnification Agreement"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

function buildPartnershipAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "General Partnership Agreement"));
  p1.push(subtitle(MX + 18, 96, "Formation & Governance of Partnership"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: New York", 180));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This General Partnership Agreement (\"Agreement\") is entered into as of June 1, 2025, by and between the undersigned partners for the purpose of establishing and governing a general partnership under the laws of the State of New York. The partners intend to conduct business together and share in the profits, losses, and management responsibilities as set forth herein.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "PARTNER A"));
  p1.push(name(MX + 16, y + 28, "Hartfield & Associates"));
  p1.push(addr(MX + 16, y + 46, "900 Lexington Avenue, Suite 1200\nNew York, NY 10065"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "PARTNER B"));
  p1.push(name(MX + 286, y + 28, "Westbrook Consulting Group"));
  p1.push(addr(MX + 286, y + 46, "1455 Pennsylvania Avenue NW\nWashington, DC 20004"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The partnership shall be known as Hartfield-Westbrook Partners and shall conduct business under that name or such other name as the partners may agree upon in writing. The purpose of the partnership is to provide strategic management consulting and advisory services to corporate clients. The partnership may engage in any lawful activity incidental to this purpose as unanimously agreed by the partners.";
  const [s1, s1e] = sec(y, 1, "Partnership Name & Purpose", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Each partner shall contribute an initial capital amount of One Hundred Fifty Thousand Dollars ($150,000) to the partnership within thirty (30) days of the Effective Date. Additional capital contributions may be required upon unanimous written consent of all partners. Capital contributions shall be recorded in each partner's individual capital account and shall accrue no interest unless otherwise agreed upon in writing.";
  const [s2, s2e] = sec(y, 2, "Capital Contributions", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "Net profits and net losses of the partnership shall be allocated equally between the partners on a fifty-fifty (50/50) basis. Distributions of available cash shall be made quarterly unless the partners determine that funds are needed for operations or reserves. Each partner's share of profits shall be credited to their capital account, and losses shall be charged against it accordingly.";
  const [s3, s3e] = sec(y, 3, "Profit & Loss Sharing", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Both partners shall have equal rights in the management and conduct of partnership business. Major decisions — including entering contracts exceeding $25,000, hiring or terminating employees, incurring indebtedness, and altering the scope of business — require the unanimous written consent of both partners. Day-to-day operational decisions may be made independently by either partner.";
  const [s4, s4e] = sec(y, 4, "Management & Decision-Making", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Partnership Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("General Partnership Agreement"), divLine(40));
  y = 48;

  const s5t = "Neither partner shall, without the prior written consent of the other partner, borrow money on behalf of the partnership, assign or transfer partnership property, execute any mortgage or lease, or enter into any agreement that would bind the partnership to obligations exceeding $10,000. Any act taken in violation of this provision shall not bind the partnership and the acting partner shall indemnify the partnership against all resulting liabilities.";
  const [s5, s5e] = sec(y, 5, "Authority & Binding the Partnership", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The partnership shall maintain a dedicated business bank account at a mutually agreed financial institution. All partnership funds shall be deposited into this account and all disbursements shall be made therefrom. The partnership shall maintain accurate books of account on an accrual basis in accordance with generally accepted accounting principles. An independent certified public accountant shall prepare annual financial statements and tax returns for the partnership.";
  const [s6, s6e] = sec(y, 6, "Banking & Accounting", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "No additional partners may be admitted to the partnership without the unanimous written consent of all existing partners. Any new partner must agree in writing to be bound by the terms of this Agreement. The admission of a new partner shall not dissolve the existing partnership but shall constitute a continuation thereof with amended profit-sharing ratios as agreed by all partners.";
  const [s7, s7e] = sec(y, 7, "Additional Partners", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Any partner may voluntarily withdraw from the partnership upon ninety (90) days' prior written notice to the other partners. Upon withdrawal, the departing partner's interest shall be valued based on the fair market value of the partnership's net assets as determined by an independent appraiser. The remaining partners shall have the right to purchase the departing partner's interest over a period not to exceed twenty-four (24) months in equal quarterly installments.";
  const [s8, s8e] = sec(y, 8, "Withdrawal & Buyout", s8t);
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "During the term of the partnership and for a period of eighteen (18) months following withdrawal or dissolution, no partner shall directly or indirectly engage in any business that competes with the partnership's business within a one-hundred-mile radius of any office maintained by the partnership. Each partner acknowledges that this restriction is reasonable in scope and duration given the nature of the partnership's business and the investment made by the partners.";
  const [s9, s9e] = sec(y, 9, "Non-Compete", s9t);
  p2.push(...s9); y = s9e;

  p2.push(footer(772, "Confidential — Partnership Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("General Partnership Agreement"), divLine(40));
  y = 48;

  const s10t = "In the event of the death or permanent disability of a partner, the surviving or remaining partner shall have the option to purchase the deceased or disabled partner's interest at fair market value as determined by an independent appraiser. If the remaining partner does not elect to purchase the interest within sixty (60) days, the partnership shall be dissolved and wound down in an orderly manner. The partnership may also be dissolved by unanimous written agreement of all partners or by judicial decree.";
  const [s10, s10e] = sec(y, 10, "Death, Disability & Dissolution", s10t);
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s11t = "This Agreement shall be governed by and construed in accordance with the laws of the State of New York, without regard to its conflict of law provisions. Any disputes arising under or in connection with this Agreement shall be resolved through mediation, and if mediation is unsuccessful, through binding arbitration conducted in New York County in accordance with the rules of the American Arbitration Association. This Agreement constitutes the entire understanding between the partners and supersedes all prior negotiations and agreements.";
  const [s11, s11e] = sec(y, 11, "Governing Law", s11t);
  p3.push(...s11); y = s11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Partner A", "partnership_partnerA"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Partner B", "partnership_partnerB"));

  p3.push(footer(772, "Confidential — Partnership Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  2. BUSINESS LOAN AGREEMENT
 * ==================================================================== */

function buildLoanAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Business Loan Agreement"));
  p1.push(subtitle(MX + 18, 96, "Commercial Lending & Repayment Terms"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: July 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Oregon", 170));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Business Loan Agreement (\"Agreement\") is entered into as of July 15, 2025, by and between the Lender and the Borrower identified below. The Lender agrees to extend a commercial loan to the Borrower on the terms and conditions set forth herein, and the Borrower agrees to repay the loan in accordance with the repayment schedule described below.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LENDER"));
  p1.push(name(MX + 16, y + 28, "Pacific Northwest Credit Union"));
  p1.push(addr(MX + 16, y + 46, "3200 SW Morrison Street\nPortland, OR 97204"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "BORROWER"));
  p1.push(name(MX + 286, y + 28, "Willow Creek Hospitality Group LLC"));
  p1.push(addr(MX + 286, y + 46, "1875 Willamette Falls Drive\nBend, OR 97701"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Lender agrees to lend the Borrower the principal sum of Five Hundred Thousand Dollars ($500,000). The entire loan amount shall be disbursed in a single lump-sum payment to the Borrower's designated business account within five (5) business days following the execution of this Agreement and satisfaction of all conditions precedent. The Borrower acknowledges receipt of a truth-in-lending disclosure statement.";
  const [s1, s1e] = sec(y, 1, "Loan Amount & Disbursement", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The outstanding principal balance shall bear interest at a fixed annual rate of six and three-quarters percent (6.75%). Interest shall be calculated on the basis of a 360-day year and the actual number of days elapsed. Interest shall accrue from the date of disbursement and shall be compounded monthly. In no event shall the interest rate exceed the maximum rate permitted by applicable law.";
  const [s2, s2e] = sec(y, 2, "Interest Rate & Calculation", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Borrower shall repay the loan in sixty (60) equal monthly installments of principal and interest commencing on the first day of the month following disbursement. Each installment shall be approximately $9,842.55 based on a fully amortizing schedule. Payments received after the fifteenth (15th) day of the month shall incur a late fee equal to five percent (5%) of the overdue amount. All payments shall be applied first to accrued interest and then to principal.";
  const [s3, s3e] = sec(y, 3, "Repayment Schedule", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "As security for the repayment of this loan, the Borrower grants the Lender a first-priority security interest in all business assets of the Borrower, including but not limited to equipment, inventory, accounts receivable, and general intangibles. The Borrower authorizes the Lender to file a UCC-1 Financing Statement with the Oregon Secretary of State to perfect this security interest. The Borrower shall not grant any subordinate lien without the Lender's prior written consent.";
  const [s4, s4e] = sec(y, 4, "Collateral & Security Interest", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Loan Agreement"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Business Loan Agreement"), divLine(40));
  y = 48;

  const s5t = "The Borrower represents and warrants that: (a) it is a limited liability company duly organized and in good standing under the laws of the State of Oregon; (b) it has the legal authority to enter into this Agreement and incur the obligations herein; (c) all financial statements provided to the Lender are true and complete in all material respects; and (d) there are no pending or threatened actions that could materially affect the Borrower's ability to repay the loan.";
  const [s5, s5e] = sec(y, 5, "Representations & Warranties of Borrower", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "Throughout the term of this Agreement, the Borrower shall: (a) maintain a debt-to-equity ratio not exceeding 2.5:1 at all times; (b) furnish the Lender with quarterly unaudited and annual audited financial statements within forty-five (45) days of each period's close; (c) maintain adequate insurance on all collateral; (d) promptly notify the Lender of any material adverse change in financial condition; and (e) comply with all applicable laws and regulations.";
  const [s6, s6e] = sec(y, 6, "Covenants", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "An event of default shall occur if: (a) the Borrower fails to make any payment within ten (10) days of its due date; (b) the Borrower breaches any covenant or representation; (c) the Borrower becomes insolvent or files for bankruptcy; (d) a judgment is entered against the Borrower exceeding $50,000; or (e) any material collateral is lost, stolen, or substantially damaged without adequate insurance proceeds. Upon default, the Lender may declare the entire unpaid balance immediately due and payable.";
  const [s7, s7e] = sec(y, 7, "Events of Default", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Upon the occurrence of an event of default, the Lender shall be entitled to exercise all rights and remedies available under this Agreement, the Uniform Commercial Code, and applicable law. These remedies include acceleration of the outstanding balance, foreclosure on collateral, appointment of a receiver, and recovery of all costs of collection including reasonable attorney's fees. The Lender's remedies are cumulative and not exclusive.";
  const [s8, s8e] = sec(y, 8, "Remedies Upon Default", s8t);
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "The Borrower may prepay the loan in whole or in part at any time without penalty after the first twelve (12) months. Prepayments made within the first twelve months shall be subject to a prepayment premium of two percent (2%) of the amount prepaid. All prepayments shall be applied first to accrued interest and then to principal in reverse order of maturity.";
  const [s9, s9e] = sec(y, 9, "Prepayment Terms", s9t);
  p2.push(...s9); y = s9e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s10t = "This Agreement shall be governed by and construed in accordance with the laws of the State of Oregon. Any dispute arising under this Agreement shall be resolved in the state or federal courts located in Multnomah County, Oregon. This Agreement constitutes the entire understanding between the parties and may not be amended except in a writing signed by both parties.";
  const [s10, s10e] = sec(y, 10, "Governing Law", s10t);
  p2.push(...s10); y = s10e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Lender", "loan_lender"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Borrower", "loan_borrower"));

  p2.push(footer(772, "Confidential — Loan Agreement"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  3. FRANCHISE AGREEMENT
 * ==================================================================== */

function buildFranchiseAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Franchise Agreement"));
  p1.push(subtitle(MX + 18, 96, "Franchise Grant & Operating Terms"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: August 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Colorado", 180));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Franchise Agreement (\"Agreement\") is entered into as of August 1, 2025, by and between the Franchisor and the Franchisee identified below. The Franchisor grants the Franchisee the right to operate a franchised business under the Franchisor's trademarks, proprietary system, and brand standards, subject to the terms and conditions set forth herein.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "FRANCHISOR"));
  p1.push(name(MX + 16, y + 28, "GreenLeaf Caf\u00e9 Holdings Inc."));
  p1.push(addr(MX + 16, y + 46, "4500 Pearl Street, Suite 300\nBoulder, CO 80302"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "FRANCHISEE"));
  p1.push(name(MX + 286, y + 28, "Sunrise Foods LLC"));
  p1.push(addr(MX + 286, y + 46, "2780 Table Mesa Drive\nDenver, CO 80203"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The Franchisor hereby grants the Franchisee a non-exclusive license to operate one GreenLeaf Caf\u00e9 location within a designated territory. The Franchisee shall have an exclusivity radius of three (3) miles from the approved site, within which the Franchisor shall not establish or license another GreenLeaf Caf\u00e9 location during the term of this Agreement. The Franchisee shall not relocate the franchised business without the Franchisor's prior written approval.";
  const [s1, s1e] = sec(y, 1, "Grant of Franchise", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "The Franchisee shall pay the Franchisor an initial franchise fee of Forty-Five Thousand Dollars ($45,000) upon execution of this Agreement. This fee is fully earned upon payment and is non-refundable. The initial franchise fee covers the right to use the Franchisor's trademarks, initial training program, site selection assistance, and access to the Franchisor's proprietary operating manuals and systems.";
  const [s2, s2e] = sec(y, 2, "Initial Franchise Fee", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The Franchisee shall pay the Franchisor a continuing royalty fee equal to six percent (6%) of the Franchisee's gross revenue, due and payable on the tenth (10th) day of each month for the preceding month's revenue. Gross revenue includes all income from the sale of goods and services at the franchised location, excluding applicable sales taxes. Late royalty payments shall incur interest at the rate of one and one-half percent (1.5%) per month.";
  const [s3, s3e] = sec(y, 3, "Ongoing Royalties", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "The Franchisee shall contribute two percent (2%) of gross revenue to the Franchisor's national marketing and advertising fund. Contributions are due monthly on the same schedule as royalty payments. The Franchisor shall administer the fund and allocate expenditures for brand-level advertising, digital marketing campaigns, promotional materials, and public relations initiatives at its sole discretion.";
  const [s4, s4e] = sec(y, 4, "Marketing Fund Contribution", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Franchise Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Franchise Agreement"), divLine(40));
  y = 48;

  const s5t = "The Franchisor shall provide the Franchisee with an initial training program of four (4) weeks at the Franchisor's headquarters or a designated training facility. Training shall cover food preparation, customer service standards, point-of-sale systems, inventory management, and brand standards. The Franchisor shall also provide ongoing support including field consultations, updated training materials, and access to a dedicated franchise support hotline.";
  const [s5, s5e] = sec(y, 5, "Training & Support", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "The Franchisee shall operate the franchised business in strict compliance with the Franchisor's operations manual, quality control standards, and brand guidelines. The Franchisor reserves the right to conduct periodic inspections of the Franchisee's premises to ensure compliance. Failure to maintain prescribed standards after receiving written notice and a thirty (30) day cure period shall constitute a material breach of this Agreement.";
  const [s6, s6e] = sec(y, 6, "Operations Standards & Quality Control", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "The Franchisee shall purchase all food products, beverages, packaging materials, and equipment only from suppliers approved by the Franchisor. The Franchisor maintains a list of approved suppliers and may update it from time to time. The Franchisee may submit alternative suppliers for the Franchisor's consideration, provided the proposed supplier meets the Franchisor's quality specifications and certification requirements.";
  const [s7, s7e] = sec(y, 7, "Approved Suppliers & Products", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "The initial term of this Agreement is ten (10) years commencing on the Effective Date. The Franchisee shall have the option to renew for one additional ten-year term, provided the Franchisee is in good standing, has complied with all obligations, executes the then-current franchise agreement, completes any required renovations, and pays the applicable renewal fee. Written notice of intent to renew must be provided at least twelve (12) months prior to expiration.";
  const [s8, s8e] = sec(y, 8, "Term & Renewal", s8t);
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "The Franchisee shall not sell, assign, transfer, or encumber this Agreement or any interest in the franchised business without the Franchisor's prior written consent, which shall not be unreasonably withheld. The proposed transferee must meet the Franchisor's then-current qualification standards, complete the required training, and pay a transfer fee of $10,000. The Franchisor shall have a right of first refusal on any proposed transfer under the same terms.";
  const [s9, s9e] = sec(y, 9, "Transfer Restrictions", s9t);
  p2.push(...s9); y = s9e;

  p2.push(footer(772, "Confidential — Franchise Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Franchise Agreement"), divLine(40));
  y = 48;

  const s10t = "The Franchisor may terminate this Agreement upon thirty (30) days' written notice if the Franchisee fails to cure a material breach within the specified cure period, fails to maintain required insurance coverage, becomes insolvent or files for bankruptcy, or is convicted of a felony. Upon termination, the Franchisee shall immediately cease using the Franchisor's trademarks, return all proprietary materials, pay all outstanding amounts, and de-identify the premises within sixty (60) days. Post-termination non-compete restrictions shall apply for two (2) years within a ten-mile radius.";
  const [s10, s10e] = sec(y, 10, "Termination & Post-Termination Obligations", s10t);
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s11t = "This Agreement shall be governed by and construed in accordance with the laws of the State of Colorado, without regard to its conflict of law provisions. Any disputes arising under this Agreement shall first be submitted to mediation administered by the American Arbitration Association. If mediation is unsuccessful within sixty (60) days, disputes shall be resolved through binding arbitration in Denver, Colorado. This Agreement constitutes the entire agreement between the parties and supersedes all prior understandings.";
  const [s11, s11e] = sec(y, 11, "Governing Law", s11t);
  p3.push(...s11); y = s11e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Franchisor", "franchise_franchisor"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Franchisee", "franchise_franchisee"));

  p3.push(footer(772, "Confidential — Franchise Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  4. JOINT VENTURE AGREEMENT
 * ==================================================================== */

function buildJointVenture(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Joint Venture Agreement"));
  p1.push(subtitle(MX + 18, 96, "Collaborative Infrastructure Partnership"));

  // Pills
  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: September 1, 2025", 180));
  p1.push(pill(MX + 212, y, 200), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 180));

  // Accent divider + intro
  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const intro = "This Joint Venture Agreement (\"Agreement\") is entered into as of September 1, 2025, by and between the Venturers identified below. The parties wish to establish a joint venture for the purpose of collaborating on large-scale infrastructure projects, pooling their respective expertise, resources, and capital to pursue opportunities that neither party could efficiently undertake independently.";
  p1.push(body(MX, y, intro)); y += bodyH(intro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "VENTURER A"));
  p1.push(name(MX + 16, y + 28, "Titan Infrastructure Corp."));
  p1.push(addr(MX + 16, y + 46, "One Liberty Plaza, 42nd Floor\nNew York, NY 10006"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "VENTURER B"));
  p1.push(name(MX + 286, y + 28, "Global Materials Partners Ltd."));
  p1.push(addr(MX + 286, y + 46, "8900 International Drive, Suite 700\nHouston, TX 77036"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const s1t = "The purpose of this joint venture is to jointly bid on, develop, and deliver large-scale infrastructure projects including but not limited to transportation networks, energy facilities, and municipal utility systems. The joint venture shall operate under the name Titan-Global Infrastructure JV and shall pursue specific projects as mutually identified and approved by the Management Committee. Neither party shall be deemed a general partner of the other by virtue of this Agreement.";
  const [s1, s1e] = sec(y, 1, "Purpose & Scope", s1t);
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s2t = "Each Venturer shall make initial capital contributions as determined by the Management Committee based on the requirements of each approved project. Unless otherwise agreed, capital contributions shall be made in equal shares. Additional funding commitments shall be documented in project-specific addenda. A Venturer that fails to fund its share within fifteen (15) days of a capital call shall have its profit participation reduced proportionally until the deficiency is cured.";
  const [s2, s2e] = sec(y, 2, "Capital Contributions & Funding Commitments", s2t);
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s3t = "The joint venture shall be governed by a Management Committee consisting of two (2) representatives appointed by each Venturer. The Management Committee shall meet at least quarterly and shall be responsible for strategic direction, project selection, budget approval, and oversight of day-to-day operations. A quorum shall consist of at least one representative from each Venturer. The Management Committee shall appoint a Project Director to manage field operations.";
  const [s3, s3e] = sec(y, 3, "Governance & Management Committee", s3t);
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const s4t = "Major decisions — including approval of new projects exceeding $5 million, changes to the joint venture's scope, incurrence of debt, admission of new venturers, and any amendments to this Agreement — require the unanimous consent of all Management Committee members. Routine operational decisions, including procurement under $500,000 and staffing for approved projects, may be made by majority vote of the Management Committee.";
  const [s4, s4e] = sec(y, 4, "Decision-Making", s4t);
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential — Joint Venture Agreement"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Joint Venture Agreement"), divLine(40));
  y = 48;

  const s5t = "Net profits and losses of the joint venture shall be allocated between the Venturers in proportion to their respective capital contributions for each project, unless otherwise specified in a project addendum. Profit distributions shall be made within sixty (60) days following the completion of each project or semi-annually for ongoing projects, whichever occurs first. Each Venturer shall be responsible for its own income tax obligations arising from its share of joint venture income.";
  const [s5, s5e] = sec(y, 5, "Profit & Loss Allocation", s5t);
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s6t = "All intellectual property, designs, engineering plans, technical specifications, and other work product developed in the course of joint venture activities shall be jointly owned by the Venturers unless otherwise agreed in a project addendum. Each Venturer retains ownership of its pre-existing intellectual property and grants the joint venture a non-exclusive license to use such property solely for joint venture purposes during the term of this Agreement.";
  const [s6, s6e] = sec(y, 6, "Intellectual Property & Work Product", s6t);
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s7t = "During the term of this Agreement, neither Venturer shall directly or indirectly bid on, participate in, or provide services for any project that competes with an approved joint venture project without the prior written consent of the other Venturer. This restriction applies to projects within the same geographic market and of a similar scope as those undertaken by the joint venture. Each Venturer remains free to pursue opportunities outside the joint venture's defined scope.";
  const [s7, s7e] = sec(y, 7, "Non-Compete During JV", s7t);
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s8t = "Each Venturer represents and warrants that: (a) it is duly organized, validly existing, and in good standing under the laws of its jurisdiction of formation; (b) it has the corporate authority and capacity to enter into this Agreement and perform its obligations; (c) this Agreement does not conflict with any existing contractual obligation; and (d) it possesses all licenses, permits, and qualifications necessary to perform infrastructure construction and development services.";
  const [s8, s8e] = sec(y, 8, "Representations & Warranties", s8t);
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const s9t = "This Agreement shall remain in effect for an initial term of five (5) years from the Effective Date. The term may be extended by mutual written agreement of the Venturers for successive two-year periods. Either Venturer may elect not to renew by providing written notice at least one hundred eighty (180) days prior to the expiration of the then-current term. Active projects at the time of non-renewal shall be completed under the terms of this Agreement.";
  const [s9, s9e] = sec(y, 9, "Term & Extension", s9t);
  p2.push(...s9); y = s9e;

  p2.push(footer(772, "Confidential — Joint Venture Agreement"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Joint Venture Agreement"), divLine(40));
  y = 48;

  const s10t = "Either Venturer may withdraw from the joint venture upon one hundred eighty (180) days' written notice, provided all active project obligations are satisfied or assumed by the remaining Venturer. Upon withdrawal, the withdrawing Venturer's interest shall be valued by an independent appraiser and bought out over twelve (12) months. Upon termination or expiration, the Venturers shall cooperate to wind down operations in an orderly manner, complete or transition active projects, settle all liabilities, and distribute remaining assets proportionally.";
  const [s10, s10e] = sec(y, 10, "Withdrawal & Wind-Down", s10t);
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s11t = "Any dispute arising out of or relating to this Agreement that cannot be resolved through good-faith negotiation within thirty (30) days shall be submitted to binding arbitration administered by the International Chamber of Commerce (ICC) in accordance with its then-current arbitration rules. The arbitration shall be conducted in New York, New York, before a panel of three (3) arbitrators. The arbitral award shall be final and binding and may be enforced in any court of competent jurisdiction.";
  const [s11, s11e] = sec(y, 11, "Dispute Resolution", s11t);
  p3.push(...s11); y = s11e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const s12t = "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law principles. This Agreement constitutes the entire understanding between the Venturers with respect to the subject matter hereof and supersedes all prior agreements, negotiations, and discussions. Amendments to this Agreement must be in writing and signed by authorized representatives of both Venturers.";
  const [s12, s12e] = sec(y, 12, "Governing Law", s12t);
  p3.push(...s12); y = s12e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Venturer A", "jv_venturerA"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Venturer B", "jv_venturerB"));

  p3.push(footer(772, "Confidential — Joint Venture Agreement"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

function buildBuySellAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Buy-Sell Agreement"));
  p1.push(subtitle(MX + 18, 96, "Shareholder Buyout & Succession Planning"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: March 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const bsIntro = "This Buy-Sell Agreement (\"Agreement\") is entered into as of March 1, 2025, by and between the Selling Shareholder and the Company, on behalf of the Remaining Shareholders. The purpose of this Agreement is to provide an orderly mechanism for the transfer of ownership interests upon the occurrence of certain triggering events, thereby preserving business continuity and protecting shareholder value.";
  p1.push(body(MX, y, bsIntro)); y += bodyH(bsIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "SELLING SHAREHOLDER"));
  p1.push(name(MX + 16, y + 28, "Marcus Chen"));
  p1.push(addr(MX + 16, y + 46, "35% Ownership Interest\n482 Summit Drive, Palo Alto, CA 94301"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "COMPANY"));
  p1.push(name(MX + 286, y + 28, "Vanguard Tech Solutions Inc."));
  p1.push(addr(MX + 286, y + 46, "Representing Remaining Shareholders\n1200 Innovation Blvd, Wilmington, DE 19801"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [s1, s1e] = sec(y, 1, "Triggering Events",
    "This Agreement shall be triggered upon the occurrence of any of the following events with respect to the Selling Shareholder: (a) death; (b) total and permanent disability as certified by an independent physician; (c) voluntary retirement upon reaching age sixty-five (65) or after providing at least twelve (12) months' advance written notice; (d) voluntary departure from the Company; or (e) involuntary termination for cause as determined by a majority vote of the Board of Directors. Upon a triggering event, the buy-sell provisions herein become immediately operative.");
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s2, s2e] = sec(y, 2, "Valuation Method",
    "The fair market value of the Selling Shareholder's interest shall be determined by an independent appraiser mutually agreed upon by the parties within thirty (30) days of the triggering event. If the parties cannot agree on an appraiser, each party shall select one appraiser and the two appraisers shall select a third; the average of the three valuations shall be binding. As a formula-based alternative, the parties may elect a valuation equal to three times (3x) the Company's trailing twelve-month EBITDA, with appropriate adjustments for working capital and outstanding indebtedness.");
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s3, s3e] = sec(y, 3, "Right of First Refusal",
    "Before any sale or transfer of shares to a third party, the Selling Shareholder must first offer the shares to the Remaining Shareholders on the same terms and conditions. The Remaining Shareholders shall have forty-five (45) days from receipt of written notice to exercise their right of first refusal. If the right is not exercised in full, the Selling Shareholder may proceed with the third-party sale within ninety (90) days on terms no more favorable than those offered.");
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Buy-Sell Agreement"), divLine(40));
  y = 48;

  const [s4, s4e] = sec(y, 4, "Put/Call Options",
    "The Selling Shareholder shall have the right (a \"Put\") to require the Company to purchase all of the Selling Shareholder's shares at the appraised value upon a triggering event. Conversely, the Company shall have the right (a \"Call\") to require the Selling Shareholder to sell all shares to the Company at the appraised value. The Put or Call must be exercised by written notice within sixty (60) days of the valuation determination. Neither option may be exercised if the other has already been validly exercised.");
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s5, s5e] = sec(y, 5, "Purchase Price and Payment Terms",
    "The purchase price shall equal the fair market value as determined under Section 2. Payment may be made in a single lump sum at closing or, at the purchaser's election, in equal monthly installments over a period not to exceed thirty-six (36) months, bearing interest at five percent (5%) per annum on the unpaid balance. All installment payments shall be secured by the purchased shares, which shall be held in escrow until paid in full. A default of more than fifteen (15) days on any installment accelerates the entire balance.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Life Insurance Funding",
    "The Company shall maintain key-person life insurance policies on each shareholder in amounts reasonably sufficient to fund the buy-sell obligation upon death. The Company shall be the owner and beneficiary of such policies. Insurance proceeds received upon a shareholder's death shall be applied first toward the purchase price. Any excess proceeds shall be retained by the Company. Each shareholder agrees to cooperate in the application process and submit to medical examinations as reasonably required.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Closing Procedures",
    "Closing shall occur within ninety (90) days of the triggering event unless otherwise agreed. At closing, the Selling Shareholder shall deliver duly endorsed stock certificates, executed transfer documents, and all corporate records in the Selling Shareholder's possession. The purchaser shall deliver the purchase price or a duly executed promissory note for installment payments. Each party shall execute all ancillary documents reasonably necessary to effectuate the transfer.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Restrictive Covenants Post-Sale",
    "For a period of twenty-four (24) months following the closing date, the Selling Shareholder shall not directly or indirectly: (a) compete with the Company in any market where the Company conducts business; (b) solicit or hire any employee of the Company; or (c) solicit any customer, client, or supplier of the Company. The geographic scope shall extend to any state in which the Company generated revenue during the twelve (12) months preceding the closing date.");
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Buy-Sell Agreement"), divLine(40));
  y = 48;

  const [s9, s9e] = sec(y, 9, "Spousal Consent",
    "Each shareholder's spouse, if any, shall execute a consent acknowledging this Agreement and agreeing to be bound by its terms with respect to any community property or marital interest in the shares. Failure to obtain spousal consent shall not invalidate this Agreement but may subject the non-complying shareholder to indemnification obligations for any claims arising from the spouse's interest.");
  p3.push(...s9); y = s9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [s10, s10e] = sec(y, 10, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to conflict of laws principles. Any dispute arising under this Agreement shall be resolved by binding arbitration in Wilmington, Delaware, administered under the rules of the American Arbitration Association. The prevailing party shall be entitled to recover reasonable attorneys' fees and costs.");
  p3.push(...s10); y = s10e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Selling Shareholder", "buySell_seller"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Company Representative", "buySell_company"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  2. STOCK OPTION GRANT AGREEMENT
 * ==================================================================== */

function buildStockOptionGrant(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Stock Option Grant Agreement"));
  p1.push(subtitle(MX + 18, 96, "Equity Incentive Plan — Option Award"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Grant Date: April 15, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: California", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const soIntro = "This Stock Option Grant Agreement (\"Agreement\") is entered into as of April 15, 2025, by and between the Company and the Optionee pursuant to the Company's 2024 Equity Incentive Plan (the \"Plan\"). This Agreement evidences the grant of stock options to the Optionee under the terms and conditions set forth herein and in the Plan.";
  p1.push(body(MX, y, soIntro)); y += bodyH(soIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "Quantum Dynamics Inc."));
  p1.push(addr(MX + 16, y + 46, "2100 Quantum Way\nSan Jose, CA 95134"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "OPTIONEE"));
  p1.push(name(MX + 286, y + 28, "Dr. Sarah Kim"));
  p1.push(addr(MX + 286, y + 46, "VP Engineering\n738 Hillcrest Ave, Mountain View, CA 94041"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [s1, s1e] = sec(y, 1, "Grant of Options",
    "Subject to the terms and conditions of this Agreement and the Plan, the Company hereby grants to the Optionee options to purchase fifty thousand (50,000) shares of the Company's Common Stock, par value $0.001 per share (the \"Option Shares\"). This grant is intended to qualify as Incentive Stock Options (\"ISOs\") to the maximum extent permitted under Section 422 of the Internal Revenue Code, with any excess treated as Non-Qualified Stock Options (\"NSOs\").");
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s2, s2e] = sec(y, 2, "Exercise Price",
    "The exercise price per Option Share shall be Four Dollars and Twenty-Five Cents ($4.25), which represents the fair market value of one share of Common Stock as determined by the Board of Directors based on an independent 409A valuation report as of the grant date. The exercise price shall not be subject to adjustment except as provided in Section 12 of the Plan in connection with stock splits, dividends, or recapitalizations.");
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s3, s3e] = sec(y, 3, "Vesting Schedule",
    "The Option Shares shall vest over a four (4) year period as follows: twenty-five percent (25%) of the Option Shares shall vest on the first anniversary of the grant date (the \"Cliff Date\"), and the remaining seventy-five percent (75%) shall vest in equal monthly installments of 1/48th of the total grant over the thirty-six (36) months following the Cliff Date, subject to the Optionee's continuous service with the Company through each applicable vesting date.");
  p1.push(...s3); y = s3e;

  // Section 4
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s4, s4e] = sec(y, 4, "Exercise Period and Expiration",
    "Options may be exercised at any time after vesting and before the tenth (10th) anniversary of the grant date (the \"Expiration Date\"). Vested options that are not exercised by the Expiration Date shall terminate and be of no further force or effect. In no event shall the exercise period extend beyond the term permitted under Section 422 of the Internal Revenue Code for ISOs.");
  p1.push(...s4); y = s4e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Stock Option Grant Agreement"), divLine(40));
  y = 48;

  const [s5, s5e] = sec(y, 5, "Method of Exercise",
    "The Optionee may exercise vested options by delivering a written notice of exercise to the Company accompanied by payment of the aggregate exercise price. Payment may be made by: (a) cash or check; (b) cashless exercise through a broker-assisted same-day sale; or (c) net exercise whereby the Company withholds a number of Option Shares with a fair market value equal to the aggregate exercise price. The Company shall deliver shares within ten (10) business days of a valid exercise.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Tax Treatment",
    "The Optionee acknowledges that the tax treatment of ISOs and NSOs differs materially. ISOs may qualify for favorable long-term capital gains treatment if the holding period requirements under Section 422 are met. NSOs are subject to ordinary income tax upon exercise. The Company shall have the right to withhold applicable taxes or require the Optionee to remit amounts sufficient to satisfy withholding obligations. The Optionee is advised to consult a tax advisor. This grant is intended to comply with, or be exempt from, Section 409A of the Internal Revenue Code.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Termination of Employment",
    "Upon termination of the Optionee's service for any reason other than cause, death, or disability, vested options must be exercised within ninety (90) days of the termination date or they shall expire. Upon termination for cause, all options (vested and unvested) shall immediately terminate. Upon death or disability, the exercise period shall extend to twelve (12) months following such event. Unvested options shall terminate immediately upon cessation of service.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Change of Control",
    "In the event of a Change of Control (as defined in the Plan), if the successor entity does not assume or substitute equivalent options, then one hundred percent (100%) of all unvested Option Shares shall immediately vest and become exercisable. If the options are assumed, the Board may in its discretion provide for accelerated vesting of fifty percent (50%) or more of the unvested shares upon a qualifying termination within twelve (12) months following the Change of Control (double-trigger acceleration).");
  p2.push(...s8); y = s8e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s9, s9e] = sec(y, 9, "Transferability Restrictions",
    "Options granted under this Agreement are personal to the Optionee and may not be sold, assigned, transferred, pledged, or otherwise encumbered, except by will or the laws of descent and distribution. During the lifetime of the Optionee, options may be exercised only by the Optionee or the Optionee's guardian or legal representative.");
  p2.push(...s9); y = s9e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Company", "stockOption_company"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Optionee", "stockOption_optionee"));
  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  3. SHAREHOLDER VOTING AGREEMENT
 * ==================================================================== */

function buildVotingAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Shareholder Voting Agreement"));
  p1.push(subtitle(MX + 18, 96, "Board Composition & Governance"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: June 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const vaIntro = "This Shareholder Voting Agreement (\"Agreement\") is entered into as of June 1, 2025, by and between the Lead Investor and the Founder identified below. The parties desire to establish a framework governing the exercise of their respective voting rights to ensure stable governance, appropriate board representation, and alignment of interests throughout the term of this Agreement.";
  p1.push(body(MX, y, vaIntro)); y += bodyH(vaIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "LEAD INVESTOR"));
  p1.push(name(MX + 16, y + 28, "Crestview Capital Partners LP"));
  p1.push(addr(MX + 16, y + 46, "Series B Lead Investor\n400 Park Avenue, Suite 1200, New York, NY 10022"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "FOUNDER"));
  p1.push(name(MX + 286, y + 28, "Daniel Park, CEO"));
  p1.push(addr(MX + 286, y + 46, "Co-Founder & Chief Executive Officer\n55 Startup Lane, San Francisco, CA 94107"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [s1, s1e] = sec(y, 1, "Purpose and Recitals",
    "The parties enter into this Agreement in connection with the Series B Preferred Stock financing of the Company. The Lead Investor is acquiring shares representing a significant minority interest, and the Founder holds shares representing a controlling interest. This Agreement is intended to provide for predictable governance, protect the interests of all shareholders, and facilitate informed decision-making at the board level throughout the Company's growth stage.");
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s2, s2e] = sec(y, 2, "Board Composition",
    "The Board of Directors shall consist of five (5) seats allocated as follows: two (2) directors designated by the Lead Investor (the \"Investor Directors\"), two (2) directors designated by the Founder (the \"Founder Directors\"), and one (1) independent director mutually agreed upon by both the Lead Investor and the Founder (the \"Independent Director\"). The Independent Director shall be a person with relevant industry experience who is not affiliated with either party. Changes to the board size require unanimous consent of the parties.");
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s3, s3e] = sec(y, 3, "Voting Commitments",
    "Each party agrees to vote all shares of capital stock held by such party, whether now owned or hereafter acquired, to elect and maintain in office the directors designated in accordance with Section 2 above. The parties shall take all actions necessary, including voting at annual and special meetings and executing written consents, to ensure the composition of the Board remains consistent with this Agreement. Neither party shall take any action to remove a director designated by the other party without such party's consent.");
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 2"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Shareholder Voting Agreement"), divLine(40));
  y = 48;

  const [s4, s4e] = sec(y, 4, "Protective Provisions",
    "For so long as the Lead Investor holds at least fifty percent (50%) of the shares originally purchased, the Company shall not, without the Lead Investor's prior written consent: (a) incur indebtedness in excess of Five Hundred Thousand Dollars ($500,000) individually or in the aggregate; (b) issue new equity securities or securities convertible into equity; (c) effect any merger, acquisition, or consolidation; or (d) sell, lease, or dispose of material assets representing more than twenty percent (20%) of total Company assets.");
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s5, s5e] = sec(y, 5, "Drag-Along Rights",
    "If holders of at least sixty-five percent (65%) of the outstanding shares approve a bona fide acquisition, merger, or sale of all or substantially all Company assets to an unaffiliated third party, all parties to this Agreement shall vote in favor of and take all actions necessary to consummate such transaction on the same terms and conditions. No party shall exercise dissenters' or appraisal rights in connection with a drag-along transaction.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Information Rights",
    "The Company shall deliver to the Lead Investor: (a) unaudited monthly financial statements within thirty (30) days of month-end; (b) audited annual financial statements prepared by a nationally recognized accounting firm within ninety (90) days of fiscal year-end; and (c) an annual operating budget at least thirty (30) days prior to the start of each fiscal year. The Lead Investor shall have the right to designate one (1) board observer who may attend all board meetings in a non-voting capacity.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "VCOC Compliance",
    "The Company acknowledges that the Lead Investor is a venture capital operating company (\"VCOC\") within the meaning of the Department of Labor regulations. The Company shall provide the Lead Investor with management rights sufficient to qualify the investment as a VCOC operating company investment, including the right to consult with and advise management on significant business matters and to inspect the Company's books and records.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Term and Termination",
    "This Agreement shall remain in effect until the earliest of: (a) consummation of a firm-commitment underwritten initial public offering with aggregate gross proceeds of at least Twenty-Five Million Dollars ($25,000,000); (b) consummation of an acquisition resulting in a Change of Control; (c) the written consent of the parties holding at least seventy-five percent (75%) of the outstanding shares; or (d) dissolution or liquidation of the Company.");
  p2.push(...s8); y = s8e;

  // Signatures
  y += DIV_PRE; p2.push(accentDiv(y)); y += 10;
  p2.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p2.push(...sigBlockWithRole(MX, y, "Lead Investor", "voting_investor"));
  p2.push(...sigBlockWithRole(MX + 276, y, "Founder", "voting_founder"));
  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 2"));

  return [page(p1), page(p2)];
}

/* ====================================================================
 *  4. INVESTOR RIGHTS AGREEMENT
 * ==================================================================== */

function buildInvestorRightsAgreement(): Doc {
  const p1: CanvasElement[] = [];
  let y: number;

  // Header
  p1.push(stripe(), logoPh(), banner(58, 66));
  p1.push(title(MX + 18, 72, "Investor Rights Agreement"));
  p1.push(subtitle(MX + 18, 96, "Registration Rights, Protections & Governance"));

  y = 132;
  p1.push(pill(MX, y, 200), meta(MX + 20, y + PILL_TEXT_Y, "Effective: July 1, 2025", 180));
  p1.push(pill(MX + 212, y, 190), meta(MX + 232, y + PILL_TEXT_Y, "Governing Law: Delaware", 170));

  y = 166;
  p1.push(accentDiv(y)); y += 10;
  const irIntro = "This Investor Rights Agreement (\"Agreement\") is entered into as of July 1, 2025, by and between the Company and the Lead Investor identified below. This Agreement establishes certain registration rights, information rights, participation rights, and protective provisions granted in connection with the Lead Investor's purchase of Series A Preferred Stock of the Company.";
  p1.push(body(MX, y, irIntro)); y += bodyH(irIntro) + GAP;

  // Party cards
  const PH = 100;
  p1.push(...pCard(MX, y, 258, PH, INK));
  p1.push(label(MX + 16, y + 12, "COMPANY"));
  p1.push(name(MX + 16, y + 28, "NovaBridge AI Inc."));
  p1.push(addr(MX + 16, y + 46, "350 AI Boulevard, Suite 800\nAustin, TX 78701"));
  p1.push(...pCard(MX + 270, y, 270, PH, "#3b82f6"));
  p1.push(label(MX + 286, y + 12, "LEAD INVESTOR"));
  p1.push(name(MX + 286, y + 28, "Summit Ventures Fund III LP"));
  p1.push(addr(MX + 286, y + 46, "Series A Lead Investor\n600 Venture Drive, Menlo Park, CA 94025"));
  y += PH + GAP;

  // Section 1
  p1.push(divLine(y)); y += DIV_POST + DIV_PRE;
  const [s1, s1e] = sec(y, 1, "Definitions",
    "\"Registrable Securities\" means shares of Common Stock issuable upon conversion of the Preferred Stock, excluding any shares sold in a public offering or pursuant to Rule 144. \"Holders\" means the Lead Investor and any permitted transferee who holds Registrable Securities and has agreed to be bound by this Agreement. \"Form S-3\" means such form under the Securities Act as in effect on the date hereof or any registration form under the Securities Act subsequently adopted by the SEC that permits incorporation of substantial information by reference.");
  p1.push(...s1); y = s1e;

  // Section 2
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s2, s2e] = sec(y, 2, "Registration Rights",
    "Demand Registration: At any time beginning three (3) years after the closing, Holders of at least forty percent (40%) of the Registrable Securities may demand that the Company file a registration statement under the Securities Act covering all or part of their Registrable Securities. The Company shall not be obligated to effect more than two (2) demand registrations. Piggyback Rights: If the Company proposes to register any securities for its own account or the account of others, it shall give each Holder written notice and include their Registrable Securities in such registration. S-3 Registration: If the Company qualifies for Form S-3, Holders may request unlimited short-form registrations, provided the anticipated aggregate offering price exceeds One Million Dollars ($1,000,000).");
  p1.push(...s2); y = s2e;

  // Section 3
  y += DIV_PRE; p1.push(divLine(y)); y += DIV_POST;
  const [s3, s3e] = sec(y, 3, "Information and Inspection Rights",
    "The Company shall deliver to each Holder: (a) unaudited quarterly financial statements within forty-five (45) days of each fiscal quarter-end; (b) audited annual financial statements prepared by a Big Four or nationally recognized accounting firm within ninety (90) days of fiscal year-end; and (c) an annual budget at least thirty (30) days before each fiscal year. The Lead Investor shall have the right to designate one (1) board observer who may attend all board meetings and receive all board materials. The Lead Investor shall also have the right to inspect the Company's books and records during normal business hours upon reasonable notice.");
  p1.push(...s3); y = s3e;

  p1.push(footer(772, "Confidential"), footerR(772, "Page 1 of 3"));

  /* ── page 2 ── */
  const p2: CanvasElement[] = [];
  p2.push(stripe(), contHdr("Investor Rights Agreement"), divLine(40));
  y = 48;

  const [s4, s4e] = sec(y, 4, "Right of First Offer",
    "The Company hereby grants each Holder the right to participate on a pro-rata basis in any future issuance of equity securities or securities convertible into equity (a \"New Issuance\"), based on such Holder's percentage ownership on an as-converted basis. The Company shall provide written notice of any proposed New Issuance at least twenty (20) days prior to closing. Each Holder shall have fifteen (15) days from receipt of such notice to exercise its participation right. This right shall not apply to excluded issuances including employee equity plans, strategic acquisitions, or equipment leasing arrangements.");
  p2.push(...s4); y = s4e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s5, s5e] = sec(y, 5, "Anti-Dilution Protections",
    "The conversion price of the Preferred Stock shall be subject to weighted-average broad-based anti-dilution adjustment in the event the Company issues additional shares of Common Stock or securities convertible into Common Stock at a price per share less than the then-applicable conversion price. The weighted-average formula shall include in the denominator all outstanding shares on an as-converted and fully-diluted basis, including all options, warrants, and convertible securities. Excluded from anti-dilution adjustment are issuances under the Company's equity incentive plan up to the reserved pool.");
  p2.push(...s5); y = s5e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s6, s6e] = sec(y, 6, "Board Representation",
    "For so long as the Lead Investor holds at least twenty-five percent (25%) of the shares originally purchased, the Lead Investor shall be entitled to designate one (1) member of the Company's Board of Directors. The Lead Investor's board designee shall be entitled to serve on the compensation and audit committees. The Company shall reimburse reasonable expenses incurred by the designee in attending board meetings and shall maintain directors' and officers' liability insurance in coverage amounts acceptable to the Lead Investor.");
  p2.push(...s6); y = s6e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s7, s7e] = sec(y, 7, "Protective Provisions",
    "For so long as any shares of Preferred Stock remain outstanding, the Company shall not, without the approval of Holders representing at least a majority of the outstanding Preferred Stock: (a) amend the Certificate of Incorporation in a manner that adversely affects the rights of the Preferred Stock; (b) authorize or issue any security senior to or on parity with the Preferred Stock; (c) declare or pay any dividend on the Common Stock; (d) repurchase shares except from departing employees at cost; or (e) increase the size of the Board beyond seven (7) members.");
  p2.push(...s7); y = s7e;

  y += DIV_PRE; p2.push(divLine(y)); y += DIV_POST;
  const [s8, s8e] = sec(y, 8, "Lock-Up Agreement",
    "Each Holder agrees that, upon the request of the Company or the managing underwriter in connection with an initial public offering, the Holder shall not sell, transfer, or otherwise dispose of any shares of the Company for a period of one hundred eighty (180) days following the effective date of the IPO registration statement. This restriction shall apply to all shares held by the Holder, whether acquired through conversion or otherwise, and shall be subject to customary exceptions for estate planning transfers and pro-rata release provisions.");
  p2.push(...s8); y = s8e;

  p2.push(footer(772, "Confidential"), footerR(772, "Page 2 of 3"));

  /* ── page 3 ── */
  const p3: CanvasElement[] = [];
  p3.push(stripe(), contHdr("Investor Rights Agreement"), divLine(40));
  y = 48;

  const [s9, s9e] = sec(y, 9, "Non-Compete and Non-Solicitation",
    "During the term of this Agreement and for a period of twelve (12) months following its termination, the Founder and key executives shall not directly or indirectly engage in or assist any business that competes with the Company's core business within the markets in which the Company operates. Neither party shall solicit, recruit, or hire any employee or consultant of the Company during the restricted period without the Board's prior written consent.");
  p3.push(...s9); y = s9e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [s10, s10e] = sec(y, 10, "Confidentiality",
    "Each party acknowledges that, in the course of its relationship with the Company, it will receive confidential and proprietary information. All financial data, business plans, product roadmaps, customer lists, and other non-public information shall be treated as strictly confidential and shall not be disclosed to any third party without prior written consent, except as required by law or regulation. This obligation survives termination of this Agreement for a period of three (3) years.");
  p3.push(...s10); y = s10e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [s11, s11e] = sec(y, 11, "Term and Termination",
    "This Agreement shall remain in effect until the earliest of: (a) the closing of a firm-commitment underwritten initial public offering; (b) a deemed liquidation event or Change of Control resulting in distributions to all Preferred Stock holders; (c) the date on which no Registrable Securities remain outstanding; or (d) the written agreement of the Company and Holders of at least seventy percent (70%) of the outstanding Registrable Securities to terminate this Agreement.");
  p3.push(...s11); y = s11e;

  y += DIV_PRE; p3.push(divLine(y)); y += DIV_POST;
  const [s12, s12e] = sec(y, 12, "Governing Law",
    "This Agreement shall be governed by and construed in accordance with the laws of the State of Delaware, without regard to conflict of laws principles. Any dispute arising under this Agreement shall be resolved exclusively in the federal or state courts located in Wilmington, Delaware. Each party irrevocably consents to the jurisdiction of such courts and waives any objection based on venue or inconvenient forum.");
  p3.push(...s12); y = s12e;

  // Signatures
  y += DIV_PRE; p3.push(accentDiv(y)); y += 10;
  p3.push(text({ x: MX, y, text: "Signatures", fontSize: 16, fontWeight: 800, fill: INK, fontFamily: F, align: "left", width: CW }));
  y += 22;
  p3.push(...sigBlockWithRole(MX, y, "Company", "investorRights_company"));
  p3.push(...sigBlockWithRole(MX + 276, y, "Lead Investor", "investorRights_investor"));
  p3.push(footer(772, "Confidential"), footerR(772, "Page 3 of 3"));

  return [page(p1), page(p2), page(p3)];
}

/* ====================================================================
 *  Template Role Metadata (for Quick Send)
 * ==================================================================== */

export type TemplateRole = {
  key: string;       // e.g. "partyA", "partyB"
  label: string;     // e.g. "Disclosing Party", "Receiving Party"
  fieldIds: string[]; // IDs of signature + field elements assigned to this role
};

export type TemplateMetadata = {
  roles: TemplateRole[];
};

export const templateMetadata: Record<string, TemplateMetadata> = {
  nda: {
    roles: [
      { key: "partyA", label: "Disclosing Party", fieldIds: ["nda_partyA_sig", "nda_partyA_name", "nda_partyA_date"] },
      { key: "partyB", label: "Receiving Party", fieldIds: ["nda_partyB_sig", "nda_partyB_name", "nda_partyB_date"] },
    ],
  },
  contractor: {
    roles: [
      { key: "company", label: "Company Representative", fieldIds: ["contractor_company_sig", "contractor_company_name", "contractor_company_date"] },
      { key: "contractor", label: "Contractor", fieldIds: ["contractor_contractor_sig", "contractor_contractor_name", "contractor_contractor_date"] },
    ],
  },
  offerLetter: {
    roles: [
      { key: "company", label: "Company Representative", fieldIds: ["offer_company_sig", "offer_company_name", "offer_company_date"] },
      { key: "candidate", label: "Candidate", fieldIds: ["offer_candidate_sig", "offer_candidate_name", "offer_candidate_date"] },
    ],
  },
  invoice: {
    roles: [
      { key: "sender", label: "Authorized Signer", fieldIds: ["invoice_sender_sig"] },
    ],
  },
  serviceAgreement: {
    roles: [
      { key: "provider", label: "Service Provider", fieldIds: ["service_provider_sig", "service_provider_name", "service_provider_date"] },
      { key: "client", label: "Client", fieldIds: ["service_client_sig", "service_client_name", "service_client_date"] },
    ],
  },
  residentialLease: {
    roles: [
      { key: "landlord", label: "Landlord", fieldIds: ["lease_landlord_sig", "lease_landlord_name", "lease_landlord_date"] },
      { key: "tenant", label: "Tenant", fieldIds: ["lease_tenant_sig", "lease_tenant_name", "lease_tenant_date"] },
    ],
  },
  propertyPurchase: {
    roles: [
      { key: "seller", label: "Seller", fieldIds: ["purchase_seller_sig", "purchase_seller_name", "purchase_seller_date"] },
      { key: "buyer", label: "Buyer", fieldIds: ["purchase_buyer_sig", "purchase_buyer_name", "purchase_buyer_date"] },
    ],
  },
  propertyManagement: {
    roles: [
      { key: "owner", label: "Property Owner", fieldIds: ["mgmt_owner_sig", "mgmt_owner_name", "mgmt_owner_date"] },
      { key: "manager", label: "Management Company", fieldIds: ["mgmt_manager_sig", "mgmt_manager_name", "mgmt_manager_date"] },
    ],
  },
  materialSupply: {
    roles: [
      { key: "supplier", label: "Supplier", fieldIds: ["material_supplier_sig", "material_supplier_name", "material_supplier_date"] },
      { key: "buyer", label: "Buyer", fieldIds: ["material_buyer_sig", "material_buyer_name", "material_buyer_date"] },
    ],
  },
  constructionServices: {
    roles: [
      { key: "owner", label: "Owner", fieldIds: ["construction_owner_sig", "construction_owner_name", "construction_owner_date"] },
      { key: "contractor", label: "General Contractor", fieldIds: ["construction_contractor_sig", "construction_contractor_name", "construction_contractor_date"] },
    ],
  },
  equipmentLease: {
    roles: [
      { key: "lessor", label: "Lessor", fieldIds: ["equip_lessor_sig", "equip_lessor_name", "equip_lessor_date"] },
      { key: "lessee", label: "Lessee", fieldIds: ["equip_lessee_sig", "equip_lessee_name", "equip_lessee_date"] },
    ],
  },
  patientConsent: {
    roles: [
      { key: "provider", label: "Healthcare Provider", fieldIds: ["patientConsent_provider_sig", "patientConsent_provider_name", "patientConsent_provider_date"] },
      { key: "patient", label: "Patient", fieldIds: ["patientConsent_patient_sig", "patientConsent_patient_name", "patientConsent_patient_date"] },
    ],
  },
  hipaaBAA: {
    roles: [
      { key: "coveredEntity", label: "Covered Entity", fieldIds: ["hipaaBAA_coveredEntity_sig", "hipaaBAA_coveredEntity_name", "hipaaBAA_coveredEntity_date"] },
      { key: "businessAssociate", label: "Business Associate", fieldIds: ["hipaaBAA_businessAssociate_sig", "hipaaBAA_businessAssociate_name", "hipaaBAA_businessAssociate_date"] },
    ],
  },
  medicalRelease: {
    roles: [
      { key: "patient", label: "Patient", fieldIds: ["medicalRelease_patient_sig", "medicalRelease_patient_name", "medicalRelease_patient_date"] },
    ],
  },
  saasAgreement: {
    roles: [
      { key: "provider", label: "Provider", fieldIds: ["saasAgreement_provider_sig", "saasAgreement_provider_name", "saasAgreement_provider_date"] },
      { key: "customer", label: "Customer", fieldIds: ["saasAgreement_customer_sig", "saasAgreement_customer_name", "saasAgreement_customer_date"] },
    ],
  },
  softwareLicense: {
    roles: [
      { key: "licensor", label: "Licensor", fieldIds: ["softwareLicense_licensor_sig", "softwareLicense_licensor_name", "softwareLicense_licensor_date"] },
      { key: "licensee", label: "Licensee", fieldIds: ["softwareLicense_licensee_sig", "softwareLicense_licensee_name", "softwareLicense_licensee_date"] },
    ],
  },
  dataProcessing: {
    roles: [
      { key: "controller", label: "Data Controller", fieldIds: ["dataProcessing_controller_sig", "dataProcessing_controller_name", "dataProcessing_controller_date"] },
      { key: "processor", label: "Data Processor", fieldIds: ["dataProcessing_processor_sig", "dataProcessing_processor_name", "dataProcessing_processor_date"] },
    ],
  },
  handbookAck: {
    roles: [
      { key: "employee", label: "Employee", fieldIds: ["handbookAck_employee_sig", "handbookAck_employee_name", "handbookAck_employee_date"] },
    ],
  },
  separationAgreement: {
    roles: [
      { key: "employer", label: "Employer Representative", fieldIds: ["separationAgreement_employer_sig", "separationAgreement_employer_name", "separationAgreement_employer_date"] },
      { key: "employee", label: "Employee", fieldIds: ["separationAgreement_employee_sig", "separationAgreement_employee_name", "separationAgreement_employee_date"] },
    ],
  },
  nonCompete: {
    roles: [
      { key: "employer", label: "Employer Representative", fieldIds: ["nonCompete_employer_sig", "nonCompete_employer_name", "nonCompete_employer_date"] },
      { key: "employee", label: "Employee", fieldIds: ["nonCompete_employee_sig", "nonCompete_employee_name", "nonCompete_employee_date"] },
    ],
  },
  shareholderAgreement: {
    roles: [
      { key: "company", label: "Company Representative", fieldIds: ["shareholder_company_sig", "shareholder_company_name", "shareholder_company_date"] },
      { key: "lead", label: "Lead Shareholder", fieldIds: ["shareholder_lead_sig", "shareholder_lead_name", "shareholder_lead_date"] },
    ],
  },
  llcOperating: {
    roles: [
      { key: "managing", label: "Managing Member", fieldIds: ["llcOperating_managing_sig", "llcOperating_managing_name", "llcOperating_managing_date"] },
      { key: "member", label: "Member", fieldIds: ["llcOperating_member_sig", "llcOperating_member_name", "llcOperating_member_date"] },
    ],
  },
  powerOfAttorney: {
    roles: [
      { key: "principal", label: "Principal", fieldIds: ["poa_principal_sig", "poa_principal_name", "poa_principal_date"] },
      { key: "attorney", label: "Attorney-in-Fact", fieldIds: ["poa_attorney_sig", "poa_attorney_name", "poa_attorney_date"] },
    ],
  },
  promissoryNote: {
    roles: [
      { key: "lender", label: "Lender", fieldIds: ["promissory_lender_sig", "promissory_lender_name", "promissory_lender_date"] },
      { key: "borrower", label: "Borrower", fieldIds: ["promissory_borrower_sig", "promissory_borrower_name", "promissory_borrower_date"] },
    ],
  },
  tutorAgreement: {
    roles: [
      { key: "provider", label: "Tutor / Service Provider", fieldIds: ["tutor_provider_sig", "tutor_provider_name", "tutor_provider_date"] },
      { key: "client", label: "Client / Parent", fieldIds: ["tutor_client_sig", "tutor_client_name", "tutor_client_date"] },
    ],
  },
  securityServices: {
    roles: [
      { key: "provider", label: "Security Provider", fieldIds: ["security_provider_sig", "security_provider_name", "security_provider_date"] },
      { key: "client", label: "Client", fieldIds: ["security_client_sig", "security_client_name", "security_client_date"] },
    ],
  },
  consultingAgreement: {
    roles: [
      { key: "consultant", label: "Consultant", fieldIds: ["consulting_consultant_sig", "consulting_consultant_name", "consulting_consultant_date"] },
      { key: "client", label: "Client", fieldIds: ["consulting_client_sig", "consulting_client_name", "consulting_client_date"] },
    ],
  },
  freelancerAgreement: {
    roles: [
      { key: "freelancer", label: "Freelancer", fieldIds: ["freelancer_freelancer_sig", "freelancer_freelancer_name", "freelancer_freelancer_date"] },
      { key: "client", label: "Client", fieldIds: ["freelancer_client_sig", "freelancer_client_name", "freelancer_client_date"] },
    ],
  },
  shiftWorker: {
    roles: [
      { key: "employer", label: "Employer Representative", fieldIds: ["shiftWorker_employer_sig", "shiftWorker_employer_name", "shiftWorker_employer_date"] },
      { key: "employee", label: "Employee", fieldIds: ["shiftWorker_employee_sig", "shiftWorker_employee_name", "shiftWorker_employee_date"] },
    ],
  },
  internshipAgreement: {
    roles: [
      { key: "company", label: "Company Representative", fieldIds: ["internship_company_sig", "internship_company_name", "internship_company_date"] },
      { key: "intern", label: "Intern", fieldIds: ["internship_intern_sig", "internship_intern_name", "internship_intern_date"] },
    ],
  },
  commissionAgreement: {
    roles: [
      { key: "company", label: "Company", fieldIds: ["commission_company_sig", "commission_company_name", "commission_company_date"] },
      { key: "rep", label: "Sales Representative", fieldIds: ["commission_rep_sig", "commission_rep_name", "commission_rep_date"] },
    ],
  },
  tempStaffing: {
    roles: [
      { key: "agency", label: "Staffing Agency", fieldIds: ["tempStaffing_agency_sig", "tempStaffing_agency_name", "tempStaffing_agency_date"] },
      { key: "client", label: "Client Company", fieldIds: ["tempStaffing_client_sig", "tempStaffing_client_name", "tempStaffing_client_date"] },
    ],
  },
  liabilityWaiver: {
    roles: [
      { key: "participant", label: "Participant", fieldIds: ["liabilityWaiver_participant_sig", "liabilityWaiver_participant_name", "liabilityWaiver_participant_date"] },
    ],
  },
  ceaseAndDesist: {
    roles: [
      { key: "sender", label: "Authorized Representative", fieldIds: ["ceaseDesist_sender_sig", "ceaseDesist_sender_name", "ceaseDesist_sender_date"] },
    ],
  },
  settlementAgreement: {
    roles: [
      { key: "partyA", label: "First Party", fieldIds: ["settlement_partyA_sig", "settlement_partyA_name", "settlement_partyA_date"] },
      { key: "partyB", label: "Second Party", fieldIds: ["settlement_partyB_sig", "settlement_partyB_name", "settlement_partyB_date"] },
    ],
  },
  indemnificationAgreement: {
    roles: [
      { key: "indemnitor", label: "Indemnitor", fieldIds: ["indemnification_indemnitor_sig", "indemnification_indemnitor_name", "indemnification_indemnitor_date"] },
      { key: "indemnitee", label: "Indemnitee", fieldIds: ["indemnification_indemnitee_sig", "indemnification_indemnitee_name", "indemnification_indemnitee_date"] },
    ],
  },
  partnershipAgreement: {
    roles: [
      { key: "partnerA", label: "Partner A", fieldIds: ["partnership_partnerA_sig", "partnership_partnerA_name", "partnership_partnerA_date"] },
      { key: "partnerB", label: "Partner B", fieldIds: ["partnership_partnerB_sig", "partnership_partnerB_name", "partnership_partnerB_date"] },
    ],
  },
  loanAgreement: {
    roles: [
      { key: "lender", label: "Lender", fieldIds: ["loan_lender_sig", "loan_lender_name", "loan_lender_date"] },
      { key: "borrower", label: "Borrower", fieldIds: ["loan_borrower_sig", "loan_borrower_name", "loan_borrower_date"] },
    ],
  },
  franchiseAgreement: {
    roles: [
      { key: "franchisor", label: "Franchisor", fieldIds: ["franchise_franchisor_sig", "franchise_franchisor_name", "franchise_franchisor_date"] },
      { key: "franchisee", label: "Franchisee", fieldIds: ["franchise_franchisee_sig", "franchise_franchisee_name", "franchise_franchisee_date"] },
    ],
  },
  jointVenture: {
    roles: [
      { key: "venturerA", label: "Venturer A", fieldIds: ["jv_venturerA_sig", "jv_venturerA_name", "jv_venturerA_date"] },
      { key: "venturerB", label: "Venturer B", fieldIds: ["jv_venturerB_sig", "jv_venturerB_name", "jv_venturerB_date"] },
    ],
  },
  buySellAgreement: {
    roles: [
      { key: "seller", label: "Selling Shareholder", fieldIds: ["buySell_seller_sig", "buySell_seller_name", "buySell_seller_date"] },
      { key: "company", label: "Company Representative", fieldIds: ["buySell_company_sig", "buySell_company_name", "buySell_company_date"] },
    ],
  },
  stockOptionGrant: {
    roles: [
      { key: "company", label: "Company", fieldIds: ["stockOption_company_sig", "stockOption_company_name", "stockOption_company_date"] },
      { key: "optionee", label: "Optionee", fieldIds: ["stockOption_optionee_sig", "stockOption_optionee_name", "stockOption_optionee_date"] },
    ],
  },
  votingAgreement: {
    roles: [
      { key: "investor", label: "Lead Investor", fieldIds: ["voting_investor_sig", "voting_investor_name", "voting_investor_date"] },
      { key: "founder", label: "Founder", fieldIds: ["voting_founder_sig", "voting_founder_name", "voting_founder_date"] },
    ],
  },
  investorRights: {
    roles: [
      { key: "company", label: "Company", fieldIds: ["investorRights_company_sig", "investorRights_company_name", "investorRights_company_date"] },
      { key: "investor", label: "Lead Investor", fieldIds: ["investorRights_investor_sig", "investorRights_investor_name", "investorRights_investor_date"] },
    ],
  },
};

/* ====================================================================
 *  ALL_TEMPLATES export
 * ==================================================================== */

export const ALL_TEMPLATES: TemplateDescriptor[] = [
  {
    key: "nda",
    name: "Non-Disclosure Agreement",
    description: "Standard mutual NDA with confidentiality clauses, exclusions, and dual signature blocks.",
    category: "Legal",
    tags: ["nda", "confidentiality", "legal"],
    pageCount: 2,
    get doc() { return buildNda(); },
  },
  {
    key: "contractor",
    name: "Independent Contractor Agreement",
    description: "Comprehensive contractor engagement with IP assignment, non-solicitation, and payment terms.",
    category: "Legal",
    tags: ["contractor", "freelance", "agreement"],
    pageCount: 3,
    get doc() { return buildContractor(); },
  },
  {
    key: "offerLetter",
    name: "Offer of Employment",
    description: "Professional offer letter with compensation, benefits, at-will terms, and acceptance signature.",
    category: "HR",
    tags: ["offer", "employment", "hr"],
    pageCount: 2,
    get doc() { return buildOfferLetter(); },
  },
  {
    key: "invoice",
    name: "Invoice",
    description: "Clean invoice layout with bill from/to, line items, totals, and payment terms.",
    category: "Finance",
    tags: ["invoice", "billing", "payment"],
    pageCount: 1,
    get doc() { return buildInvoice(); },
  },
  {
    key: "serviceAgreement",
    name: "Service Agreement",
    description: "Full service agreement covering scope, compensation, IP, liability, and dispute resolution.",
    category: "Legal",
    tags: ["service", "agreement", "legal"],
    pageCount: 3,
    get doc() { return buildServiceAgreement(); },
  },
  {
    key: "residentialLease",
    name: "Residential Lease Agreement",
    description: "Standard residential lease with rent, security deposit, maintenance, utilities, and termination provisions.",
    category: "Real Estate",
    tags: ["lease", "rental", "residential"],
    pageCount: 3,
    get doc() { return buildResidentialLease(); },
  },
  {
    key: "propertyPurchase",
    name: "Commercial Property Purchase Agreement",
    description: "Commercial real estate purchase agreement with earnest money escrow, due diligence, title, and closing terms.",
    category: "Real Estate",
    tags: ["purchase", "commercial", "real estate"],
    pageCount: 3,
    get doc() { return buildPropertyPurchase(); },
  },
  {
    key: "propertyManagement",
    name: "Property Management Agreement",
    description: "Professional property management agreement covering services, compensation, reporting, and liability.",
    category: "Real Estate",
    tags: ["management", "property", "real estate"],
    pageCount: 2,
    get doc() { return buildPropertyManagement(); },
  },
  {
    key: "materialSupply",
    name: "Materials Supply Contract",
    description: "Industrial materials procurement contract with ASTM specifications, delivery scheduling, quality standards, and price escalation clauses.",
    category: "Construction",
    tags: ["materials", "supply", "construction", "steel"],
    pageCount: 3,
    get doc() { return buildMaterialSupply(); },
  },
  {
    key: "constructionServices",
    name: "Construction Services Agreement",
    description: "General contractor engagement with scope, retainage, change orders, insurance, safety compliance, and lien waivers.",
    category: "Construction",
    tags: ["construction", "contractor", "building", "services"],
    pageCount: 3,
    get doc() { return buildConstructionServices(); },
  },
  {
    key: "equipmentLease",
    name: "Equipment Lease Agreement",
    description: "Heavy construction equipment lease with maintenance obligations, operator requirements, and return condition terms.",
    category: "Construction",
    tags: ["equipment", "lease", "construction", "machinery"],
    pageCount: 2,
    get doc() { return buildEquipmentLease(); },
  },
  {
    key: "patientConsent",
    name: "Patient Consent Form",
    description: "Professional informed consent for medical procedures with treatment details, risks, alternatives, and dual signature blocks.",
    category: "Healthcare",
    tags: ["healthcare", "consent", "patient", "medical"],
    pageCount: 2,
    get doc() { return buildPatientConsent(); },
  },
  {
    key: "hipaaBAA",
    name: "HIPAA Business Associate Agreement",
    description: "HIPAA-compliant BAA with 45 CFR references covering PHI safeguards, breach notification, and termination provisions.",
    category: "Healthcare",
    tags: ["healthcare", "hipaa", "baa", "compliance", "phi"],
    pageCount: 3,
    get doc() { return buildHipaaBAA(); },
  },
  {
    key: "medicalRelease",
    name: "Medical Records Release Authorization",
    description: "Single-page authorization for disclosure of protected health information with expiration and patient rights.",
    category: "Healthcare",
    tags: ["healthcare", "medical", "records", "release", "authorization"],
    pageCount: 1,
    get doc() { return buildMedicalRelease(); },
  },
  {
    key: "saasAgreement",
    name: "SaaS Subscription Agreement",
    description: "Comprehensive SaaS subscription agreement with uptime SLA, data ownership, auto-renewal, and security provisions.",
    category: "Technology",
    tags: ["saas", "subscription", "cloud", "technology"],
    pageCount: 3,
    get doc() { return buildSaasAgreement(); },
  },
  {
    key: "softwareLicense",
    name: "Software License Agreement",
    description: "Enterprise software license with seat-based licensing, update provisions, support terms, and IP protections.",
    category: "Technology",
    tags: ["software", "license", "enterprise", "technology"],
    pageCount: 2,
    get doc() { return buildSoftwareLicense(); },
  },
  {
    key: "dataProcessing",
    name: "Data Processing Agreement",
    description: "GDPR-aligned DPA with specific Article references, sub-processor controls, breach notification, and audit rights.",
    category: "Technology",
    tags: ["gdpr", "dpa", "data", "privacy", "technology"],
    pageCount: 3,
    get doc() { return buildDataProcessing(); },
  },
  {
    key: "handbookAck",
    name: "Employee Handbook Acknowledgment",
    description: "Employee acknowledgment of receipt and understanding of the company handbook, covering at-will employment, code of conduct, anti-harassment, and confidentiality.",
    category: "HR",
    tags: ["handbook", "acknowledgment", "hr", "onboarding"],
    pageCount: 1,
    get doc() { return buildHandbookAck(); },
  },
  {
    key: "separationAgreement",
    name: "Employee Separation Agreement",
    description: "Comprehensive separation agreement with severance terms, COBRA continuation, release of claims, ADEA consideration and revocation periods, and mutual non-disparagement.",
    category: "HR",
    tags: ["separation", "termination", "severance", "hr", "release"],
    pageCount: 3,
    get doc() { return buildSeparationAgreement(); },
  },
  {
    key: "nonCompete",
    name: "Non-Compete Agreement",
    description: "Non-compete and non-solicitation agreement with defined geographic scope, duration restrictions, confidentiality obligations, and equitable remedies.",
    category: "HR",
    tags: ["non-compete", "non-solicitation", "restrictive covenant", "hr"],
    pageCount: 2,
    get doc() { return buildNonCompete(); },
  },
  {
    key: "shareholderAgreement",
    name: "Shareholder Agreement",
    description: "Corporate shareholder agreement with transfer restrictions, tag-along/drag-along rights, anti-dilution protections, and deadlock resolution.",
    category: "Legal",
    tags: ["shareholder", "corporate", "equity", "governance"],
    pageCount: 3,
    get doc() { return buildShareholderAgreement(); },
  },
  {
    key: "llcOperating",
    name: "LLC Operating Agreement",
    description: "Comprehensive LLC operating agreement with capital contributions, profit allocation, management structure, and dissolution provisions.",
    category: "Legal",
    tags: ["llc", "operating", "partnership", "corporate"],
    pageCount: 3,
    get doc() { return buildLLCOperatingAgreement(); },
  },
  {
    key: "powerOfAttorney",
    name: "Power of Attorney",
    description: "Durable general power of attorney covering financial, real estate, legal, and healthcare decisions with third-party reliance provisions.",
    category: "Legal",
    tags: ["power of attorney", "poa", "durable", "legal"],
    pageCount: 2,
    get doc() { return buildPowerOfAttorney(); },
  },
  {
    key: "promissoryNote",
    name: "Promissory Note",
    description: "Secured promissory note with fixed interest, monthly installments, collateral, acceleration clause, and default provisions.",
    category: "Finance",
    tags: ["promissory", "note", "loan", "finance", "debt"],
    pageCount: 2,
    get doc() { return buildPromissoryNote(); },
  },
  {
    key: "tutorAgreement",
    name: "Tutor Services Agreement",
    description: "Private tutoring engagement with session scheduling, FERPA-compliant confidentiality, cancellation policy, and payment terms.",
    category: "Legal",
    tags: ["tutor", "education", "services", "legal"],
    pageCount: 2,
    get doc() { return buildTutorAgreement(); },
  },
  {
    key: "securityServices",
    name: "Security Guard Services Agreement",
    description: "Commercial security engagement with armed/unarmed personnel, 24/7 scheduling, insurance requirements, use of force policy, and incident reporting.",
    category: "Legal",
    tags: ["security", "guard", "services", "commercial"],
    pageCount: 3,
    get doc() { return buildSecurityServicesAgreement(); },
  },
  {
    key: "consultingAgreement",
    name: "Consulting Services Agreement",
    description: "Strategic advisory engagement with monthly retainer, deliverable milestones, IP ownership, non-solicitation, and limitation of liability.",
    category: "Legal",
    tags: ["consulting", "advisory", "strategy", "legal"],
    pageCount: 2,
    get doc() { return buildConsultingAgreement(); },
  },
  {
    key: "freelancerAgreement",
    name: "Freelancer Agreement",
    description: "Independent creative services contract with fixed fee, revision policy, IP transfer, kill fee, and independent contractor status.",
    category: "Legal",
    tags: ["freelancer", "gig", "creative", "independent contractor"],
    pageCount: 2,
    get doc() { return buildFreelancerAgreement(); },
  },
  {
    key: "shiftWorker",
    name: "Shift Worker Employment Agreement",
    description: "Shift-based employment agreement with rotating schedules, hourly compensation, overtime policy, attendance rules, and workplace safety requirements.",
    category: "HR",
    tags: ["shift", "hourly", "warehouse", "employment", "hr"],
    pageCount: 2,
    get doc() { return buildShiftWorkerAgreement(); },
  },
  {
    key: "internshipAgreement",
    name: "Internship Agreement",
    description: "Paid internship agreement with stipend, mentorship, learning objectives, IP assignment, and academic credit provisions.",
    category: "HR",
    tags: ["internship", "intern", "student", "hr"],
    pageCount: 2,
    get doc() { return buildInternshipAgreement(); },
  },
  {
    key: "commissionAgreement",
    name: "Commission-Based Sales Agreement",
    description: "Commission sales agreement with territory assignment, base draw, performance quotas, clawback policy, and tail period provisions.",
    category: "HR",
    tags: ["commission", "sales", "compensation", "hr"],
    pageCount: 2,
    get doc() { return buildCommissionAgreement(); },
  },
  {
    key: "tempStaffing",
    name: "Temporary Staffing Agreement",
    description: "Staffing agency agreement with bill rates, temp-to-hire conversion fees, insurance requirements, and non-solicitation provisions.",
    category: "HR",
    tags: ["staffing", "temporary", "agency", "hr"],
    pageCount: 2,
    get doc() { return buildTempStaffingAgreement(); },
  },
  {
    key: "liabilityWaiver",
    name: "Liability Waiver & Release",
    description: "Single-page liability waiver with assumption of risk, release of claims, indemnification, and medical authorization for outdoor adventure activities.",
    category: "Legal",
    tags: ["waiver", "liability", "release", "legal"],
    pageCount: 1,
    get doc() { return buildLiabilityWaiver(); },
  },
  {
    key: "ceaseAndDesist",
    name: "Cease and Desist Letter",
    description: "Formal cease and desist demand for trademark and IP infringement with required actions, deadline, and legal consequences.",
    category: "Legal",
    tags: ["cease", "desist", "trademark", "infringement", "legal"],
    pageCount: 1,
    get doc() { return buildCeaseAndDesist(); },
  },
  {
    key: "settlementAgreement",
    name: "Settlement Agreement & Mutual Release",
    description: "Comprehensive settlement agreement with payment schedule, mutual release of claims, confidentiality, non-disparagement, and governing law provisions.",
    category: "Legal",
    tags: ["settlement", "release", "dispute", "legal"],
    pageCount: 2,
    get doc() { return buildSettlementAgreement(); },
  },
  {
    key: "indemnificationAgreement",
    name: "Indemnification Agreement",
    description: "Detailed indemnification agreement with defense obligations, notice requirements, insurance provisions, subrogation rights, and three-year survival period.",
    category: "Legal",
    tags: ["indemnification", "defense", "hold harmless", "legal"],
    pageCount: 2,
    get doc() { return buildIndemnificationAgreement(); },
  },
  {
    key: "partnershipAgreement",
    name: "General Partnership Agreement",
    description: "Comprehensive partnership formation agreement covering capital contributions, profit sharing, management authority, non-compete, and buyout provisions.",
    category: "Legal",
    tags: ["partnership", "formation", "legal", "business"],
    pageCount: 3,
    get doc() { return buildPartnershipAgreement(); },
  },
  {
    key: "loanAgreement",
    name: "Business Loan Agreement",
    description: "Commercial loan agreement with fixed interest rate, amortization schedule, collateral security interest, covenants, and default remedies.",
    category: "Finance",
    tags: ["loan", "lending", "finance", "credit"],
    pageCount: 2,
    get doc() { return buildLoanAgreement(); },
  },
  {
    key: "franchiseAgreement",
    name: "Franchise Agreement",
    description: "Franchise grant agreement with territory exclusivity, royalty structure, training obligations, quality standards, and transfer restrictions.",
    category: "Legal",
    tags: ["franchise", "licensing", "legal", "restaurant"],
    pageCount: 3,
    get doc() { return buildFranchiseAgreement(); },
  },
  {
    key: "jointVenture",
    name: "Joint Venture Agreement",
    description: "Joint venture agreement for infrastructure projects with governance committee, profit allocation, IP ownership, and ICC arbitration.",
    category: "Legal",
    tags: ["joint venture", "infrastructure", "legal", "partnership"],
    pageCount: 3,
    get doc() { return buildJointVenture(); },
  },
  {
    key: "buySellAgreement",
    name: "Buy-Sell Agreement",
    description: "Shareholder buyout agreement with triggering events, valuation methods, put/call options, life insurance funding, and restrictive covenants.",
    category: "Legal",
    tags: ["buy-sell", "shareholder", "buyout", "succession"],
    pageCount: 3,
    get doc() { return buildBuySellAgreement(); },
  },
  {
    key: "stockOptionGrant",
    name: "Stock Option Grant Agreement",
    description: "Equity incentive stock option grant with vesting schedule, exercise terms, ISO/NSO tax treatment, and change of control provisions.",
    category: "Legal",
    tags: ["stock option", "equity", "incentive", "grant"],
    pageCount: 2,
    get doc() { return buildStockOptionGrant(); },
  },
  {
    key: "votingAgreement",
    name: "Shareholder Voting Agreement",
    description: "Board composition and governance agreement with voting commitments, protective provisions, drag-along rights, and information rights.",
    category: "Legal",
    tags: ["voting", "governance", "board", "shareholder"],
    pageCount: 2,
    get doc() { return buildVotingAgreement(); },
  },
  {
    key: "investorRights",
    name: "Investor Rights Agreement",
    description: "Comprehensive investor protections including registration rights, pro-rata participation, anti-dilution, board representation, and lock-up provisions.",
    category: "Legal",
    tags: ["investor rights", "registration", "anti-dilution", "preferred stock"],
    pageCount: 3,
    get doc() { return buildInvestorRightsAgreement(); },
  },
];
